import { VitrineBadge } from '../components/simulation/VitrineBadge';
import { FormatPill } from '../components/simulation/FormatPill';
import { RenewalCornerBox } from '../components/simulation/RenewalCornerBox';
import { LinkedComparison } from '../components/simulation/LinkedComparison';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency, formatPercent } from '../components/simulation/formatters';
import { CommissionRecommendationCard, CapexSplitCard, HypothesisCard } from '../components/simulation/result';
import { ValidationWorkflow } from '../components/simulation/result/ValidationWorkflow';
import { DecisionHistory } from '../components/simulation/result/DecisionHistory';
import { CornerCategoryBadge, simulationRetainedCa } from '../utils/cornerCategory';
import { getRecommendedCommissionRate, isClientCapexCarrier, getCommissionBrackets, getCaBracket, isSystemeU, getCapexCarrierForEnseigne } from '../components/simulation/commissionCalculator';
import { 
  ArrowLeft,
  Search as SearchIcon,
  Download, 
  Trash2,
  TrendingUp,
  TrendingDown,
  Minus,
  Star,
  Clock,
  Wallet,
  BarChart3,
  Loader2,
  Check,
  AlertTriangle,
  X,
  FileSpreadsheet,
  FileText,
  CheckCircle2,
  XCircle,
  Calendar,
  Send,
  Mail,
  Pencil,
  History,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Shield,
  Trophy,
  Save,
  Edit2,
  Calculator,
  Lock,
  Camera,
  Upload,
  Plus,
  FileSignature,
  Building2,
  MapPin,
  ExternalLink,
  ShieldCheck
} from 'lucide-react';
import { RefreshCw, Copy, Megaphone, Presentation } from 'lucide-react';
import { SimulationBridge } from '../components/bb/SimulationBridge';
import { AnnouncementEditorModal } from '../components/announcements/AnnouncementEditorModal';
import { SimulationChat } from '../components/SimulationChat';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
} from 'recharts';

// Extracted components for optimization
import TestSimulationPanel from '../components/simulation/TestSimulationPanel';
import DecisionModificationRequest from '../components/DecisionModificationRequest';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SimulationResultPage() {
  const { id } = useParams();
  const { api, token, user, isAdmin } = useAuth();
  const { t, language } = useLanguage();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [simulation, setSimulation] = useState(null);
  const [prereqStatus, setPrereqStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementStatus, setAnnouncementStatus] = useState(null);
  const [sendEmailData, setSendEmailData] = useState({
    recipients: '',
    includePdf: true,
    includeExcel: true,
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [includedHypotheses, setIncludedHypotheses] = useState({
    h1: true,
    h2: true,
    h3: true
  });
  
  // Validation state
  const [validationData, setValidationData] = useState({
    commission_rate: 0.15,
    ca_previsionnel_ht: '',
    confirmed: false,
    commercial_comment: ''
  });
  const [validating, setValidating] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  
  // Admin decision state
  const [adminDecision, setAdminDecision] = useState({
    status: '',
    comment: '',
    counter_commission: 0.18,
    counter_ca: ''
  });
  const [submittingDecision, setSubmittingDecision] = useState(false);
  const [showCounterProposal, setShowCounterProposal] = useState(false);
  
  // Permission-based access control
  const canMakeDecision = hasPermission('make_decision');
  const canDeleteSimulation = hasPermission('delete_simulation');
  const canExportSimulation = hasPermission('export_simulation');
  const canChangeBusinessStatus = user?.role === 'super_admin';
  const canEditSimulation = hasPermission('edit_simulation');
  
  // Check if the simulation belongs to current user
  const isOwner = simulation?.user_id === user?.id;
  
  // Verdict history state
  const [verdictHistory, setVerdictHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Commission rate adjustment state
  const [customCommissionRate, setCustomCommissionRate] = useState(null);
  const [customCA, setCustomCA] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [sendingNote, setSendingNote] = useState(false);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [showCommissionEdit, setShowCommissionEdit] = useState(false);
  const [savingHypotheses, setSavingHypotheses] = useState(false);
  
  // Analysis source state - allows switching between different CA/Commission sources
  const [analysisSource, setAnalysisSource] = useState('tool'); // 'tool', 'commercial', 'direction'
  const [previewData, setPreviewData] = useState(null); // Holds preview recalculation results
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [isEditingDecision, setIsEditingDecision] = useState(false);
  const [resettingDecision, setResettingDecision] = useState(false);
  const [isEditingFinalStatus, setIsEditingFinalStatus] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(null); // Filename being deleted
  const [uploadingPhotos, setUploadingPhotos] = useState(false); // Photo upload in progress
  
  // Contract Request Modal State
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractData, setContractData] = useState({
    enseigne: '',
    raison_sociale: '',
    capital: '',
    siret: '',
    adresse: '',
    code_postal: '',
    ville: '',
    genre_dirigeant: 'Monsieur',
    prenom_dirigeant: '',
    nom_dirigeant: '',
    date_mise_en_place: '',
    duree_mois: '36',
    ca_minimum_mensuel: '15000',
    ca_sortie_anticipee: '15000',
    date_signature: '',
    surface_m2: '',
    capex_carrier: '',
    capex_carrier_label: '',
    zone_chalandise_voiture: '10',
    zone_chalandise_pied: '30',
    taux_commission: '',
    // Investissement CAPEX fields
    corner_investor: 'CCD',
    vitrine_investor: 'CCD',
    inox_investor: 'CCD',
    concept_wasabi: true,
    concept_mme_wang: false,
    concept_sorae: false,
    concept_pokawa: false
  });
  const [exportingContract, setExportingContract] = useState(false);
  const [savingContractDraft, setSavingContractDraft] = useState(false);
  const [sendingContract, setSendingContract] = useState(false);
  const [contractDraftLoaded, setContractDraftLoaded] = useState(false);
  const [contractSendModal, setContractSendModal] = useState(false);
  const [contractSendData, setContractSendData] = useState({ recipient_emails: [], recipient_names: [], message: '' });
  const [contractRecipients, setContractRecipients] = useState([]);
  
  // Contract Request History State
  const [contractHistory, setContractHistory] = useState([]);
  const [loadingContractHistory, setLoadingContractHistory] = useState(false);
  const [showContractHistory, setShowContractHistory] = useState(false);
  const [sendingContractEmail, setSendingContractEmail] = useState(null);
  const [contractEmailModal, setContractEmailModal] = useState({ open: false, requestId: null });
  const [contractEmailData, setContractEmailData] = useState({ recipient_emails: [], recipient_names: [], message: '' });

  // Helper function to get CAPEX carrier label based on enseigne
  const getCapexCarrierLabel = (enseigne, carrier) => {
    if (!enseigne || !carrier) return carrier;
    
    const isLeclerc = enseigne.toLowerCase().includes('leclerc');
    
    if (isLeclerc) {
      return carrier === 'CCD' ? 'Commettant' : 'Commissionnaire';
    } else {
      // Intermarché, Système U, Carrefour, etc.
      return carrier === 'CCD' ? 'Concessionnaire' : 'Concédant';
    }
  };
  
  // Note: Test Simulation state and functions have been moved to TestSimulationPanel component

  // Function to upload photos
  const handleUploadPhotos = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingPhotos(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('photos', file);
      });
      
      const response = await api().post(`/simulations/${id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update local state with new photos
      if (response.data.photos) {
        setSimulation(prev => ({
          ...prev,
          photos: response.data.photos
        }));
      }
      
      toast.success(language === 'fr' 
        ? `${files.length} photo(s) ajoutée(s)` 
        : `${files.length} photo(s) uploaded`);
    } catch (error) {
      console.error('Upload photos failed:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'upload' : 'Upload failed');
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Function to delete a photo
  const handleDeletePhoto = async (filename) => {
    if (!window.confirm(language === 'fr' 
      ? 'Êtes-vous sûr de vouloir supprimer cette photo ?' 
      : 'Are you sure you want to delete this photo?')) {
      return;
    }
    
    setDeletingPhoto(filename);
    try {
      await api().delete(`/simulations/${id}/photos/${filename}`);
      // Update local state to remove the photo
      setSimulation(prev => ({
        ...prev,
        photos: prev.photos.filter(p => !p.endsWith(filename))
      }));
      toast.success(language === 'fr' ? 'Photo supprimée' : 'Photo deleted');
    } catch (error) {
      console.error('Delete photo failed:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la suppression' : 'Delete failed');
    } finally {
      setDeletingPhoto(null);
    }
  };

  // Function to recalculate with preview (no save)
  const recalculatePreview = async (caHtAnnual, commissionRate, showToast = true) => {
    if (!caHtAnnual || caHtAnnual <= 0 || !commissionRate || commissionRate <= 0) {
      if (showToast) {
        toast.error(language === 'fr' ? 'Valeurs CA ou Commission invalides' : 'Invalid CA or Commission values');
      }
      return;
    }
    
    setLoadingPreview(true);
    try {
      const response = await api().post(`/simulations/${id}/preview-recalculate`, {
        ca_ht_annual: caHtAnnual,
        commission_rate: commissionRate
      });
      setPreviewData(response.data);
      if (showToast) {
        toast.success(language === 'fr' 
          ? `Analyse recalculée avec CA ${formatCurrency(caHtAnnual)} et commission ${(commissionRate * 100).toFixed(1)}%` 
          : `Analysis recalculated with CA ${formatCurrency(caHtAnnual)} and commission ${(commissionRate * 100).toFixed(1)}%`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors du recalcul' : 'Recalculation failed');
      toast.error(msg);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Calcule le CA HT et le taux de commission à utiliser selon la source sélectionnée
  const computeSourceValues = (source) => {
    if (!simulation) return { caToUse: null, commissionToUse: null };
    let caToUse, commissionToUse;
    if (source === 'tool') {
      caToUse = simulation.average_hypothesis?.ca_ht_annual;
      const enseigne = simulation.input_data?.general_info?.retailer_name || '';
      const clientCarriesCapex = isSystemeU(enseigne) ? false :
        (simulation.input_data?.capex_investment ? isClientCapexCarrier(simulation.input_data.capex_investment) : false);
      commissionToUse = getRecommendedCommissionRate(caToUse || 0, clientCarriesCapex, enseigne);
    } else if (source === 'commercial') {
      caToUse = simulation.ca_previsionnel_ht_proposed || simulation.average_hypothesis?.ca_ht_annual;
      commissionToUse = simulation.commission_rate_proposed || simulation.commission_rate_used || 0.18;
    } else if (source === 'direction') {
      if (simulation.admin_counter_proposal?.ca_previsionnel_ht) {
        caToUse = simulation.admin_counter_proposal.ca_previsionnel_ht;
        commissionToUse = simulation.admin_counter_proposal.commission_rate || simulation.commission_rate_proposed || 0.18;
      } else {
        caToUse = simulation.ca_previsionnel_ht_proposed || simulation.average_hypothesis?.ca_ht_annual;
        commissionToUse = simulation.commission_rate_proposed || simulation.commission_rate_used || 0.18;
      }
    }
    return { caToUse, commissionToUse };
  };

  // Handle source change — le recalcul est géré par l'effet réactif ci-dessous
  const handleSourceChange = (newSource) => {
    setAnalysisSource(newSource);
  };

  // Function to reset admin decision
  const resetAdminDecision = async () => {
    if (!window.confirm(language === 'fr' 
      ? 'Êtes-vous sûr de vouloir annuler cette décision ? Vous pourrez ensuite prendre une nouvelle décision.'
      : 'Are you sure you want to reset this decision? You can then make a new decision.')) {
      return;
    }
    
    setResettingDecision(true);
    try {
      await api().post(`/simulations/${id}/reset-admin-decision`);
      
      // Update local state
      setSimulation(prev => ({
        ...prev,
        admin_status: null,
        admin_status_updated_by_name: null,
        admin_status_updated_at: null,
        admin_comment: null,
        admin_counter_proposal: null
      }));
      
      // Reset admin decision form
      setAdminDecision({ status: '', comment: '' });
      setShowCounterProposal(false);
      
      toast.success(language === 'fr' 
        ? 'Décision réinitialisée. Vous pouvez maintenant prendre une nouvelle décision.' 
        : 'Decision reset. You can now make a new decision.');
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la réinitialisation' : 'Reset failed');
      toast.error(msg);
    } finally {
      setResettingDecision(false);
    }
  };

  // Calculate dynamic average based on selected hypotheses
  const calculateAverage = () => {
    if (!simulation) return { ca_ht_annual: 0, ca_ttc_annual: 0 };
    
    const hypotheses = [];
    if (includedHypotheses.h1) hypotheses.push(simulation.hypothesis_1);
    if (includedHypotheses.h2) hypotheses.push(simulation.hypothesis_2);
    if (includedHypotheses.h3) hypotheses.push(simulation.hypothesis_3);
    
    if (hypotheses.length === 0) return { ca_ht_annual: 0, ca_ttc_annual: 0 };
    
    const avg_ca_ht = hypotheses.reduce((sum, h) => sum + h.ca_ht_annual, 0) / hypotheses.length;
    const avg_ca_ttc = hypotheses.reduce((sum, h) => sum + h.ca_ttc_annual, 0) / hypotheses.length;
    
    return { ca_ht_annual: avg_ca_ht, ca_ttc_annual: avg_ca_ttc };
  };

  // Calculate dynamic recommended commission rate based on current average CA
  const getDynamicCommissionRate = () => {
    if (!simulation?.input_data?.capex_investment) return simulation?.recommended_commission_rate || 0.18;
    
    // Use simulation.average_hypothesis which is recalculated by the API after each toggle
    const avgCaHt = simulation.average_hypothesis?.ca_ht_annual || 0;
    const enseigne = simulation?.input_data?.general_info?.retailer_name || '';
    
    // Special case: Système U has its own commission grid and CCD always carries CAPEX
    const clientCarriesCapex = isSystemeU(enseigne) ? false : isClientCapexCarrier(simulation.input_data?.capex_investment);
    
    return getRecommendedCommissionRate(avgCaHt, clientCarriesCapex, enseigne);
  };

  const toggleHypothesis = async (key) => {
    // Calculate new state
    const newIncluded = {
      ...includedHypotheses,
      [key]: !includedHypotheses[key]
    };
    
    // Check if at least one hypothesis is selected
    if (!newIncluded.h1 && !newIncluded.h2 && !newIncluded.h3) {
      toast.error(language === 'fr' ? 'Au moins une hypothèse doit être sélectionnée' : 'At least one hypothesis must be selected');
      return;
    }
    
    // Update local state immediately for responsiveness
    setIncludedHypotheses(newIncluded);
    setSavingHypotheses(true);
    
    try {
      // Call API to save and recalculate
      const response = await api().post(`/simulations/${id}/update-included-hypotheses`, newIncluded);
      const data = response.data;
      
      // Update simulation with recalculated values
      setSimulation(prev => ({
        ...prev,
        included_hypotheses: data.included_hypotheses,
        average_hypothesis: data.average_hypothesis,
        cluster_used: data.cluster_used,
        food_cost_rate_used: data.food_cost_rate_used,
        other_cost_rate_used: data.other_cost_rate_used,
        commission_rate_used: data.commission_rate_used,
        monthly_breakdown_y1: data.monthly_breakdown_y1,
        red_months_count: data.red_months_count,
        verdict: data.verdict,
        verdict_status: data.verdict_status,
        pl_projections: data.pl_projections,
      }));
      
      // Update validationData with new average CA if simulation is not yet validated
      if (!isValidated && data.average_hypothesis?.ca_ht_annual) {
        const newAvgCaHt = data.average_hypothesis.ca_ht_annual;
        const capex = simulation?.input_data?.capex_investment || {};
        const clientCarriesAll = 
          capex.corner_investor === 'CLIENT' && 
          capex.vitrine_investor === 'CLIENT' && 
          capex.inox_investor === 'CLIENT';
        
        // Recalculate recommended commission rate
        const getRecommendedRate = (ca, isClient) => {
          if (ca <= 350000) return isClient ? 0.18 : 0.15;
          if (ca <= 500000) return isClient ? 0.20 : 0.18;
          if (ca <= 600000) return isClient ? 0.21 : 0.19;
          if (ca <= 700000) return isClient ? 0.22 : 0.20;
          if (ca <= 800000) return isClient ? 0.23 : 0.21;
          if (ca <= 1000000) return isClient ? 0.24 : 0.22;
          return isClient ? 0.25 : 0.23;
        };
        
        const newCommissionRate = getRecommendedRate(newAvgCaHt, clientCarriesAll);
        
        setValidationData(prev => ({
          ...prev,
          ca_previsionnel_ht: Math.round(newAvgCaHt),
          commission_rate: newCommissionRate
        }));
      }
      
      toast.success(language === 'fr' 
        ? `Projections recalculées avec ${data.cluster_used}` 
        : `Projections recalculated with ${data.cluster_used}`);
    } catch (error) {
      // Revert local state on error
      setIncludedHypotheses(includedHypotheses);
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la mise à jour' : 'Update failed');
      toast.error(msg);
    } finally {
      setSavingHypotheses(false);
    }
  };

  // Generate formatted project name: "ETUDE CAPEX + ENSEIGNE + VILLE"
  const getFormattedProjectName = () => {
    if (!simulation) return '';
    const retailer = simulation.input_data?.general_info?.retailer_name || '';
    const city = simulation.input_data?.general_info?.city || '';
    return `ETUDE CAPEX ${retailer.toUpperCase()} ${city.toUpperCase()}`.trim();
  };

  useEffect(() => {
    const fetchSimulation = async () => {
      try {
        const response = await api().get(`/simulations/${id}`);
        const sim = response.data;
        setSimulation(sim);

        // Default the monthly-analysis "Source des données" tab to the current
        // workflow stage:
        //  - Direction acting (contre-proposition / validation / Go en cours) -> 'direction'
        //  - Proposition commerciale envoyée à la Direction -> 'commercial'
        //  - Sinon (simulation créée / données outil) -> 'tool'
        const directionEngaged = !!sim.admin_counter_proposal ||
          ['counter_proposal', 'under_review', 'approved', 'rejected', 'pending_review', 'go'].includes(sim.admin_status);
        if (directionEngaged) {
          setAnalysisSource('direction');
        } else if (sim.validated) {
          setAnalysisSource('commercial');
        } else {
          setAnalysisSource('tool');
        }

        // Announcement status indicator (admin/super_admin only)
        if (sim.admin_status === 'approved' && (user?.role === 'admin' || user?.role === 'super_admin')) {
          try {
            const annResp = await api().get(`/announcements/by-simulation/${id}`);
            setAnnouncementStatus(annResp.data?.exists ? annResp.data.status : null);
          } catch (e) {
            setAnnouncementStatus(null);
          }
        }

        // Fetch prerequis validation status
        try {
          const prereqRes = await api().get(`/prerequis-techniques/check-validation/${id}`);
          setPrereqStatus(prereqRes.data);
        } catch { /* Ignore if no prerequis */ }
        
        // Initialize included hypotheses from saved state or default to all true
        if (sim.included_hypotheses) {
          setIncludedHypotheses(sim.included_hypotheses);
        }
        
        // Check if already validated
        if (sim.validated) {
          setIsValidated(true);
          setValidationData({
            commission_rate: sim.commission_rate_proposed || 0.15,
            ca_previsionnel_ht: sim.ca_previsionnel_ht_proposed || '',
            confirmed: true
          });
        } else {
          // Pre-fill with recommended values
          const capex = sim.input_data?.capex_investment;
          const avgCaHt = sim.average_hypothesis?.ca_ht_annual || 0;
          
          // Determine who carries the CAPEX
          const clientCarriesAll = capex &&
            capex.corner_investor === 'CLIENT' && 
            capex.vitrine_investor === 'CLIENT' && 
            capex.inox_investor === 'CLIENT';
          
          // Calculate recommended commission rate
          const getRecommendedRate = (ca, isClient) => {
            if (ca <= 350000) return isClient ? 0.18 : 0.15;
            if (ca <= 500000) return isClient ? 0.20 : 0.18;
            if (ca <= 600000) return isClient ? 0.21 : 0.19;
            if (ca <= 700000) return isClient ? 0.22 : 0.20;
            if (ca <= 800000) return isClient ? 0.23 : 0.21;
            if (ca <= 1000000) return isClient ? 0.24 : 0.22;
            return isClient ? 0.25 : 0.23;
          };
          
          const recommendedRate = getRecommendedRate(avgCaHt, clientCarriesAll);
          
          setValidationData({
            commission_rate: recommendedRate,
            ca_previsionnel_ht: Math.round(avgCaHt),
            confirmed: false,
            commercial_comment: sim.commercial_comment || ''
          });
        }
      } catch (error) {
        console.error('Failed to fetch simulation:', error);
        toast.error(t('error'));
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchSimulation();
  }, [id, api, t, navigate]);

  // Recalcul en DIRECT de l'analyse mensuelle : suit la source active et se relance
  // dès qu'une donnée de cette source (CA HT, commission, contre-proposition Direction) change.
  useEffect(() => {
    if (!simulation || loading) return;
    const { caToUse, commissionToUse } = computeSourceValues(analysisSource);
    if (caToUse && commissionToUse) {
      recalculatePreview(caToUse, commissionToUse, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    analysisSource,
    simulation?.average_hypothesis?.ca_ht_annual,
    simulation?.ca_previsionnel_ht_proposed,
    simulation?.commission_rate_proposed,
    simulation?.commission_rate_used,
    simulation?.admin_counter_proposal?.ca_previsionnel_ht,
    simulation?.admin_counter_proposal?.commission_rate,
    simulation?.admin_status,
    simulation?.input_data?.general_info?.retailer_name,
  ]);


  // Fetch verdict history
  const fetchVerdictHistory = async () => {
    if (loadingHistory || verdictHistory.length > 0) return;
    
    setLoadingHistory(true);
    try {
      const response = await api().get(`/simulations/${id}/verdict-history`);
      setVerdictHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch verdict history:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement de l\'historique' : 'Failed to load history');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Toggle history visibility and load if needed
  const toggleHistory = () => {
    if (!showHistory) {
      fetchVerdictHistory();
    }
    setShowHistory(!showHistory);
  };

  const fetchAuditLogs = async () => {
    setLoadingAudit(true);
    try {
      const response = await api().get(`/simulations/${id}/audit-log`);
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoadingAudit(false);
    }
  };

  // Notes CRUD
  const fetchNotes = async () => {
    setLoadingNotes(true);
    try {
      const response = await api().get(`/simulations/${id}/notes`);
      setNotes(response.data);
    } catch (error) { console.error(error); }
    finally { setLoadingNotes(false); }
  };
  
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSendingNote(true);
    try {
      const response = await api().post(`/simulations/${id}/notes`, { content: newNote });
      setNotes([response.data, ...notes]);
      setNewNote('');
      toast.success('Note ajoutée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSendingNote(false); }
  };
  
  const handleDeleteNote = async (noteId) => {
    try {
      await api().delete(`/simulations/${id}/notes/${noteId}`);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note supprimée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Show reminder toast after 5 seconds if not validated
  useEffect(() => {
    if (simulation && !isValidated && !loading) {
      const timer = setTimeout(() => {
        toast.info(
          language === 'fr' 
            ? '📋 N\'oubliez pas de valider et envoyer votre CAPEX à la direction !' 
            : '📋 Don\'t forget to validate and send your CAPEX to management!',
          {
            duration: 8000,
            action: {
              label: language === 'fr' ? 'Voir' : 'View',
              onClick: () => {
                document.getElementById('proposition-client-section')?.scrollIntoView({ behavior: 'smooth' });
              }
            }
          }
        );
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [simulation, isValidated, loading, language]);

  // NOTE: We no longer auto-recalculate for the Tool tab
  // The system_red_months_count stores the original calculation result
  // This ensures consistency between the workflow summary and the detail view

  // Handle validation submission
  const handleValidation = async () => {
    // No longer require checkbox confirmation - button click is the confirmation
    if (!validationData.ca_previsionnel_ht || validationData.ca_previsionnel_ht <= 0) {
      toast.error(language === 'fr' ? 'Veuillez saisir le CA prévisionnel' : 'Please enter the projected CA');
      return;
    }

    setValidating(true);
    try {
      // Use modify-proposal endpoint if already validated (modification), otherwise use validate endpoint
      const endpoint = simulation?.validated 
        ? `/simulations/${id}/modify-proposal`
        : `/simulations/${id}/validate`;
      
      await api().post(endpoint, {
        commission_rate: validationData.commission_rate,
        ca_previsionnel_ht: parseFloat(validationData.ca_previsionnel_ht),
        commercial_comment: validationData.commercial_comment || null
      });
      
      setIsValidated(true);
      
      // Refresh simulation data
      const response = await api().get(`/simulations/${id}`);
      setSimulation(response.data);
      
      const message = simulation?.validated 
        ? (language === 'fr' ? 'Proposition modifiée et renvoyée à la Direction !' : 'Proposal modified and resent to Management!')
        : (language === 'fr' ? 'Proposition envoyée à la Direction Wasabi !' : 'Proposal sent to Wasabi Management!');
      toast.success(message);
    } catch (error) {
      console.error('Validation failed:', error);
      const errorMessage = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de l\'envoi' : 'Sending failed');
      toast.error(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  // Dialogue "Date de signature" (obligatoire pour statut Gagné / Signé)
  const [sigDialog, setSigDialog] = useState({ open: false, status: null });
  const [sigDate, setSigDate] = useState('');
  const openSignatureDialog = (status) => {
    setSigDate(simulation?.date_signature || new Date().toISOString().slice(0, 10));
    setSigDialog({ open: true, status });
  };
  const confirmSignature = async () => {
    if (!sigDate) {
      toast.error(language === 'fr' ? 'Veuillez indiquer la date de signature' : 'Please enter the signature date');
      return;
    }
    const d = new Date(sigDate + 'T00:00:00');
    const created = simulation?.created_at ? new Date(simulation.created_at) : null;
    const maxDate = new Date(); maxDate.setFullYear(maxDate.getFullYear() + 1);
    if (created && d < new Date(created.getFullYear(), created.getMonth(), created.getDate())) {
      toast.error(language === 'fr' ? 'La date de signature ne peut pas être antérieure à la création du dossier' : 'Signature date cannot be before the project creation date');
      return;
    }
    if (d > maxDate) {
      toast.error(language === 'fr' ? 'La date de signature est trop éloignée dans le futur (max 1 an)' : 'Signature date is too far in the future (max 1 year)');
      return;
    }
    try {
      await api().patch(`/simulations/${id}/business-status`, { business_status: sigDialog.status, date_signature: sigDate });
      const response = await api().get(`/simulations/${id}`);
      setSimulation(response.data);
      setIsEditingFinalStatus(false);
      setSigDialog({ open: false, status: null });
      toast.success(sigDialog.status === 'signed'
        ? (language === 'fr' ? 'Offre définitive validée / signée' : 'Final offer validated / signed')
        : (language === 'fr' ? 'Dossier Gagné' : 'Project Won'));
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  // Recalculate monthly breakdown with custom commission rate
  const handleRecalculateCommission = async () => {
    if (!customCommissionRate || customCommissionRate <= 0 || customCommissionRate > 1) {
      toast.error(language === 'fr' ? 'Veuillez saisir un taux valide (ex: 0.19 pour 19%)' : 'Please enter a valid rate (e.g., 0.19 for 19%)');
      return;
    }

    setRecalculating(true);
    try {
      const response = await api().post(`/simulations/${id}/recalculate-commission`, {
        commission_rate: parseFloat(customCommissionRate)
      });
      
      // Update local simulation state with new data
      setSimulation(prev => ({
        ...prev,
        monthly_breakdown_y1: response.data.monthly_breakdown_y1,
        red_months_count: response.data.red_months_count,
        verdict: response.data.verdict,
        verdict_status: response.data.verdict_status,
        commission_rate_used: response.data.commission_rate_used
      }));
      
      setShowCommissionEdit(false);
      toast.success(
        language === 'fr' 
          ? `Recalculé avec ${(customCommissionRate * 100).toFixed(1)}% de commission` 
          : `Recalculated with ${(customCommissionRate * 100).toFixed(1)}% commission`
      );
    } catch (error) {
      console.error('Recalculation failed:', error);
      toast.error(language === 'fr' ? 'Erreur lors du recalcul' : 'Recalculation failed');
    } finally {
      setRecalculating(false);
    }
  };

  // Recalculate with both custom CA and commission rate (manual mode)
  const handleManualRecalculate = async () => {
    const caValue = customCA || previewData?.ca_ht_annual || simulation.average_hypothesis?.ca_ht_annual || 0;
    const commissionValue = customCommissionRate || previewData?.commission_rate || simulation.commission_rate_used || 0.18;
    
    if (!caValue || caValue <= 0) {
      toast.error(language === 'fr' ? 'Veuillez saisir un CA valide' : 'Please enter a valid CA');
      return;
    }
    if (!commissionValue || commissionValue <= 0 || commissionValue > 1) {
      toast.error(language === 'fr' ? 'Veuillez saisir un taux valide' : 'Please enter a valid rate');
      return;
    }

    setRecalculating(true);
    try {
      // Use preview-recalculate to get updated values
      const response = await api().post(`/simulations/${id}/preview-recalculate`, {
        ca_ht_annual: parseFloat(caValue),
        commission_rate: parseFloat(commissionValue)
      });
      
      // Set preview data with recalculated values
      setPreviewData(response.data);
      setAnalysisSource('tool'); // Reset to show "manual" recalculation
      setShowCommissionEdit(false);
      
      toast.success(
        language === 'fr' 
          ? `Recalculé avec CA ${formatCurrency(caValue)} et commission ${(commissionValue * 100).toFixed(1)}%` 
          : `Recalculated with CA ${formatCurrency(caValue)} and commission ${(commissionValue * 100).toFixed(1)}%`
      );
    } catch (error) {
      console.error('Manual recalculation failed:', error);
      toast.error(language === 'fr' ? 'Erreur lors du recalcul' : 'Recalculation failed');
    } finally {
      setRecalculating(false);
    }
  };

  // Save custom CA and commission permanently
  const handleSaveCustomParameters = async () => {
    const caValue = previewData?.ca_ht_annual || customCA || simulation.ca_ht_used_for_monthly || simulation.average_hypothesis?.ca_ht_annual || 0;
    const commissionValue = previewData?.commission_rate || customCommissionRate || simulation.commission_rate_used || 0.18;
    
    if (!caValue || caValue <= 0) {
      toast.error(language === 'fr' ? 'Aucune valeur de CA à sauvegarder' : 'No CA value to save');
      return;
    }

    setRecalculating(true);
    try {
      const response = await api().post(`/simulations/${id}/save-custom-parameters`, {
        ca_ht_annual: parseFloat(caValue),
        commission_rate: parseFloat(commissionValue)
      });
      
      // Update local simulation state with saved values
      setSimulation(prev => ({
        ...prev,
        commission_rate_used: response.data.commission_rate,
        ca_ht_used_for_monthly: response.data.ca_ht_annual,
        cluster_used: response.data.cluster_used,
        food_cost_rate_used: response.data.food_cost_rate_used,
        other_cost_rate_used: response.data.other_cost_rate_used,
        monthly_breakdown_y1: response.data.monthly_breakdown_y1,
        red_months_count: response.data.red_months_count,
        verdict: response.data.verdict,
        verdict_status: response.data.verdict_status
      }));
      
      // Clear preview data since it's now saved
      setPreviewData(null);
      setShowCommissionEdit(false);
      
      toast.success(
        language === 'fr' 
          ? `Paramètres sauvegardés: CA ${formatCurrency(caValue)} et commission ${(commissionValue * 100).toFixed(1)}%` 
          : `Parameters saved: CA ${formatCurrency(caValue)} and commission ${(commissionValue * 100).toFixed(1)}%`
      );
    } catch (error) {
      console.error('Save custom parameters failed:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Save failed');
    } finally {
      setRecalculating(false);
    }
  };

  // Admin decision handlers
  const handleAdminDecision = async (status) => {
    if (!canMakeDecision) {
      toast.error(language === 'fr' ? 'Vous n\'êtes pas autorisé à prendre cette décision' : 'You are not authorized to make this decision');
      return;
    }

    setSubmittingDecision(true);
    try {
      await api().put(`/simulations/${id}/status`, {
        status: status,
        comment: adminDecision.comment
      });
      
      const { data: fresh } = await api().get(`/simulations/${id}`);
      setSimulation(fresh);
      
      const statusLabels = {
        approved: language === 'fr' ? 'GO - Projet validé' : 'GO - Project approved',
        rejected: language === 'fr' ? 'NO GO - Projet refusé' : 'NO GO - Project rejected',
        pending_review: language === 'fr' ? 'À l\'étude' : 'Under review'
      };
      
      toast.success(statusLabels[status]);
      setAdminDecision({ status: '', comment: '', counter_commission: 0.18, counter_ca: '' });
    } catch (error) {
      console.error('Admin decision failed:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la mise à jour du statut' : 'Status update failed');
    } finally {
      setSubmittingDecision(false);
    }
  };

  const handleCounterProposal = async () => {
    if (!canMakeDecision) {
      toast.error(language === 'fr' ? 'Vous n\'êtes pas autorisé à faire une contre-proposition' : 'You are not authorized to make a counter-proposal');
      return;
    }
    
    if (!adminDecision.counter_ca || adminDecision.counter_ca <= 0) {
      toast.error(language === 'fr' ? 'Veuillez saisir le CA prévisionnel' : 'Please enter the projected CA');
      return;
    }

    setSubmittingDecision(true);
    try {
      await api().post(`/simulations/${id}/counter-proposal`, {
        commission_rate: adminDecision.counter_commission,
        ca_previsionnel_ht: parseFloat(adminDecision.counter_ca),
        comment: adminDecision.comment
      });
      
      // Recharger la simulation : contribution margin et calculs recalculés côté serveur
      const { data: fresh } = await api().get(`/simulations/${id}`);
      setSimulation(fresh);
      
      toast.success(language === 'fr' ? 'Contre-proposition envoyée' : 'Counter-proposal sent');
      setShowCounterProposal(false);
      setAdminDecision({ status: '', comment: '', counter_commission: 0.18, counter_ca: '' });
    } catch (error) {
      console.error('Counter-proposal failed:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi de la contre-proposition' : 'Counter-proposal failed');
    } finally {
      setSubmittingDecision(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(language === 'fr' ? 'Voulez-vous vraiment supprimer cette simulation ?' : 'Are you sure you want to delete this simulation?')) {
      return;
    }
    
    try {
      await api().delete(`/simulations/${id}`);
      toast.success(t('success'));
      navigate('/history');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Duplicate simulation
  const [duplicating, setDuplicating] = useState(false);
  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const response = await api().post(`/simulations/${id}/duplicate`);
      toast.success(language === 'fr' ? 'Simulation dupliquée avec succès !' : 'Simulation duplicated!');
      navigate(`/simulation/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la duplication' : 'Duplication failed'));
    } finally {
      setDuplicating(false);
    }
  };

  // Send to HG (partner) - sends PDF and Excel
  const [sendingToHG, setSendingToHG] = useState(false);
  const [hgModalOpen, setHgModalOpen] = useState(false);
  const [hgPreview, setHgPreview] = useState(null);
  const [hgIncludeContracts, setHgIncludeContracts] = useState(true);
  const [hgNote, setHgNote] = useState('');
  const [hgHistory, setHgHistory] = useState([]);
  const [hgHistoryOpen, setHgHistoryOpen] = useState(false);

  const openHGModal = async () => {
    try {
      const [pv, hist] = await Promise.all([
        api().get(`/simulations/${id}/hg-preview`),
        api().get(`/simulations/${id}/hg-history`).catch(() => ({ data: { items: [] } })),
      ]);
      setHgPreview(pv.data);
      setHgIncludeContracts(pv.data.has_contract_attachments);
      setHgNote('');
      setHgHistory(hist.data.items || []);
      setHgHistoryOpen(false);
      setHgModalOpen(true);
    } catch (e) {
      toast.error(language === 'fr' ? "Impossible de charger l'aperçu HG" : "Unable to load HG preview");
    }
  };

  const handleSendToHG = async () => {
    setSendingToHG(true);
    try {
      const response = await api().post(`/simulations/${id}/send-to-hg`, {
        include_contract_attachments: hgIncludeContracts,
        note: hgNote || null,
      });
      const cnt = response.data.contract_attachments_count || 0;
      const baseMsg = language === 'fr'
        ? `Dossier envoyé à HG avec succès ! (${response.data.recipients?.length || 0} destinataire(s))`
        : `Successfully sent to HG! (${response.data.recipients?.length || 0} recipient(s))`;
      const extra = cnt > 0
        ? (language === 'fr' ? ` + ${cnt} contrat(s) joint(s)` : ` + ${cnt} contract(s) attached`)
        : '';
      toast.success(baseMsg + extra);
      // Refresh history + bump local sim hg_history count for the badge
      try {
        const h = await api().get(`/simulations/${id}/hg-history`);
        setHgHistory(h.data.items || []);
        setSimulation(prev => prev ? { ...prev, hg_history: h.data.items || [] } : prev);
      } catch { /* silent */ }
      setHgModalOpen(false);
    } catch (error) {
      console.error('Send to HG error:', error);
      const message = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de l\'envoi à HG' : 'Failed to send to HG');
      toast.error(message);
    } finally {
      setSendingToHG(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/simulations/${id}/export/${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Use formatted project name for export file
      const formattedName = `ETUDE_CAPEX_${simulation.input_data?.general_info?.retailer_name || 'X'}_${simulation.input_data?.general_info?.city || 'X'}`.toUpperCase().replace(/\s+/g, '_');
      a.download = `${formattedName}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success(language === 'fr' ? 'Export réussi !' : 'Export successful!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'export' : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Open Contract Request Modal with pre-filled data or saved draft
  const [societeMemoire, setSocieteMemoire] = useState(null);
  const applySocieteMemoire = async (enseigne, codePostal, onlyIfEmpty) => {
    setSocieteMemoire(null);
    if (!enseigne || !codePostal) return;
    try {
      const { data } = await api().get('/societes/memoire', { params: { enseigne, code_postal: codePostal } });
      const m = data?.memoire;
      if (!m) return;
      setContractData(prev => {
        if (onlyIfEmpty && prev.raison_sociale) return prev;
        return { ...prev, raison_sociale: m.raison_sociale || prev.raison_sociale, siret: m.siret || prev.siret, capital: m.capital || prev.capital,
          prenom_dirigeant: m.prenom_dirigeant || prev.prenom_dirigeant, nom_dirigeant: m.nom_dirigeant || prev.nom_dirigeant,
          genre_dirigeant: m.genre_dirigeant || prev.genre_dirigeant };
      });
      setSocieteMemoire(m);
    } catch (e) { /* mémoire indisponible : saisie normale */ }
  };
  const rememberSociete = (d) => api().put('/societes/memoire', d).catch(() => {});

  const openContractModal = async () => {
    setContractDraftLoaded(false);
    
    // Load contract recipients for email dropdown
    try {
      const recipientsResponse = await api().get('/admin/contract-recipients');
      setContractRecipients(recipientsResponse.data || []);
    } catch (error) {
      console.log('Could not load contract recipients');
    }
    
    // Taux de commission = accord direction (Décision Direction) en priorité
    const directionRate = simulation?.admin_counter_proposal?.commission_rate
      ?? simulation?.workflow_data?.final_offer?.commission_rate
      ?? simulation?.workflow_data?.direction_decision?.commission_rate
      ?? simulation?.commission_rate_proposed;
    const directionRatePct = directionRate ? String(+(directionRate < 1 ? directionRate * 100 : directionRate).toFixed(1)) : '';

    // Investissement CAPEX + Concept = étape 4/5 de la simulation
    const cx = simulation?.input_data?.capex_investment || {};
    const inv = (v) => (String(v || 'CCD').toUpperCase() === 'CLIENT' ? 'Client' : 'CCD');
    const concepts = String(cx.concept || '').toUpperCase().split(',').map(c => c.trim().replace('_', ' '));
    const capexFromSim = {
      corner_investor: inv(cx.corner_investor),
      vitrine_investor: inv(cx.vitrine_investor),
      inox_investor: inv(cx.inox_investor),
      concept_wasabi: concepts.includes('WASABI'),
      concept_mme_wang: concepts.includes('MME WANG'),
      concept_sorae: concepts.includes('SORAE'),
      concept_pokawa: concepts.includes('POKAWA'),
    };

    // First, try to load saved draft
    try {
      const draftResponse = await api().get(`/simulations/${id}/contract-draft`);
      if (draftResponse.data?.has_draft && draftResponse.data?.data) {
        const savedData = draftResponse.data.data;
        // Recalculate capex label
        const capexCarrierLabel = getCapexCarrierLabel(savedData.enseigne || '', savedData.capex_carrier || 'CCD');
        setContractData({
          ...savedData,
          ...capexFromSim,
          ...(directionRatePct ? { taux_commission: directionRatePct } : {}),
          capex_carrier_label: capexCarrierLabel
        });
        setContractDraftLoaded(true);
        setShowContractModal(true);
        applySocieteMemoire(savedData.enseigne, savedData.code_postal, true);
        return;
      }
    } catch (error) {
      console.log('No draft found, using simulation data');
    }
    
    // No draft found - use simulation data as defaults
    const generalInfo = simulation?.input_data?.general_info || {};
    const finalOffer = simulation?.workflow_data?.final_offer || simulation?.workflow_data?.direction_decision || {};
    
    // Get commission rate and CA from final offer or validated commercial proposal
    let commissionRate = directionRate || finalOffer?.commission_rate || simulation?.workflow_data?.commercial_proposal?.commission_rate || '';
    let caPrevisionnel = finalOffer?.ca || simulation?.workflow_data?.commercial_proposal?.ca || '';
    
    // Format commission rate as percentage
    if (commissionRate && commissionRate < 1) {
      commissionRate = String(+(commissionRate * 100).toFixed(1));
    }
    
    // Determine CAPEX carrier
    const capexCarrierRaw = generalInfo.capex_carrier === 'client' ? 'Client' : 'CCD';
    const enseigne = generalInfo.retailer_name || '';
    const capexCarrierLabel = getCapexCarrierLabel(enseigne, capexCarrierRaw);
    
    // Get zone de chalandise from simulation if available
    const zoneVoiture = generalInfo.zone_chalandise_voiture || '10';
    const zonePied = generalInfo.zone_chalandise_pied || '30';
    
    setContractData({
      enseigne: enseigne,
      raison_sociale: generalInfo.raison_sociale || '',
      capital: generalInfo.capital || '',
      siret: generalInfo.siret || '',
      adresse: generalInfo.address || '',
      code_postal: generalInfo.postal_code || '',
      ville: generalInfo.city || '',
      genre_dirigeant: 'Monsieur',
      prenom_dirigeant: '',
      nom_dirigeant: '',
      date_mise_en_place: '',
      duree_mois: '36',
      ca_minimum_mensuel: '15000',
      ca_sortie_anticipee: '15000',
      date_signature: '',
      surface_m2: generalInfo.surface_m2?.toString() || '',
      capex_carrier: capexCarrierRaw,
      capex_carrier_label: capexCarrierLabel,
      zone_chalandise_voiture: zoneVoiture,
      zone_chalandise_pied: zonePied,
      taux_commission: commissionRate?.toString() || '',
      ...capexFromSim
    });
    setShowContractModal(true);
    applySocieteMemoire(enseigne, generalInfo.postal_code || '', false);
  };

  // Recherche société (Pappers, secours API publique) — remplit les champs, qui restent modifiables
  const [societeSearch, setSocieteSearch] = useState({ loading: false, results: null });
  const searchSocietes = async () => {
    setSocieteSearch({ loading: true, results: null });
    try {
      const { data } = await api().get('/societes/recherche', { params: { enseigne: contractData.enseigne, code_postal: contractData.code_postal, ville: contractData.ville } });
      setSocieteSearch({ loading: false, results: data.results || [], fallback: data.fallback, excluded: data.excluded_small });
    } catch (e) {
      setSocieteSearch({ loading: false, results: null });
      toast.error(e?.response?.data?.detail || 'Recherche impossible');
    }
  };
  const pickSociete = (r) => {
    const siret = (r.siret || '').replace(/^(\d{3})(\d{3})(\d{3})(\d{5})$/, '$1 $2 $3 $4');
    setContractData(prev => ({
      ...prev,
      raison_sociale: r.raison_sociale || prev.raison_sociale,
      siret: siret || prev.siret,
      capital: r.capital != null ? String(r.capital) : prev.capital,
      ...(r.dirigeant ? {
        prenom_dirigeant: r.dirigeant.prenom || '',
        nom_dirigeant: r.dirigeant.nom || '',
        ...(r.dirigeant.genre ? { genre_dirigeant: r.dirigeant.genre } : {}),
      } : {}),
    }));
    setSocieteSearch({ loading: false, results: null });
    toast.success(r.capital == null ? 'Société remplie — pensez à saisir le capital' : 'Société remplie (modifiable)');
  };

  // Save Contract Draft
  const handleSaveContractDraft = async () => {
    setSavingContractDraft(true);
    try {
      await api().post(`/simulations/${id}/contract-draft`, contractData);
      rememberSociete(contractData);
      toast.success('Brouillon enregistré avec succès');
      setContractDraftLoaded(true);
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingContractDraft(false);
    }
  };

  // Send Contract Email (using saved draft)
  const handleSendContractEmail = async () => {
    const allEmails = contractSendData.recipient_emails.filter(e => e.trim());
    if (allEmails.length === 0) {
      toast.error('Veuillez sélectionner au moins un destinataire');
      return;
    }
    
    setSendingContract(true);
    try {
      await api().post(`/simulations/${id}/contract-draft`, contractData);
      rememberSociete(contractData);
      
      // Send to each selected recipient
      const names = contractSendData.recipient_names;
      for (let i = 0; i < allEmails.length; i++) {
        await api().post(`/simulations/${id}/contract-draft/send`, {
          recipient_email: allEmails[i],
          recipient_name: names[i] || allEmails[i],
          message: contractSendData.message,
        });
      }
      toast.success(`Contrat envoyé à ${allEmails.join(', ')}`);
      setContractSendModal(false);
      setContractSendData({ recipient_emails: [], recipient_names: [], message: '' });
      loadContractHistory();
    } catch (error) {
      console.error('Error sending contract:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingContract(false);
    }
  };

  // Export Contract Request to Excel
  const handleExportContractExcel = async () => {
    if (!contractData.enseigne || !contractData.ville || !contractData.taux_commission || !contractData.date_mise_en_place) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    // Update label before export
    const dataToExport = {
      ...contractData,
      capex_carrier_label: getCapexCarrierLabel(contractData.enseigne, contractData.capex_carrier)
    };
    
    setExportingContract(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/simulations/${id}/contract-request/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToExport)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = `Demande_Contrat_${contractData.enseigne}_${contractData.ville}.xlsx`.replace(/\s+/g, '_');
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Export Excel réussi !');
      setShowContractModal(false);
      // Refresh contract history after export
      loadContractHistory();
    } catch (error) {
      console.error('Contract export error:', error);
      toast.error(error.message || 'Erreur lors de l\'export');
    } finally {
      setExportingContract(false);
    }
  };

  // Load contract requests history
  const loadContractHistory = async () => {
    setLoadingContractHistory(true);
    try {
      const response = await api().get(`/simulations/${id}/contract-requests`);
      setContractHistory(response.data || []);
    } catch (error) {
      console.error('Failed to load contract history:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoadingContractHistory(false);
    }
  };

  // Re-export a contract request from history
  const handleReexportContract = async (requestId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/contract-requests/${requestId}/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Demande_Contrat_reexport.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Ré-export réussi !');
    } catch (error) {
      console.error('Reexport error:', error);
      toast.error('Erreur lors du ré-export');
    }
  };

  // Send contract request by email
  // Send contract email from history (re-send existing request)
  const handleSendContractFromHistory = async () => {
    const allEmails = contractEmailData.recipient_emails.filter(e => e.trim());
    if (allEmails.length === 0) {
      toast.error('Veuillez sélectionner au moins un destinataire');
      return;
    }
    
    setSendingContractEmail(contractEmailModal.requestId);
    try {
      const names = contractEmailData.recipient_names;
      for (let i = 0; i < allEmails.length; i++) {
        await api().post(`/contract-requests/${contractEmailModal.requestId}/send`, {
          recipient_email: allEmails[i],
          recipient_name: names[i] || allEmails[i],
          message: contractEmailData.message,
        });
      }
      toast.success(`Email envoyé à ${allEmails.join(', ')}`);
      setContractEmailModal({ open: false, requestId: null });
      setContractEmailData({ recipient_emails: [], recipient_names: [], message: '' });
    } catch (error) {
      console.error('Send contract email error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingContractEmail(null);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
        </div>
      </AppLayout>
    );
  }

  if (!simulation) {
    return null;
  }

  // Handle send exports by email
  const handleSendExportsByEmail = async () => {
    if (!sendEmailData.recipients.trim()) {
      toast.error(language === 'fr' ? 'Veuillez saisir au moins un destinataire' : 'Please enter at least one recipient');
      return;
    }
    
    if (!sendEmailData.includePdf && !sendEmailData.includeExcel) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner au moins un type de document' : 'Please select at least one document type');
      return;
    }
    
    setSendingEmail(true);
    try {
      const recipients = sendEmailData.recipients.split(/[,;\s]+/).filter(email => email.trim());
      
      const response = await api.post(`/api/simulations/${simulation.id}/send-exports`, {
        recipients,
        include_pdf: sendEmailData.includePdf,
        include_excel: sendEmailData.includeExcel,
        message: sendEmailData.message || null
      });
      
      toast.success(language === 'fr' 
        ? `Documents envoyés à ${response.data.sent_count} destinataire(s)` 
        : `Documents sent to ${response.data.sent_count} recipient(s)`
      );
      
      setShowSendEmailModal(false);
      setSendEmailData({ recipients: '', includePdf: true, includeExcel: true, message: '' });
    } catch (error) {
      console.error('Error sending exports:', error);
      toast.error(error.response?.data?.detail || 'Error sending exports');
    }
    setSendingEmail(false);
  };

  return (
    <AppLayout>
      <div className="space-y-8 animate-fade-in" data-testid="simulation-result-page">
        <SimulationBridge simulationId={id} />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" data-testid="simulation-back-btn" title="Retour aux simulations en cours" onClick={() => navigate('/simulations/tableau')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-3 flex-wrap">
                {getFormattedProjectName()}
                <CornerCategoryBadge ca={simulationRetainedCa(simulation)} />
              </h1>
              <p className="text-slate-500 dark:text-slate-400 flex items-center gap-2">
                {simulation.input_data.general_info.project_type}
                <VitrineBadge format={simulation.input_data.general_info.format} testid="result-vitrine-badge" />
              </p>
              {simulation.input_data.general_info.format === 'vitrine_deportee' && (
                <p className="text-[11px] text-orange-700 mt-1" data-testid="result-vitrine-note">
                  Vitrine déportée : CA = 50 % d'un corner · frais de personnel = 10 % d'un corner · CAPEX vitrine uniquement
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button 
              variant="outline" 
              className="gap-2" 
              onClick={() => navigate(`/simulation/edit/${id}`)}
              data-testid="edit-btn"
            >
              <Pencil className="w-4 h-4" />
              {language === 'fr' ? 'Modifier' : 'Edit'}
            </Button>
            <Button 
              variant="outline" 
              className="gap-2" 
              onClick={handleDuplicate}
              disabled={duplicating}
              data-testid="duplicate-btn"
            >
              {duplicating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
              {language === 'fr' ? 'Dupliquer' : 'Duplicate'}
            </Button>
            <Button 
              variant="outline" 
              className="gap-2 bg-gradient-to-r from-amber-300 to-yellow-400 border-amber-500 text-amber-900 hover:from-amber-400 hover:to-yellow-500 font-semibold shadow-sm" 
              onClick={() => navigate(`/prerequis-techniques/simulation/${id}`)}
              data-testid="prerequis-techniques-btn"
              title="Prérequis Techniques"
            >
              <ShieldCheck className="w-4 h-4" />
              Prérequis Techniques
              {prereqStatus?.validated && <CheckCircle2 className="w-3.5 h-3.5 text-green-700 ml-1" />}
              {prereqStatus?.exists && !prereqStatus?.validated && <AlertTriangle className="w-3.5 h-3.5 text-red-700 ml-1" />}
            </Button>
            <Button
              variant="outline"
              className="gap-2 bg-gradient-to-r from-orange-500 to-orange-600 border-orange-600 text-white hover:from-orange-600 hover:to-orange-700 font-semibold shadow-sm"
              onClick={() => navigate(`/croquis-agenceur?etudeId=${id}`)}
              data-testid="croquis-agenceur-btn"
              title="Faire une demande de croquis"
            >
              <Presentation className="w-4 h-4" />
              Croquis Corner
            </Button>
            <Button 
              variant="outline" 
              className="gap-2 bg-gradient-to-r from-yellow-50 to-amber-50 border-amber-300 text-amber-700 hover:from-yellow-100 hover:to-amber-100" 
              onClick={openContractModal}
              data-testid="contract-request-btn"
              title="Générer une demande de contrat"
            >
              <FileSignature className="w-4 h-4" />
              {language === 'fr' ? 'Demande de contrat' : 'Contract Request'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="gap-2" 
                  disabled={exporting || simulation.business_status !== 'signed'} 
                  data-testid="export-btn"
                  title={simulation.business_status !== 'signed' ? (language === 'fr' ? "Validez l'offre définitive pour exporter" : "Validate final offer to export") : ''}
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {t('export')}
                  {simulation.business_status !== 'signed' && (
                    <Lock className="w-3 h-3 ml-1 text-amber-500" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('excel')} className="gap-2 cursor-pointer" data-testid="export-excel-btn">
                  <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                  {t('exportExcel')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('pdf')} className="gap-2 cursor-pointer" data-testid="export-pdf-btn">
                  <FileText className="w-4 h-4 text-red-600" />
                  {t('exportPDF')}
                </DropdownMenuItem>
                {(user?.role === 'admin' || user?.role === 'super_admin') && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => setShowSendEmailModal(true)} 
                      className="gap-2 cursor-pointer" 
                      data-testid="send-email-btn"
                    >
                      <Mail className="w-4 h-4 text-blue-600" />
                      {language === 'fr' ? 'Envoyer par email' : 'Send by email'}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Send to HG Button - Only for approved (GO) simulations */}
            {(user?.role === 'admin' || user?.role === 'super_admin') && simulation.admin_status === 'approved' && (
              <Button 
                onClick={openHGModal}
                disabled={sendingToHG}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white gap-2 relative"
                data-testid="send-to-hg-btn"
              >
                {sendingToHG ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {language === 'fr' ? 'Envoyer à HG' : 'Send to HG'}
                {(simulation?.hg_history?.length || 0) > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[1.4rem] h-5 px-1.5 ml-1 rounded-full bg-white text-amber-700 text-[11px] font-bold border border-amber-300 shadow-sm"
                    title={language === 'fr'
                      ? `Déjà envoyé ${simulation.hg_history.length} fois à HG`
                      : `Already sent ${simulation.hg_history.length} times to HG`}
                    data-testid="hg-send-count-badge"
                  >
                    {simulation.hg_history.length}
                  </span>
                )}
              </Button>
            )}
            
            {/* Annonce ALL shortcut - admin/super_admin only, when the offer is validated (GO) */}
            {(user?.role === 'admin' || user?.role === 'super_admin') && simulation.admin_status === 'approved' && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setShowAnnouncementModal(true)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
                  title={language === 'fr' ? 'Envoyer une Annonce ALL' : 'Send ALL announcement'}
                  data-testid="announce-all-shortcut-btn"
                >
                  <Megaphone className="w-4 h-4" />
                  ALL
                </Button>
                {announcementStatus && (() => {
                  const cfg = {
                    brouillon: { cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200', fr: 'Brouillon', en: 'Draft' },
                    en_attente: { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', fr: 'Soumise', en: 'Submitted' },
                    valide: { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', fr: 'Validée', en: 'Approved' },
                    refuse: { cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', fr: 'Refusée', en: 'Rejected' },
                  }[announcementStatus];
                  if (!cfg) return null;
                  return (
                    <Badge className={`text-xs ${cfg.cls}`} data-testid="announcement-status-badge">
                      {language === 'fr' ? `Annonce : ${cfg.fr}` : `Announcement: ${cfg.en}`}
                    </Badge>
                  );
                })()}
              </div>
            )}
            
            <Button variant="destructive" size="icon" onClick={handleDelete} data-testid="delete-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <LinkedComparison simId={id} />

        {/* Validation Workflow - Visual Timeline - AVANT Analyse Mensuelle */}
        <ValidationWorkflow 
          simulation={simulation} 
          language={language} 
          isCommercial={user?.role === 'commercial' || simulation.user_id === user?.id}
          analysisSource={analysisSource}
          previewData={previewData}
          // Pass dynamic values that update when hypotheses are toggled
          dynamicCaHtAnnual={simulation.average_hypothesis?.ca_ht_annual}
          dynamicCommissionRate={getDynamicCommissionRate()}
          dynamicRedMonths={simulation.red_months_count}
          onRespondCounterProposal={async (response, comment) => {
            try {
              const fetchResponse = await fetch(`${BACKEND_URL}/api/simulations/${simulation.id}/respond-counter-proposal`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('capex_token')}`
                },
                body: JSON.stringify({ response, comment })
              });
              const responseData = await fetchResponse.json();
              if (!fetchResponse.ok) {
                throw new Error(responseData.detail || 'Error');
              }
              toast.success(language === 'fr' 
                ? `Contre-proposition ${response === 'accept' ? 'acceptée' : 'refusée'}`
                : `Counter-proposal ${response === 'accept' ? 'accepted' : 'refused'}`
              );
              // Refresh simulation data
              const res = await fetch(`${BACKEND_URL}/api/simulations/${simulation.id}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('capex_token')}` }
              });
              const updatedSim = await res.json();
              setSimulation(updatedSim);
            } catch (error) {
              console.error('Error responding to counter-proposal:', error);
              toast.error(error.message || 'Error');
              throw error;
            }
          }}
          onModifyCommercial={async (data) => {
            try {
              const response = await fetch(`${BACKEND_URL}/api/simulations/${simulation.id}/modify-proposal`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('capex_token')}`
                },
                body: JSON.stringify({
                  ca_previsionnel_ht: data.ca_previsionnel_ht,
                  commission_rate: data.commission_rate
                })
              });
              const responseData = await response.json();
              if (!response.ok) {
                throw new Error(responseData.detail || 'Error');
              }
              toast.success(language === 'fr' 
                ? 'Proposition commerciale mise à jour'
                : 'Commercial proposal updated'
              );
              // Refresh simulation data
              const res = await fetch(`${BACKEND_URL}/api/simulations/${simulation.id}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('capex_token')}` }
              });
              const updatedSim = await res.json();
              setSimulation(updatedSim);
            } catch (error) {
              console.error('Error modifying commercial proposal:', error);
              toast.error(error.message || 'Error');
              throw error;
            }
          }}
          onModifyDirection={async (data) => {
            try {
              const response = await fetch(`${BACKEND_URL}/api/simulations/${simulation.id}/modify-direction-decision`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('capex_token')}`
                },
                body: JSON.stringify({
                  ca_previsionnel_ht: data.ca_previsionnel_ht,
                  commission_rate: data.commission_rate
                })
              });
              const responseData = await response.json();
              if (!response.ok) {
                throw new Error(responseData.detail || 'Error');
              }
              toast.success(language === 'fr' 
                ? 'Décision Direction mise à jour'
                : 'Direction decision updated'
              );
              // Refresh simulation data
              const res = await fetch(`${BACKEND_URL}/api/simulations/${simulation.id}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('capex_token')}` }
              });
              const updatedSim = await res.json();
              setSimulation(updatedSim);
            } catch (error) {
              console.error('Error modifying direction decision:', error);
              toast.error(error.message || 'Error');
              throw error;
            }
          }}
          onValidateDirection={async () => {
            // Only allow validation if simulation has admin approval/counter-proposal
            if (!simulation.admin_status || simulation.admin_status === 'pending') {
              toast.error(language === 'fr' 
                ? 'La Direction doit d\'abord prendre une décision'
                : 'Direction must first make a decision'
              );
              return;
            }
            // Demander la date de signature (obligatoire pour un dossier signé)
            openSignatureDialog('signed');
          }}
        />
        <DecisionHistory simulationId={simulation.id} refreshKey={`${simulation.admin_status}-${simulation.updated_at}-${simulation.admin_status_updated_at}`} />

        {/* Renewal savings banner */}
        {simulation.input_data?.general_info?.project_type === 'Renouvellement contrat' && (() => {
          const gi = simulation.input_data.general_info;
          const items = [];
          if (gi.renouvellement_change_inox === 'Non') items.push({ label: 'Inox', amount: 7000 });
          if (gi.renouvellement_change_vitrines === 'Non') items.push({ label: 'Vitrine(s)', amount: 8000 });
          if (gi.renouvellement_change_plv === 'Non') items.push({ label: 'PLV du corner', amount: 12000 });
          if (items.length === 0) return null;
          const total = items.reduce((s, i) => s + i.amount, 0);
          const fmt = (n) => n.toLocaleString('fr-FR') + ' €';
          return (
            <div className="p-4 rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-amber-50/40" data-testid="renewal-savings-banner-result">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <span className="text-emerald-700 text-lg font-bold">−</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-emerald-800">
                    Renouvellement allégé : <span className="text-base">{fmt(total)}</span> exclus du CAPEX
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    Éléments non remplacés : {items.map((i, k) => (
                      <span key={i.label} className="inline-block ml-1">
                        <span className="font-medium text-slate-700">{i.label}</span>
                        <span className="text-slate-500"> ({fmt(i.amount)})</span>
                        {k < items.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* CAPEX auto-correction banner (admin only) */}
        {isAdmin && simulation.capex_auto_corrected && simulation.capex_auto_correction && (
          <div
            className="mb-4 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-4 flex items-start gap-3"
            data-testid="capex-auto-corrected-banner"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              <p className="font-semibold">
                {language === 'fr' ? 'CAPEX réajusté automatiquement' : 'CAPEX automatically adjusted'}
              </p>
              <p className="mt-0.5">
                {language === 'fr' ? 'Total : ' : 'Total: '}
                <span className="font-mono line-through opacity-70">{formatCurrency(simulation.capex_auto_correction.old_total)}</span>
                {' → '}
                <span className="font-mono font-semibold">{formatCurrency(simulation.capex_auto_correction.new_total)}</span>
                {simulation.capex_auto_corrected_at && (
                  <span className="text-amber-600 dark:text-amber-400">
                    {' '}({new Date(simulation.capex_auto_corrected_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })})
                  </span>
                )}
              </p>
              <p className="text-xs mt-1 text-amber-700 dark:text-amber-300">
                {language === 'fr'
                  ? 'Une étude enregistrée avant une mise à jour du calcul (ex. Machine à riz / Inox Labo) a été recalculée et corrigée en base.'
                  : 'A study saved before a calculation update was recomputed and corrected.'}
              </p>
            </div>
          </div>
        )}

        {/* CAPEX Split Card - Visual breakdown of CCD vs CLIENT investment */}
        {simulation.input_data?.capex_investment && (
          <CapexSplitCard
            capexInvestment={simulation.input_data.capex_investment}
            totalCapex={simulation.total_capex}
            ccdCapex={simulation.ccd_capex}
            clientCapex={simulation.client_capex}
            language={language}
            enseigne={simulation.input_data?.general_info?.retailer_name || ''}
            simulationId={simulation.id}
          />
        )}

        {/* Les 3 Hypothèses - SECTION PRINCIPALE */}
        {simulation.hypothesis_1 && simulation.hypothesis_2 && simulation.hypothesis_3 && (
          <Card className="border-2 border-blue-200 bg-gradient-to-br from-white to-blue-50/30 dark:from-slate-900 dark:to-blue-900/10" data-testid="hypotheses-section">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <Calculator className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      {language === 'fr' ? 'Les 3 Hypothèses de CA' : 'The 3 Revenue Hypotheses'}
                    </CardTitle>
                    <CardDescription className="text-sm">
                      {language === 'fr' 
                        ? 'Sélectionnez les hypothèses à inclure dans le calcul de la moyenne'
                        : 'Select hypotheses to include in average calculation'}
                    </CardDescription>
                  </div>
                </div>
                {savingHypotheses && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{language === 'fr' ? 'Recalcul...' : 'Recalculating...'}</span>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* 3 Hypothesis Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <HypothesisCard
                  hypothesis={simulation.hypothesis_1}
                  isRecommended={simulation.recommended_hypothesis === 1}
                  number={1}
                  t={t}
                  isIncluded={includedHypotheses.h1}
                  onToggleInclude={() => toggleHypothesis('h1')}
                  recommendedCommissionRate={getDynamicCommissionRate()}
                  disabled={savingHypotheses}
                />
                <HypothesisCard
                  hypothesis={simulation.hypothesis_2}
                  isRecommended={simulation.recommended_hypothesis === 2}
                  number={2}
                  t={t}
                  isIncluded={includedHypotheses.h2}
                  onToggleInclude={() => toggleHypothesis('h2')}
                  recommendedCommissionRate={getDynamicCommissionRate()}
                  disabled={savingHypotheses}
                />
                <HypothesisCard
                  hypothesis={simulation.hypothesis_3}
                  isRecommended={simulation.recommended_hypothesis === 3}
                  number={3}
                  t={t}
                  isIncluded={includedHypotheses.h3}
                  onToggleInclude={() => toggleHypothesis('h3')}
                  recommendedCommissionRate={getDynamicCommissionRate()}
                  disabled={savingHypotheses}
                />
              </div>

              {/* Commission Rate Table - Collapsible */}
              {simulation.input_data?.capex_investment && (() => {
                const capex = simulation.input_data.capex_investment;
                const enseigne = simulation.input_data?.general_info?.retailer_name || '';
                const isSystemeUEnseigne = isSystemeU(enseigne);
                
                // For Système U, CCD always carries CAPEX
                const clientCarriesAll = isSystemeUEnseigne ? false : isClientCapexCarrier(capex);
                const capexCarrier = isSystemeUEnseigne ? 'CCD' : (clientCarriesAll ? 'CLIENT' : 'CCD');
                
                const brackets = getCommissionBrackets(enseigne);
                const avgCaHt = simulation.average_hypothesis?.ca_ht_annual || 0;
                const currentBracketIndex = brackets.findIndex(b => avgCaHt <= b.max);
                const caBracket = getCaBracket(avgCaHt, enseigne);
                
                return (
                  <details className="group bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
                    <summary className="flex items-center justify-between cursor-pointer select-none">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {language === 'fr' ? 'Barème des Commissions' : 'Commission Rate Table'}
                        </span>
                        {isSystemeUEnseigne && (
                          <Badge className="text-xs bg-orange-500 text-white">
                            SYSTÈME U
                          </Badge>
                        )}
                        <Badge className={`text-xs ${capexCarrier === 'CLIENT' ? 'bg-emerald-500' : 'bg-violet-500'} text-white`}>
                          {language === 'fr' ? 'Porteur CAPEX:' : 'CAPEX Carrier:'} {capexCarrier}
                        </Badge>
                      </div>
                      <ChevronDown className="w-4 h-4 text-slate-500 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="mt-3 overflow-x-auto">
                      {isSystemeUEnseigne ? (
                        /* Système U specific table - single column */
                        <Table>
                          <TableHeader>
                            <TableRow className="text-xs bg-orange-100 dark:bg-orange-900/30">
                              <TableHead className="py-2 font-semibold">{language === 'fr' ? 'CA HT encaissé sur Année N' : 'Revenue Bracket Year N'}</TableHead>
                              <TableHead className="text-center py-2 font-semibold text-orange-700 dark:text-orange-400">
                                {language === 'fr' ? 'Commission sur CA HT' : 'Commission Rate'}
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {brackets.map((row, idx) => {
                              const isCurrentBracket = idx === currentBracketIndex;
                              return (
                                <TableRow 
                                  key={idx} 
                                  className={`text-xs ${isCurrentBracket ? 'bg-orange-100 dark:bg-orange-900/40 font-semibold' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                >
                                  <TableCell className="py-2">
                                    {row.range}
                                    {isCurrentBracket && (
                                      <span className="ml-2 text-[10px] text-orange-600 dark:text-orange-400">
                                        ← {language === 'fr' ? 'actuel' : 'current'}
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell className={`text-center py-2 ${isCurrentBracket ? 'text-orange-600 dark:text-orange-400 font-bold text-base' : ''}`}>
                                    {(row.rate * 100).toFixed(0)}%
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      ) : (
                        /* Standard table - two columns (CCD and CLIENT) */
                        <Table>
                          <TableHeader>
                            <TableRow className="text-xs bg-slate-100 dark:bg-slate-700">
                              <TableHead className="py-2 font-semibold">{language === 'fr' ? 'Tranche CA HT Année N' : 'Revenue Bracket'}</TableHead>
                              <TableHead className="text-center py-2 font-semibold">
                                <span className={!clientCarriesAll ? 'text-violet-600 dark:text-violet-400' : ''}>CCD</span>
                              </TableHead>
                              <TableHead className="text-center py-2 font-semibold">
                                <span className={clientCarriesAll ? 'text-emerald-600 dark:text-emerald-400' : ''}>CLIENT</span>
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {brackets.map((row, idx) => {
                              const isCurrentBracket = idx === currentBracketIndex;
                              return (
                                <TableRow 
                                  key={idx} 
                                  className={`text-xs ${isCurrentBracket ? 'bg-blue-100 dark:bg-blue-900/40 font-semibold' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                >
                                  <TableCell className="py-2">
                                    {row.range}
                                    {isCurrentBracket && (
                                      <span className="ml-2 text-[10px] text-blue-600 dark:text-blue-400">
                                        ← {language === 'fr' ? 'actuel' : 'current'}
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell className={`text-center py-2 ${!clientCarriesAll && isCurrentBracket ? 'text-violet-600 dark:text-violet-400 font-bold text-base' : ''}`}>
                                    {(row.ccd * 100).toFixed(0)}%
                                  </TableCell>
                                  <TableCell className={`text-center py-2 ${clientCarriesAll && isCurrentBracket ? 'text-emerald-600 dark:text-emerald-400 font-bold text-base' : ''}`}>
                                    {(row.client * 100).toFixed(0)}%
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                      <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 text-center">
                        {language === 'fr' 
                          ? `Tranche actuelle: ${caBracket} → Taux recommandé: ${formatPercent(getDynamicCommissionRate())}`
                          : `Current bracket: ${caBracket} → Recommended rate: ${formatPercent(getDynamicCommissionRate())}`
                        }
                      </p>
                    </div>
                  </details>
                );
              })()}

              {/* Average Calculation Result with Dynamic Commission Rate */}
              <div className="p-4 bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-xl text-white shadow-lg">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-6 h-6 text-white" />
                    <div>
                      <p className="text-sm text-emerald-100">
                        {language === 'fr' ? 'Moyenne des hypothèses sélectionnées' : 'Average of selected hypotheses'}
                      </p>
                      <p className="text-xs text-emerald-200">
                        ({[includedHypotheses.h1 && 'H1', includedHypotheses.h2 && 'H2', includedHypotheses.h3 && 'H3'].filter(Boolean).join(' + ')})
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    {/* CA HT */}
                    <div className="text-right">
                      <p className="text-2xl font-mono font-bold text-white">
                        {formatCurrency(calculateAverage().ca_ht_annual)}
                      </p>
                      <p className="text-xs text-emerald-100">
                        {language === 'fr' ? 'CA HT Annuel' : 'Annual Revenue'}
                      </p>
                    </div>
                    {/* Commission Rate - Dynamic */}
                    <div className="text-right border-l border-emerald-400 pl-6">
                      <p className="text-2xl font-mono font-bold text-white">
                        {formatPercent(getDynamicCommissionRate())}
                      </p>
                      <p className="text-xs text-emerald-100">
                        {language === 'fr' ? 'Taux Recommandé' : 'Recommended Rate'}
                      </p>
                    </div>
                    {/* Red Months */}
                    <div className="text-right border-l border-emerald-400 pl-6">
                      <p className={`text-2xl font-mono font-bold ${
                        (simulation.red_months_count || 0) <= 3 ? 'text-white' :
                        (simulation.red_months_count || 0) <= 5 ? 'text-amber-200' : 'text-red-200'
                      }`}>
                        {simulation.red_months_count || 0}
                      </p>
                      <p className="text-xs text-emerald-100">
                        {language === 'fr' ? 'Mois en Rouge' : 'Red Months'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Monthly Breakdown Y1 - Red Months Indicator */}
        {simulation.monthly_breakdown_y1 && (() => {
          // Select monthly data and red months count based on analysisSource
          let monthlyData;
          let redMonths;
          let currentCluster;
          let currentFoodCostRate;
          let currentOtherCostRate;
          
          if (previewData?.monthly_breakdown_y1) {
            // Preview data has priority for visual display (monthly breakdown with correct rates)
            monthlyData = previewData.monthly_breakdown_y1;
            currentCluster = previewData.cluster_used;
            currentFoodCostRate = previewData.food_cost_rate_used;
            currentOtherCostRate = previewData.other_cost_rate_used;
            
            // But use STORED red months count for consistency with workflow status
            if (analysisSource === 'direction') {
              redMonths = simulation.direction_red_months_count ?? previewData.monthly_breakdown_y1.filter(m => m.is_negative === true).length;
            } else if (analysisSource === 'commercial') {
              redMonths = simulation.commercial_red_months_count ?? previewData.monthly_breakdown_y1.filter(m => m.is_negative === true).length;
            } else {
              redMonths = previewData.monthly_breakdown_y1.filter(m => m.is_negative === true).length;
            }
          } else if (analysisSource === 'direction') {
            // Direction tab: use direction-specific data if available
            monthlyData = simulation.direction_monthly_breakdown_y1 || simulation.monthly_breakdown_y1;
            redMonths = simulation.direction_red_months_count ?? simulation.red_months_count ?? 0;
            currentCluster = simulation.direction_cluster_used || simulation.cluster_used;
            currentFoodCostRate = simulation.direction_food_cost_rate ?? simulation.food_cost_rate_used;
            currentOtherCostRate = simulation.direction_other_cost_rate ?? simulation.other_cost_rate_used;
          } else if (analysisSource === 'commercial') {
            // Commercial tab: use commercial-specific data if available
            monthlyData = simulation.commercial_monthly_breakdown_y1 || simulation.monthly_breakdown_y1;
            redMonths = simulation.commercial_red_months_count ?? simulation.red_months_count ?? 0;
            currentCluster = simulation.commercial_cluster_used || simulation.cluster_used;
            currentFoodCostRate = simulation.commercial_food_cost_rate ?? simulation.food_cost_rate_used;
            currentOtherCostRate = simulation.commercial_other_cost_rate ?? simulation.other_cost_rate_used;
          } else {
            // Tool tab: use original tool data (system_red_months_count)
            monthlyData = simulation.monthly_breakdown_y1;
            redMonths = simulation.system_red_months_count ?? simulation.red_months_count ?? 0;
            currentCluster = simulation.cluster_used;
            currentFoodCostRate = simulation.food_cost_rate_used;
            currentOtherCostRate = simulation.other_cost_rate_used;
          }
          
          const currentVerdict = previewData?.verdict || simulation.verdict;
          
          // Logique de validation basée sur le fichier Excel:
          // 0-3 mois rouge = GO (vert)
          // 4-5 mois rouge = À L'ÉTUDE (orange)  
          // 6+ mois rouge = NO GO (rouge)
          const getStatus = () => {
            if (redMonths <= 3) return { status: 'go', label: language === 'fr' ? 'GO' : 'GO', color: 'emerald', bgClass: 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' };
            if (redMonths <= 5) return { status: 'review', label: language === 'fr' ? 'À L\'ÉTUDE' : 'UNDER REVIEW', color: 'amber', bgClass: 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' };
            return { status: 'nogo', label: language === 'fr' ? 'NO GO' : 'NO GO', color: 'red', bgClass: 'border-red-400 bg-red-50 dark:bg-red-900/20' };
          };
          const statusInfo = getStatus();
          
          return (
          <Card className={`border-2 ${statusInfo.bgClass}`} data-testid="monthly-breakdown-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className={`w-5 h-5 text-${statusInfo.color}-600`} />
                  <CardTitle className="text-lg">
                    {language === 'fr' ? 'Analyse Mensuelle Y1' : 'Monthly Breakdown Y1'}
                    {previewData && (
                      <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">
                        {language === 'fr' ? 'Prévisualisation' : 'Preview'}
                      </Badge>
                    )}
                    {(() => {
                      const stage = (!!simulation.admin_counter_proposal || ['counter_proposal', 'under_review', 'approved', 'rejected', 'pending_review', 'go'].includes(simulation.admin_status))
                        ? 'direction'
                        : simulation.validated ? 'commercial' : 'tool';
                      const cfg = {
                        tool: { cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', fr: 'Outil', en: 'Tool' },
                        commercial: { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', fr: 'Commercial', en: 'Commercial' },
                        direction: { cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', fr: 'Direction', en: 'Management' },
                      }[stage];
                      return (
                        <Badge className={`ml-2 text-xs ${cfg.cls}`} data-testid="monthly-analysis-stage-badge">
                          {language === 'fr' ? `Étape en cours : ${cfg.fr}` : `Current stage: ${cfg.en}`}
                        </Badge>
                      );
                    })()}
                  </CardTitle>
                </div>
                <RenewalCornerBox generalInfo={simulation.input_data?.general_info} />
                <Badge className={`text-lg px-4 py-1 ${
                  statusInfo.status === 'go' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-400' :
                  statusInfo.status === 'review' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-400' :
                  'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
                }`}>
                  {statusInfo.status === 'go' && '✓ '}{statusInfo.status === 'nogo' && '✗ '}{statusInfo.label}
                </Badge>
              </div>
              <CardDescription>
                {language === 'fr' 
                  ? `Cash Estimate = EBITDA - CAPEX (CAPEX appliqué au mois 1 uniquement)`
                  : `Cash Estimate = EBITDA - CAPEX (CAPEX applied to Month 1 only)`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Red months counter */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <span className={`text-2xl font-bold text-${statusInfo.color}-600`}>
                    {redMonths}
                  </span>
                  <span className="text-slate-600 dark:text-slate-400">
                    {language === 'fr' ? 'mois en rouge sur 12' : 'red months out of 12'}
                  </span>
                </div>
                <Badge variant="outline" className={`border-${statusInfo.color}-400 text-${statusInfo.color}-600`}>
                  {redMonths <= 3 
                    ? (language === 'fr' ? '0-3 mois = GO' : '0-3 months = GO')
                    : redMonths <= 5
                    ? (language === 'fr' ? '4-5 mois = À l\'étude' : '4-5 months = Review')
                    : (language === 'fr' ? '> 6 mois = Refusé' : '> 6 months = Rejected')}
                </Badge>
                {currentCluster && (
                  <Badge variant="secondary">
                    {currentCluster}
                  </Badge>
                )}
              </div>
              
              {/* Source selector for analysis */}
              <div className="mb-4 p-4 bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-800 dark:to-blue-900/20 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex flex-wrap items-center gap-4 mb-3">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {language === 'fr' ? 'Source des données :' : 'Data source:'}
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      variant={analysisSource === 'tool' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleSourceChange('tool')}
                      disabled={loadingPreview}
                      className={`gap-1.5 ${analysisSource === 'tool' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                      data-testid="source-tool-btn"
                    >
                      <BarChart3 className="w-3.5 h-3.5" />
                      {language === 'fr' ? 'Outil' : 'Tool'}
                    </Button>
                    <Button
                      variant={analysisSource === 'commercial' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleSourceChange('commercial')}
                      disabled={loadingPreview || (!simulation.ca_previsionnel_ht_proposed && !simulation.client_commission_rate)}
                      className={`gap-1.5 ${analysisSource === 'commercial' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                      data-testid="source-commercial-btn"
                    >
                      <Send className="w-3.5 h-3.5" />
                      {language === 'fr' ? 'Commercial' : 'Commercial'}
                    </Button>
                    <Button
                      variant={analysisSource === 'direction' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleSourceChange('direction')}
                      disabled={loadingPreview || (!simulation.ca_previsionnel_ht_proposed && !simulation.commission_rate_proposed)}
                      className={`gap-1.5 ${analysisSource === 'direction' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                      data-testid="source-direction-btn"
                    >
                      <Shield className="w-3.5 h-3.5" />
                      {language === 'fr' ? 'Direction' : 'Management'}
                    </Button>
                    {loadingPreview && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                  </div>
                </div>
                
                {/* Display current source values */}
                <div className={`flex items-center gap-4 px-3 py-2 rounded-lg text-sm ${
                  analysisSource === 'tool' ? 'bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800' :
                  analysisSource === 'commercial' ? 'bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800' :
                  'bg-purple-100 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800'
                }`}>
                  <span className={`font-medium ${
                    analysisSource === 'tool' ? 'text-blue-700 dark:text-blue-300' :
                    analysisSource === 'commercial' ? 'text-emerald-700 dark:text-emerald-300' :
                    'text-purple-700 dark:text-purple-300'
                  }`}>
                    {analysisSource === 'tool' ? (language === 'fr' ? 'Données Outil' : 'Tool Data') :
                     analysisSource === 'commercial' ? (language === 'fr' ? 'Données Commercial' : 'Commercial Data') :
                     (language === 'fr' ? 'Données Direction' : 'Management Data')}:
                  </span>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5">
                      <span className="text-slate-500 dark:text-slate-400 text-xs">{language === 'fr' ? 'CA HT:' : 'Revenue:'}</span>
                      <span className="font-bold text-slate-900 dark:text-white">
                        {formatCurrency(
                          analysisSource === 'tool' 
                            ? (previewData?.ca_ht || simulation.average_hypothesis?.ca_ht_annual || calculateAverage().ca_ht_annual || 0)
                            : analysisSource === 'commercial'
                            ? (simulation.ca_previsionnel_ht_proposed || 0)
                            : (simulation.admin_counter_proposal?.ca_previsionnel_ht || simulation.ca_previsionnel_ht_proposed || 0)
                        )}
                      </span>
                    </span>
                    <span className="text-slate-300 dark:text-slate-600">|</span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-slate-500 dark:text-slate-400 text-xs">{language === 'fr' ? 'Commission:' : 'Commission:'}</span>
                      <span className="font-bold text-slate-900 dark:text-white">
                        {formatPercent(
                          analysisSource === 'tool' 
                            ? getDynamicCommissionRate()
                            : analysisSource === 'commercial'
                            ? (simulation.commission_rate_proposed || simulation.client_commission_rate || 0.18)
                            : (simulation.admin_counter_proposal?.commission_rate || simulation.commission_rate_proposed || 0.18)
                        )}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Edit form (only when editing) */}
              {showCommissionEdit && (
                <div className="flex flex-wrap items-center gap-3 w-full mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-indigo-900/20 rounded-lg border-2 border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-600">{language === 'fr' ? 'Commission:' : 'Commission:'}</Label>
                    <Select
                      value={customCommissionRate?.toString() || "0.18"}
                      onValueChange={(val) => setCustomCommissionRate(parseFloat(val))}
                    >
                      <SelectTrigger className="w-20 h-7 text-xs">
                        <SelectValue placeholder="%" />
                      </SelectTrigger>
                      <SelectContent>
                        {[12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25].map(pct => (
                          <SelectItem key={pct} value={(pct / 100).toString()}>{pct}%</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-600">{language === 'fr' ? 'CA HT:' : 'Revenue:'}</Label>
                    <Input
                      type="number"
                      value={customCA || ''}
                      onChange={(e) => setCustomCA(parseFloat(e.target.value) || 0)}
                      placeholder="500000"
                      className="w-28 h-7 text-xs"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={handleManualRecalculate}
                    disabled={recalculating}
                    className="gap-1 h-7 text-xs bg-blue-600 hover:bg-blue-700"
                  >
                    {recalculating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    {language === 'fr' ? 'Prévisualiser' : 'Preview'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() => setShowCommissionEdit(false)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
              
              {/* Save button - appears when preview data exists */}
              {previewData && !showCommissionEdit && (
                <div className="mb-4">
                  <Button
                    size="sm"
                    onClick={handleSaveCustomParameters}
                    disabled={recalculating}
                    className="gap-1 h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                    data-testid="save-custom-params-btn"
                  >
                    {recalculating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    {language === 'fr' ? 'Sauvegarder' : 'Save'}
                  </Button>
                </div>
              )}
              
              {/* Cluster rates info + Recalculate button */}
              {(currentFoodCostRate || monthlyData?.[0]?.food_cost_rate) && (
                <div className="flex items-center justify-between gap-4 text-sm text-slate-600 dark:text-slate-400 mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-6 flex-wrap">
                    <span>
                      <strong>Food Cost:</strong> {((currentFoodCostRate || monthlyData?.[0]?.food_cost_rate) * 100).toFixed(2).replace('.', ',')}%
                    </span>
                    <span>
                      <strong>Other Cost:</strong> {((currentOtherCostRate || monthlyData?.[0]?.other_cost_rate) * 100).toFixed(2).replace('.', ',')}%
                    </span>
                  </div>
                  {!previewData && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-xs border-purple-300 text-purple-600 hover:bg-purple-50"
                      disabled={recalculating}
                      data-testid="recalculate-single-btn"
                      onClick={async () => {
                        setRecalculating(true);
                        try {
                          const response = await api().post(`/simulations/${id}/recalculate`);
                          const data = response.data;
                          setSimulation(prev => ({
                            ...prev,
                            cluster_used: data.cluster_used,
                            food_cost_rate_used: data.food_cost_rate_used,
                            other_cost_rate_used: data.other_cost_rate_used,
                            commission_rate_used: data.commission_rate_used,
                            monthly_breakdown_y1: data.monthly_breakdown_y1,
                            red_months_count: data.red_months_count,
                            verdict: data.verdict,
                            verdict_status: data.verdict_status,
                            pl_projections: data.pl_projections,
                          }));
                          toast.success(language === 'fr' ? `Recalculé avec ${data.cluster_used}` : `Recalculated with ${data.cluster_used}`);
                        } catch (error) {
                          const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors du recalcul' : 'Recalculation failed');
                          toast.error(msg);
                        } finally {
                          setRecalculating(false);
                        }
                      }}
                    >
                      {recalculating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      {language === 'fr' ? 'Recalculer' : 'Recalculate'}
                    </Button>
                  )}
                </div>
              )}
              
              {/* Monthly visualization */}
              <div className="grid grid-cols-12 gap-1 mb-4">
                {monthlyData.map((month) => (
                  <div
                    key={month.month}
                    className={`relative p-2 rounded text-center text-xs font-medium transition-all ${
                      month.is_negative 
                        ? 'bg-red-500 text-white' 
                        : 'bg-emerald-500 text-white'
                    }`}
                    title={`M${month.month}: Cash=${formatCurrency(month.cash_estimate)} | CM=${formatCurrency(month.contribution_margin)}`}
                  >
                    <div className="font-bold">M{month.month}</div>
                    <div className="text-[9px] opacity-90 mt-0.5">
                      {month.contribution_margin >= 0 ? '+' : ''}{Math.round(month.contribution_margin / 1000)}k€
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Monthly details table - HORIZONTAL layout like Excel */}
              <details className="cursor-pointer">
                <summary className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white">
                  {language === 'fr' ? 'Voir le détail mensuel' : 'View monthly details'}
                </summary>
                <div className="mt-4 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="sticky left-0 bg-white dark:bg-slate-800 z-10 min-w-[140px]">
                          {language === 'fr' ? 'Indicateur' : 'Indicator'}
                        </TableHead>
                        {monthlyData.map((month) => (
                          <TableHead 
                            key={month.month} 
                            className={`text-center min-w-[90px] ${month.month === 1 ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
                          >
                            M{month.month}
                          </TableHead>
                        ))}
                        <TableHead className="text-center min-w-[100px] bg-amber-100 dark:bg-amber-900/30 font-bold">
                          FY
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Gross Sales TTC Row (VAT Included = 110%) - FY = AVERAGE */}
                      <TableRow className="bg-blue-50/50 dark:bg-blue-900/10">
                        <TableCell className="sticky left-0 bg-blue-50/50 dark:bg-blue-900/20 font-medium text-blue-700">
                          Gross Sales TTC
                        </TableCell>
                        {monthlyData.map((month) => (
                          <TableCell key={month.month} className={`text-center font-mono text-xs ${month.month === 1 ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                            <div className="text-blue-700">{formatCurrency(month.gross_sales_ht * 1.10)}</div>
                            <div className="text-blue-400 text-[10px]">110%</div>
                          </TableCell>
                        ))}
                        {/* FY Column - Average for TTC */}
                        <TableCell className="text-center font-mono text-xs bg-amber-100 dark:bg-amber-900/30">
                          <div className="text-blue-700 font-bold">
                            {formatCurrency(monthlyData.reduce((sum, m) => sum + m.gross_sales_ht * 1.10, 0) / 12)}
                          </div>
                          <div className="text-blue-400 text-[10px]">110%</div>
                        </TableCell>
                      </TableRow>

                      {/* Gross Sales HT Row (VAT Excluded = 100%) - FY = CUMUL */}
                      <TableRow className="bg-yellow-50/80 dark:bg-yellow-900/10">
                        <TableCell className="sticky left-0 bg-yellow-50/80 dark:bg-yellow-900/20 font-medium">
                          Gross Sales HT
                        </TableCell>
                        {monthlyData.map((month) => (
                          <TableCell key={month.month} className={`text-center font-mono text-xs ${month.month === 1 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-yellow-50/50 dark:bg-yellow-900/10'}`}>
                            <div>{formatCurrency(month.gross_sales_ht)}</div>
                            <div className="text-slate-400 text-[10px]">100%</div>
                          </TableCell>
                        ))}
                        {/* FY Column - Cumul */}
                        <TableCell className="text-center font-mono text-xs bg-amber-100 dark:bg-amber-900/30 font-bold">
                          <div>{formatCurrency(monthlyData.reduce((sum, m) => sum + m.gross_sales_ht, 0))}</div>
                          <div className="text-slate-400 text-[10px]">100%</div>
                        </TableCell>
                      </TableRow>
                      
                      {/* Commission Row - FY = CUMUL */}
                      <TableRow>
                        <TableCell className="sticky left-0 bg-white dark:bg-slate-800 font-medium text-red-600">
                          Commission
                        </TableCell>
                        {monthlyData.map((month) => {
                          const pct = month.gross_sales_ht !== 0 ? ((month.sales_commissions / month.gross_sales_ht) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell key={month.month} className={`text-center font-mono text-xs text-red-600 ${month.month === 1 ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                              <div>{formatCurrency(month.sales_commissions)}</div>
                              <div className="text-red-400 text-[10px]">{pct}%</div>
                            </TableCell>
                          );
                        })}
                        {/* FY Column - Cumul */}
                        {(() => {
                          const fyComm = monthlyData.reduce((sum, m) => sum + m.sales_commissions, 0);
                          const fyHT = monthlyData.reduce((sum, m) => sum + m.gross_sales_ht, 0);
                          const fyPct = fyHT !== 0 ? ((fyComm / fyHT) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell className="text-center font-mono text-xs text-red-600 bg-amber-100 dark:bg-amber-900/30 font-bold">
                              <div>{formatCurrency(fyComm)}</div>
                              <div className="text-red-400 text-[10px]">{fyPct}%</div>
                            </TableCell>
                          );
                        })()}
                      </TableRow>
                      
                      {/* Food Cost Row - FY = CUMUL */}
                      <TableRow>
                        <TableCell className="sticky left-0 bg-white dark:bg-slate-800 font-medium">
                          Food Cost
                        </TableCell>
                        {monthlyData.map((month) => {
                          const pct = month.gross_sales_ht !== 0 ? ((month.food_costs / month.gross_sales_ht) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell key={month.month} className={`text-center font-mono text-xs ${month.month === 1 ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                              <div>{formatCurrency(month.food_costs)}</div>
                              <div className="text-slate-400 text-[10px]">{pct}%</div>
                            </TableCell>
                          );
                        })}
                        {/* FY Column */}
                        {(() => {
                          const fyVal = monthlyData.reduce((sum, m) => sum + m.food_costs, 0);
                          const fyHT = monthlyData.reduce((sum, m) => sum + m.gross_sales_ht, 0);
                          const fyPct = fyHT !== 0 ? ((fyVal / fyHT) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell className="text-center font-mono text-xs bg-amber-100 dark:bg-amber-900/30 font-bold">
                              <div>{formatCurrency(fyVal)}</div>
                              <div className="text-slate-400 text-[10px]">{fyPct}%</div>
                            </TableCell>
                          );
                        })()}
                      </TableRow>
                      
                      {/* Direct Labor Row - FY = CUMUL */}
                      <TableRow>
                        <TableCell className="sticky left-0 bg-white dark:bg-slate-800 font-medium text-red-600">
                          Direct Labor
                        </TableCell>
                        {monthlyData.map((month) => {
                          const pct = month.gross_sales_ht !== 0 ? ((month.direct_labor_costs / month.gross_sales_ht) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell key={month.month} className={`text-center font-mono text-xs text-red-600 ${month.month === 1 ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                              <div>{formatCurrency(month.direct_labor_costs)}</div>
                              <div className="text-red-400 text-[10px]">{pct}%</div>
                            </TableCell>
                          );
                        })}
                        {/* FY Column */}
                        {(() => {
                          const fyVal = monthlyData.reduce((sum, m) => sum + m.direct_labor_costs, 0);
                          const fyHT = monthlyData.reduce((sum, m) => sum + m.gross_sales_ht, 0);
                          const fyPct = fyHT !== 0 ? ((fyVal / fyHT) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell className="text-center font-mono text-xs text-red-600 bg-amber-100 dark:bg-amber-900/30 font-bold">
                              <div>{formatCurrency(fyVal)}</div>
                              <div className="text-red-400 text-[10px]">{fyPct}%</div>
                            </TableCell>
                          );
                        })()}
                      </TableRow>
                      
                      {/* Other Cost Row - FY = CUMUL */}
                      <TableRow>
                        <TableCell className="sticky left-0 bg-white dark:bg-slate-800 font-medium">
                          Other Cost
                        </TableCell>
                        {monthlyData.map((month) => {
                          const pct = month.gross_sales_ht !== 0 ? ((month.other_costs / month.gross_sales_ht) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell key={month.month} className={`text-center font-mono text-xs ${month.month === 1 ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                              <div>{formatCurrency(month.other_costs)}</div>
                              <div className="text-slate-400 text-[10px]">{pct}%</div>
                            </TableCell>
                          );
                        })}
                        {/* FY Column */}
                        {(() => {
                          const fyVal = monthlyData.reduce((sum, m) => sum + m.other_costs, 0);
                          const fyHT = monthlyData.reduce((sum, m) => sum + m.gross_sales_ht, 0);
                          const fyPct = fyHT !== 0 ? ((fyVal / fyHT) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell className="text-center font-mono text-xs bg-amber-100 dark:bg-amber-900/30 font-bold">
                              <div>{formatCurrency(fyVal)}</div>
                              <div className="text-slate-400 text-[10px]">{fyPct}%</div>
                            </TableCell>
                          );
                        })()}
                      </TableRow>
                      
                      {/* Contribution Margin Row - Highlighted - FY = CUMUL */}
                      <TableRow className="bg-emerald-50 dark:bg-emerald-900/20 border-t-2 border-emerald-200">
                        <TableCell className="sticky left-0 bg-emerald-50 dark:bg-emerald-900/30 font-bold text-emerald-700">
                          Contrib. Margin
                        </TableCell>
                        {monthlyData.map((month) => {
                          const pct = month.gross_sales_ht !== 0 ? ((month.contribution_margin / month.gross_sales_ht) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell key={month.month} className={`text-center font-mono text-xs font-bold ${month.month === 1 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-emerald-50 dark:bg-emerald-900/20'}`}>
                              <div className="text-emerald-700">{formatCurrency(month.contribution_margin)}</div>
                              <div className="text-emerald-500 text-[10px]">{pct}%</div>
                            </TableCell>
                          );
                        })}
                        {/* FY Column */}
                        {(() => {
                          const fyVal = monthlyData.reduce((sum, m) => sum + m.contribution_margin, 0);
                          const fyHT = monthlyData.reduce((sum, m) => sum + m.gross_sales_ht, 0);
                          const fyPct = fyHT !== 0 ? ((fyVal / fyHT) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell className="text-center font-mono text-xs font-bold bg-amber-100 dark:bg-amber-900/30">
                              <div className="text-emerald-700">{formatCurrency(fyVal)}</div>
                              <div className="text-emerald-500 text-[10px]">{fyPct}%</div>
                            </TableCell>
                          );
                        })()}
                      </TableRow>
                      
                      {/* SGA Row - FY = CUMUL */}
                      <TableRow>
                        <TableCell className="sticky left-0 bg-white dark:bg-slate-800 font-medium text-red-600">
                          SGA
                        </TableCell>
                        {monthlyData.map((month) => {
                          const pct = month.gross_sales_ht !== 0 ? (((month.sga_cost || 0) / month.gross_sales_ht) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell key={month.month} className={`text-center font-mono text-xs text-red-600 ${month.month === 1 ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                              <div>{formatCurrency(month.sga_cost || 0)}</div>
                              <div className="text-red-400 text-[10px]">{pct}%</div>
                            </TableCell>
                          );
                        })}
                        {/* FY Column */}
                        {(() => {
                          const fyVal = monthlyData.reduce((sum, m) => sum + (m.sga_cost || 0), 0);
                          const fyHT = monthlyData.reduce((sum, m) => sum + m.gross_sales_ht, 0);
                          const fyPct = fyHT !== 0 ? ((fyVal / fyHT) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell className="text-center font-mono text-xs text-red-600 bg-amber-100 dark:bg-amber-900/30 font-bold">
                              <div>{formatCurrency(fyVal)}</div>
                              <div className="text-red-400 text-[10px]">{fyPct}%</div>
                            </TableCell>
                          );
                        })()}
                      </TableRow>
                      
                      {/* EBITDA Row - Highlighted - FY = CUMUL */}
                      <TableRow className="bg-blue-50 dark:bg-blue-900/20">
                        <TableCell className="sticky left-0 bg-blue-50 dark:bg-blue-900/30 font-bold text-blue-700">
                          EBITDA
                        </TableCell>
                        {monthlyData.map((month) => {
                          const pct = month.gross_sales_ht !== 0 ? (((month.ebitda || 0) / month.gross_sales_ht) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell key={month.month} className={`text-center font-mono text-xs font-bold ${month.month === 1 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
                              <div className="text-blue-700">{formatCurrency(month.ebitda || 0)}</div>
                              <div className="text-blue-500 text-[10px]">{pct}%</div>
                            </TableCell>
                          );
                        })}
                        {/* FY Column */}
                        {(() => {
                          const fyVal = monthlyData.reduce((sum, m) => sum + (m.ebitda || 0), 0);
                          const fyHT = monthlyData.reduce((sum, m) => sum + m.gross_sales_ht, 0);
                          const fyPct = fyHT !== 0 ? ((fyVal / fyHT) * 100).toFixed(2).replace('.', ',') : '0,00';
                          return (
                            <TableCell className="text-center font-mono text-xs font-bold bg-amber-100 dark:bg-amber-900/30">
                              <div className="text-blue-700">{formatCurrency(fyVal)}</div>
                              <div className="text-blue-500 text-[10px]">{fyPct}%</div>
                            </TableCell>
                          );
                        })()}
                      </TableRow>
                      
                      {/* CAPEX Row - FY = CUMUL */}
                      <TableRow>
                        <TableCell className="sticky left-0 bg-white dark:bg-slate-800 font-medium text-orange-600">
                          CAPEX
                        </TableCell>
                        {monthlyData.map((month) => (
                          <TableCell key={month.month} className={`text-center font-mono text-xs text-orange-600 ${month.month === 1 ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                            {month.capex_applied !== 0 ? formatCurrency(month.capex_applied) : '-'}
                          </TableCell>
                        ))}
                        {/* FY Column */}
                        <TableCell className="text-center font-mono text-xs text-orange-600 bg-amber-100 dark:bg-amber-900/30 font-bold">
                          {formatCurrency(monthlyData.reduce((sum, m) => sum + (m.capex_applied || 0), 0))}
                        </TableCell>
                      </TableRow>
                      
                      {/* Cash Estimate Row - Main highlight - FY = CUMUL (Final Cash) */}
                      <TableRow className="bg-slate-100 dark:bg-slate-700 border-t-2 border-slate-300">
                        <TableCell className="sticky left-0 bg-slate-100 dark:bg-slate-700 font-bold">
                          Cash Estimate
                        </TableCell>
                        {monthlyData.map((month) => (
                          <TableCell 
                            key={month.month} 
                            className={`text-center font-mono text-sm font-bold ${
                              month.cash_estimate < 0 
                                ? 'text-red-600 bg-red-100 dark:bg-red-900/30' 
                                : 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30'
                            }`}
                          >
                            {formatCurrency(month.cash_estimate)}
                          </TableCell>
                        ))}
                        {/* FY Column - Final Cash (last month's cash_estimate) */}
                        {(() => {
                          const finalCash = monthlyData[monthlyData.length - 1]?.cash_estimate || 0;
                          return (
                            <TableCell className={`text-center font-mono text-sm font-bold bg-amber-200 dark:bg-amber-800/50 ${
                              finalCash < 0 ? 'text-red-600' : 'text-emerald-700'
                            }`}>
                              {formatCurrency(finalCash)}
                            </TableCell>
                          );
                        })()}
                      </TableRow>
                      
                      {/* Payback en mois Row - Shows when cumulative EBITDA >= CCD CAPEX */}
                      <TableRow className="bg-purple-50 dark:bg-purple-900/20 border-t-2 border-purple-200">
                        <TableCell className="sticky left-0 bg-purple-50 dark:bg-purple-900/20 font-bold text-purple-700 dark:text-purple-300">
                          {language === 'fr' ? 'Payback en mois' : 'Payback (months)'}
                        </TableCell>
                        {(() => {
                          // Recompute payback DYNAMICALLY from the monthlyData of the current tab
                          // (Outil / Commercial / Direction) for full consistency with the
                          // Cash Estimate row above. The stored simulation.payback_months only
                          // reflects the Outil view and would be inconsistent on other tabs.
                          const capex = simulation.ccd_capex || simulation.total_capex || 0;
                          let dynamicPayback = 0;
                          if (capex > 0 && Array.isArray(monthlyData) && monthlyData.length > 0) {
                            let cumul = 0;
                            for (let i = 0; i < monthlyData.length; i++) {
                              const prev = cumul;
                              const me = monthlyData[i]?.ebitda || 0;
                              cumul += me;
                              if (cumul >= capex && me > 0) {
                                dynamicPayback = Math.round((i + (capex - prev) / me) * 10) / 10;
                                break;
                              }
                            }
                            // If not paid back in Y1, estimate from annual EBITDA
                            if (dynamicPayback === 0) {
                              const annualEbitda = monthlyData.reduce((s, m) => s + (m?.ebitda || 0), 0);
                              if (annualEbitda > 0) {
                                dynamicPayback = Math.round((capex / annualEbitda) * 120) / 10;
                              }
                            }
                          }
                          
                          // Determine which month the payback occurs (rounded up, max 12 for Y1)
                          const paybackMonth = dynamicPayback > 0 && dynamicPayback <= 12 
                            ? Math.ceil(dynamicPayback) 
                            : null;
                          
                          return monthlyData.map((month, idx) => {
                            const isPaybackMonth = paybackMonth !== null && idx + 1 === paybackMonth;
                            const isBeforePayback = paybackMonth !== null && idx + 1 < paybackMonth;
                            const isAfterPayback = paybackMonth !== null && idx + 1 > paybackMonth;
                            
                            return (
                              <TableCell 
                                key={month.month} 
                                className={`text-center font-mono text-sm ${
                                  isPaybackMonth 
                                    ? 'font-bold text-purple-700 bg-purple-200 dark:bg-purple-800/50' 
                                    : isBeforePayback
                                    ? 'text-slate-400'
                                    : isAfterPayback
                                    ? 'text-emerald-500'
                                    : 'text-slate-400'
                                }`}
                              >
                                {isPaybackMonth ? `✓ ${dynamicPayback.toFixed(1)}` : '-'}
                              </TableCell>
                            );
                          });
                        })()}
                        {/* FY Column - Total Payback */}
                        <TableCell className="text-center font-mono text-sm font-bold bg-purple-200 dark:bg-purple-800/50 text-purple-700 dark:text-purple-300">
                          {(() => {
                            // Same dynamic recompute (FY label)
                            const capex = simulation.ccd_capex || simulation.total_capex || 0;
                            let paybackValue = 0;
                            if (capex > 0 && Array.isArray(monthlyData) && monthlyData.length > 0) {
                              let cumul = 0;
                              for (let i = 0; i < monthlyData.length; i++) {
                                const prev = cumul;
                                const me = monthlyData[i]?.ebitda || 0;
                                cumul += me;
                                if (cumul >= capex && me > 0) {
                                  paybackValue = Math.round((i + (capex - prev) / me) * 10) / 10;
                                  break;
                                }
                              }
                              if (paybackValue === 0) {
                                const annualEbitda = monthlyData.reduce((s, m) => s + (m?.ebitda || 0), 0);
                                if (annualEbitda > 0) {
                                  paybackValue = Math.round((capex / annualEbitda) * 120) / 10;
                                }
                              }
                            }
                            if (paybackValue <= 0) {
                              return language === 'fr' ? '> 1 an' : '> 1y';
                            }
                            if (paybackValue < 12) {
                              return `${paybackValue.toFixed(1)} ${language === 'fr' ? 'mois' : 'mo'}`;
                            } else {
                              const years = Math.floor(paybackValue / 12);
                              const months = (paybackValue % 12).toFixed(0);
                              return `${years}${language === 'fr' ? 'a' : 'y'} ${months}${language === 'fr' ? 'm' : 'm'}`;
                            }
                          })()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </details>
            </CardContent>
          </Card>
          );
        })()}

        {/* Validation Section - Proposition Commerciale Recommandée */}
        <Card 
          id="proposition-client-section"
          className={`border-2 transition-all shadow-lg ${
            isValidated 
              ? 'border-emerald-500 bg-emerald-50/30' 
              : 'border-orange-500 bg-orange-50/20 animate-pulse-border'
          }`} 
          data-testid="validation-card"
        >
          <CardContent className="py-4 space-y-3">
            {/* Header with emphasis */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                {isValidated ? (
                  <div className="p-2 rounded-lg bg-emerald-100">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                ) : (
                  <div className="p-2 rounded-lg bg-orange-100">
                    <Send className="w-5 h-5 text-orange-600" />
                  </div>
                )}
                <span className="text-base font-bold text-slate-800">
                  {language === 'fr' ? 'Proposition Commerciale Recommandée - Décision Commerciale / Décision Opérationnelle' : 'Recommended Commercial Proposal - Commercial Decision / Operational Decision'}
                </span>
                {!isValidated && (
                  <span className="text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded-full">
                    {language === 'fr' ? 'Action requise' : 'Action required'}
                  </span>
                )}
              </div>
              {isValidated && (
                <Badge className="text-xs bg-emerald-100 text-emerald-800">
                  {language === 'fr' ? '✓ Validé' : '✓ Validated'}
                </Badge>
              )}
            </div>
            
            {/* Form fields - compact grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="commission_rate" className="text-[10px]">
                  {language === 'fr' ? 'Pourcentage de commission proposé au client' : 'Commission percentage proposed to client'}
                </Label>
                <Select 
                  value={validationData.commission_rate.toString()} 
                  onValueChange={(v) => setValidationData(prev => ({...prev, commission_rate: parseFloat(v)}))}
                  disabled={isValidated}
                >
                  <SelectTrigger className="h-8 text-xs bg-white dark:bg-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25].map(pct => (
                      <SelectItem key={pct} value={(pct / 100).toString()}>{pct}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="ca_previsionnel" className="text-[10px]">
                  {language === 'fr' ? 'CA Prévisionnel HT proposé au client (€)' : 'Projected CA excl. tax (€)'}
                </Label>
                {simulation?.average_hypothesis?.ca_ht_annual > 0 && (
                  <p className="text-[10px] text-slate-500">
                    {language === 'fr' 
                      ? `Valeur par défaut : CA Potentiel Moyen HT = ${Math.round(simulation.average_hypothesis.ca_ht_annual).toLocaleString('fr-FR')} €`
                      : `Default: ${Math.round(simulation.average_hypothesis.ca_ht_annual).toLocaleString()} €`
                    }
                  </p>
                )}
                <Input
                  id="ca_previsionnel"
                  type="number"
                  inputMode="numeric"
                  placeholder={simulation?.average_hypothesis?.ca_ht_annual ? Math.round(simulation.average_hypothesis.ca_ht_annual).toString() : "500000"}
                  value={validationData.ca_previsionnel_ht}
                  onChange={(e) => setValidationData(prev => ({...prev, ca_previsionnel_ht: e.target.value}))}
                  disabled={isValidated}
                  className="h-8 text-xs bg-white dark:bg-slate-800"
                />
              </div>
            </div>
            
            {/* Commercial Comment - collapsible when not validated */}
            {!isValidated ? (
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                  + {language === 'fr' ? 'Ajouter un commentaire commercial' : 'Add commercial comment'}
                </summary>
                <Textarea
                  id="commercial_comment"
                  placeholder={language === 'fr' 
                    ? 'Informations complémentaires pour la direction...'
                    : 'Additional information for management...'}
                  value={validationData.commercial_comment}
                  onChange={(e) => setValidationData(prev => ({...prev, commercial_comment: e.target.value}))}
                  disabled={isValidated}
                  className="mt-1 text-xs bg-white dark:bg-slate-800 min-h-[60px]"
                  data-testid="commercial-comment-textarea"
                />
              </details>
            ) : simulation?.commercial_comment && (
              <div className="p-2 bg-slate-100 rounded text-xs">
                <span className="text-slate-500">{language === 'fr' ? 'Commentaire:' : 'Comment:'}</span> {simulation.commercial_comment}
              </div>
            )}
            
            {/* Validation button - Single orange button */}
            {!isValidated && (
              <div className="flex justify-center py-3">
                <Button 
                  onClick={handleValidation}
                  disabled={validating}
                  className="h-12 px-8 text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all"
                  data-testid="send-to-direction-btn"
                >
                  {validating ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {language === 'fr' 
                    ? 'ENREGISTRER ET ENVOYER A LA DIRECTION WASABI' 
                    : 'SAVE AND SEND TO WASABI MANAGEMENT'}
                </Button>
              </div>
            )}

            {/* Display user's submitted proposal if validated - compact */}
            {isValidated && simulation?.commission_rate_proposed && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
                <div className="flex flex-col gap-3">
                  {/* Proposal summary */}
                  <div className="flex items-center justify-center gap-6">
                    <div className="text-center">
                      <p className="text-[10px] text-blue-500">{language === 'fr' ? 'Commission' : 'Commission'}</p>
                      <p className="text-lg font-bold text-blue-700">
                        {((simulation.commission_rate_proposed || 0) * 100).toFixed(0)}%
                      </p>
                    </div>
                    <div className="h-8 w-px bg-blue-200"></div>
                    <div className="text-center">
                      <p className="text-[10px] text-blue-500">{language === 'fr' ? 'CA HT' : 'Revenue'}</p>
                      <p className="text-lg font-bold text-blue-700">
                        {formatCurrency(simulation.ca_previsionnel_ht_proposed || 0)}
                      </p>
                    </div>
                    {simulation.commercial_comment && (
                      <>
                        <div className="h-8 w-px bg-blue-200"></div>
                        <div className="text-center max-w-[150px]">
                          <p className="text-[10px] text-blue-500 flex items-center gap-1 justify-center">
                            <MessageSquare className="w-2.5 h-2.5" />
                            {language === 'fr' ? 'Commentaire' : 'Comment'}
                          </p>
                          <p className="text-[10px] text-blue-600 italic truncate" title={simulation.commercial_comment}>
                            "{simulation.commercial_comment}"
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Action buttons - Modify and Resend */}
                  {simulation.admin_status !== 'approved' && simulation.admin_status !== 'rejected' && (
                    <div className="flex justify-center gap-3 pt-2 border-t border-blue-200">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsValidated(false)}
                        className="h-10 px-6 text-sm font-medium text-slate-600 border-slate-300 hover:bg-slate-100 rounded-lg"
                        data-testid="modify-proposal-btn"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'MODIFIER' : 'MODIFY'}
                      </Button>
                      <Button 
                        onClick={handleValidation}
                        disabled={validating}
                        className="h-10 px-6 text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all"
                        data-testid="resend-to-direction-btn"
                      >
                        {validating ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        {language === 'fr' 
                          ? 'RENVOYER A LA DIRECTION WASABI' 
                          : 'RESEND TO WASABI MANAGEMENT'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin Decision Panel - Only visible to admins when proposal is validated */}
        {canMakeDecision && isValidated && (
          <Card 
            id="admin-decision-section"
            className={`border ${
            simulation?.admin_status === 'approved' ? 'border-emerald-400 bg-emerald-50/30' :
            simulation?.admin_status === 'rejected' ? 'border-red-400 bg-red-50/30' :
            simulation?.admin_status === 'counter_proposal' ? 'border-amber-400 bg-amber-50/30' :
            'border-amber-400 bg-amber-50/30'
          }`} data-testid="admin-decision-panel">
            <CardContent className="py-3 space-y-2">
              {/* ========== HEADER COMPACT ========== */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-slate-700">
                    {language === 'fr' ? 'Décision Direction' : 'Management Decision'}
                  </span>
                </div>
                {simulation?.admin_status && (
                  <Badge className={`text-xs ${
                    simulation.admin_status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                    simulation.admin_status === 'rejected' ? 'bg-red-100 text-red-800' :
                    simulation.admin_status === 'counter_proposal' ? 'bg-amber-100 text-amber-800' :
                    'bg-slate-100 text-slate-800'
                  }`}>
                    {simulation.admin_status === 'approved' && (language === 'fr' ? '✓ GO' : '✓ GO')}
                    {simulation.admin_status === 'rejected' && (language === 'fr' ? '✗ NO GO' : '✗ NO GO')}
                    {simulation.admin_status === 'counter_proposal' && (language === 'fr' ? '📝 Contre-prop.' : '📝 Counter')}
                    {simulation.admin_status === 'pending_review' && (language === 'fr' ? '🔄 À l\'étude' : '🔄 Review')}
                  </Badge>
                )}
              </div>
              
              {/* ========== RAPPEL PROPOSITION COMMERCIALE (COMPACT) ========== */}
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-[10px] text-blue-500">{language === 'fr' ? 'Commission' : 'Commission'}</p>
                      <p className="text-base font-bold text-blue-700">
                        {((simulation.commission_rate_proposed || 0) * 100).toFixed(0)}%
                      </p>
                    </div>
                    <div className="h-6 w-px bg-blue-200"></div>
                    <div>
                      <p className="text-[10px] text-blue-500">{language === 'fr' ? 'CA HT' : 'Revenue'}</p>
                      <p className="text-base font-bold text-blue-700">
                        {formatCurrency(simulation.ca_previsionnel_ht_proposed || 0)}
                      </p>
                    </div>
                  </div>
                  {simulation.commercial_comment && (
                    <div className="text-right max-w-[150px]">
                      <p className="text-[10px] text-blue-500 flex items-center gap-1 justify-end">
                        <MessageSquare className="w-2.5 h-2.5" />
                        {language === 'fr' ? 'Commentaire' : 'Comment'}
                      </p>
                      <p className="text-[10px] text-blue-600 italic truncate" title={simulation.commercial_comment}>
                        "{simulation.commercial_comment}"
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Show counter-proposal details if exists (COMPACT) */}
              {simulation?.admin_counter_proposal && (
                <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold text-amber-700">📝 Contre-proposition</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-amber-700">
                        {((simulation.admin_counter_proposal.commission_rate || 0) * 100).toFixed(0)}%
                      </span>
                      <span className="text-xs font-bold text-amber-700">
                        {formatCurrency(simulation.admin_counter_proposal.ca_previsionnel_ht || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Decision form */}
              {!simulation?.admin_status && (
                <>
                  {/* ========== SIMULATION TEST PANEL (extracted component) ========== */}
                  <TestSimulationPanel
                    simulationId={id}
                    simulation={simulation}
                    language={language}
                  />
                </>
              )}
                  
                  {/* Counter-proposal form (COMPACT) - aussi disponible après une décision */}
                  {showCounterProposal && simulation?.admin_status && (
                    <TestSimulationPanel simulationId={id} simulation={simulation} language={language} />
                  )}
                  {showCounterProposal && (
                    <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 space-y-2" data-testid="counter-proposal-form">
                      <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400">
                        📝 {language === 'fr' ? 'Contre-proposition' : 'Counter-proposal'}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px]">{language === 'fr' ? 'Commission (%)' : 'Commission (%)'}</Label>
                          <Select 
                            value={adminDecision.counter_commission.toString()} 
                            onValueChange={(v) => setAdminDecision(prev => ({...prev, counter_commission: parseFloat(v)}))}
                          >
                            <SelectTrigger className="bg-white dark:bg-slate-800 h-8 text-xs" data-testid="counter-commission-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25].map(pct => (
                                <SelectItem key={pct} value={(pct / 100).toString()}>{pct}%</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px]">{language === 'fr' ? 'CA HT (€)' : 'Revenue (€)'}</Label>
                          <Input
                            type="number"
                            placeholder="500000"
                            value={adminDecision.counter_ca}
                            onChange={(e) => setAdminDecision(prev => ({...prev, counter_ca: e.target.value}))}
                            className="bg-white dark:bg-slate-800 h-8 text-xs"
                            data-testid="counter-ca-input"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={handleCounterProposal}
                          disabled={submittingDecision}
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 h-7 text-xs"
                          data-testid="counter-send-btn"
                        >
                          {submittingDecision ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                          {language === 'fr' ? 'Envoyer' : 'Send'}
                        </Button>
                        <Button 
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setShowCounterProposal(false)}
                        >
                          {language === 'fr' ? 'Annuler' : 'Cancel'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* ========== BOUTONS DE DÉCISION (COMPACT) ========== */}
                  {!simulation?.admin_status && !showCounterProposal && (
                    <div className="flex gap-1.5">
                      {/* GO Button */}
                      <Button 
                        onClick={() => handleAdminDecision('approved')}
                        disabled={submittingDecision}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
                        data-testid="admin-go-btn"
                      >
                        {submittingDecision ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ThumbsUp className="w-3 h-3 mr-1" />}
                        <span className="font-bold">GO</span>
                        <span className="text-[10px] ml-1 opacity-80 hidden sm:inline">
                          ({((simulation.commission_rate_proposed || 0) * 100).toFixed(0)}%)
                        </span>
                      </Button>

                      {/* Contre-proposition Button */}
                      <Button 
                        onClick={() => setShowCounterProposal(true)}
                        disabled={submittingDecision}
                        variant="outline"
                        className="flex-1 h-8 text-xs border-amber-400 text-amber-700 hover:bg-amber-50"
                        data-testid="admin-counter-btn"
                      >
                        <MessageSquare className="w-3 h-3 mr-1" />
                        <span className="font-bold">{language === 'fr' ? 'Contre-prop.' : 'Counter'}</span>
                      </Button>

                      {/* NO GO Button */}
                      <Button 
                        onClick={() => handleAdminDecision('rejected')}
                        disabled={submittingDecision}
                        variant="destructive"
                        className="flex-1 h-8 text-xs"
                        data-testid="admin-nogo-btn"
                      >
                        {submittingDecision ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ThumbsDown className="w-3 h-3 mr-1" />}
                        <span className="font-bold">NO GO</span>
                      </Button>
                    </div>
                  )}

                  {/* Commentaire (collapsible) */}
                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                      + {language === 'fr' ? (simulation?.admin_status ? 'Ajouter un commentaire (joint au mail de décision modifiée)' : 'Ajouter un commentaire') : 'Add comment'}
                    </summary>
                    <Textarea
                      placeholder={language === 'fr' ? 'Commentaire optionnel...' : 'Optional comment...'}
                      value={adminDecision.comment}
                      onChange={(e) => setAdminDecision(prev => ({...prev, comment: e.target.value}))}
                      className="bg-white dark:bg-slate-800 mt-1 text-xs"
                      rows={2}
                      data-testid="admin-decision-comment"
                    />
                  </details>

              {/* Show admin comment if decision made */}
              {simulation?.admin_comment && simulation?.admin_status && (
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    <strong>{language === 'fr' ? 'Commentaire:' : 'Comment:'}</strong> {simulation.admin_comment}
                  </p>
                </div>
              )}

              {/* Buttons to CHANGE decision - visible when a decision has been made */}
              {simulation?.admin_status && (
                <div className="pt-2 border-t border-slate-200 dark:border-slate-700 space-y-2">
                  <p className="text-[10px] text-slate-500 font-medium">
                    {language === 'fr' ? 'Changer la décision:' : 'Change decision:'}
                  </p>
                  <div className="flex gap-1.5">
                    {/* GO Button - highlighted if current */}
                    <Button 
                      onClick={() => handleAdminDecision('approved')}
                      disabled={submittingDecision}
                      className={`flex-1 h-8 text-xs ${
                        simulation.admin_status === 'approved'
                          ? 'bg-emerald-600 hover:bg-emerald-700 ring-2 ring-emerald-300'
                          : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'
                      }`}
                      data-testid="change-to-go-btn"
                    >
                      {submittingDecision ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ThumbsUp className="w-3 h-3 mr-1" />}
                      <span className="font-bold">GO</span>
                    </Button>

                    {/* À l'étude Button - highlighted if current */}
                    <Button 
                      onClick={() => handleAdminDecision('pending_review')}
                      disabled={submittingDecision}
                      className={`flex-1 h-8 text-xs ${
                        simulation.admin_status === 'pending_review'
                          ? 'bg-amber-500 hover:bg-amber-600 text-white ring-2 ring-amber-300'
                          : 'bg-amber-100 hover:bg-amber-200 text-amber-700'
                      }`}
                      data-testid="change-to-review-btn"
                    >
                      {submittingDecision ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                      <span className="font-bold">{language === 'fr' ? 'À l\'étude' : 'Review'}</span>
                    </Button>

                    {/* Contre-proposition - toujours possible, même après un GO */}
                    <Button 
                      onClick={() => setShowCounterProposal(true)}
                      disabled={submittingDecision}
                      className={`flex-1 h-8 text-xs ${
                        simulation.admin_status === 'counter_proposal'
                          ? 'bg-orange-500 hover:bg-orange-600 text-white ring-2 ring-orange-300'
                          : 'bg-orange-100 hover:bg-orange-200 text-orange-700'
                      }`}
                      data-testid="change-to-counter-btn"
                    >
                      <MessageSquare className="w-3 h-3 mr-1" />
                      <span className="font-bold">{language === 'fr' ? 'Contre-prop.' : 'Counter'}</span>
                    </Button>

                    {/* NO GO Button - highlighted if current */}
                    <Button 
                      onClick={() => handleAdminDecision('rejected')}
                      disabled={submittingDecision}
                      className={`flex-1 h-8 text-xs ${
                        simulation.admin_status === 'rejected'
                          ? 'bg-red-600 hover:bg-red-700 text-white ring-2 ring-red-300'
                          : 'bg-red-100 hover:bg-red-200 text-red-700'
                      }`}
                      data-testid="change-to-nogo-btn"
                    >
                      {submittingDecision ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ThumbsDown className="w-3 h-3 mr-1" />}
                      <span className="font-bold">NO GO</span>
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ========== BLOC STATUT FINAL (Étape 4) - COMPACT ========== */}
        {canMakeDecision && isValidated && (
          <Card className={`border ${
            simulation?.business_status === 'won' || simulation?.business_status === 'signed' ? 'border-emerald-400 bg-emerald-50/30' :
            simulation?.business_status === 'lost' ? 'border-red-400 bg-red-50/30' :
            'border-purple-300 bg-purple-50/30'
          }`} data-testid="business-status-panel">
            <CardContent className="py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Trophy className="w-3.5 h-3.5 text-purple-600" />
                  <span className="text-xs font-medium text-slate-700">
                    {language === 'fr' ? 'Statut Final' : 'Final Status'}
                  </span>
                </div>
                
                {/* Show selected status or all buttons in edit mode */}
                {simulation?.business_status && simulation?.business_status !== 'in_progress' && !isEditingFinalStatus ? (
                  // Show only selected status with modify button
                  <div className="flex items-center gap-2">
                    <Badge className={`text-xs px-3 py-1 ${
                      simulation.business_status === 'won' || simulation.business_status === 'signed'
                        ? 'bg-emerald-600 text-white'
                        : simulation.business_status === 'lost'
                        ? 'bg-red-600 text-white'
                        : 'bg-amber-500 text-white'
                    }`}>
                      {simulation.business_status === 'won' && (
                        <><Trophy className="w-3 h-3 mr-1 inline" />{language === 'fr' ? 'Gagné' : 'Won'}</>
                      )}
                      {simulation.business_status === 'signed' && (
                        <><CheckCircle2 className="w-3 h-3 mr-1 inline" />{language === 'fr' ? 'Signé' : 'Signed'}</>
                      )}
                      {simulation.business_status === 'lost' && (
                        <><XCircle className="w-3 h-3 mr-1 inline" />{language === 'fr' ? 'Perdu' : 'Lost'}</>
                      )}
                    </Badge>
                    {(simulation.business_status === 'signed' || simulation.business_status === 'won') && simulation.date_signature && (
                      <span className="text-xs font-semibold text-emerald-700" data-testid="signed-date-label">
                        {language === 'fr' ? 'Signé le ' : 'Signed on '}
                        {new Date(simulation.date_signature).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB')}
                      </span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-xs px-2 border-slate-300 text-slate-600 hover:bg-slate-100"
                      onClick={() => setIsEditingFinalStatus(true)}
                      data-testid="edit-final-status-btn"
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      {language === 'fr' ? 'Modifier' : 'Edit'}
                    </Button>
                  </div>
                ) : (
                  // Show all status buttons
                  <div className="flex gap-1.5">
                    <Button
                      onClick={() => openSignatureDialog('won')}
                      size="sm"
                      className={`h-7 text-xs px-2 ${
                        simulation?.business_status === 'won' 
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                          : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'
                      }`}
                      data-testid="status-won-btn"
                    >
                      <Trophy className="w-3 h-3 mr-1" />
                      {language === 'fr' ? 'Gagné' : 'Won'}
                    </Button>

                    <Button
                      onClick={async () => {
                        try {
                          await api().patch(`/simulations/${id}/business-status`, { business_status: 'in_progress' });
                          setSimulation(prev => ({ ...prev, business_status: 'in_progress' }));
                          setIsEditingFinalStatus(false);
                          toast.success(language === 'fr' ? 'En cours' : 'In Progress');
                        } catch (error) {
                          toast.error(language === 'fr' ? 'Erreur' : 'Error');
                        }
                      }}
                      size="sm"
                      className={`h-7 text-xs px-2 ${
                        simulation?.business_status === 'in_progress' || !simulation?.business_status
                          ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                          : 'bg-amber-100 hover:bg-amber-200 text-amber-700'
                      }`}
                      data-testid="status-ongoing-btn"
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      {language === 'fr' ? 'En cours' : 'Ongoing'}
                    </Button>

                    <Button
                      onClick={async () => {
                        try {
                          await api().patch(`/simulations/${id}/business-status`, { business_status: 'lost' });
                          setSimulation(prev => ({ ...prev, business_status: 'lost' }));
                          setIsEditingFinalStatus(false);
                          toast.success(language === 'fr' ? 'Dossier Perdu' : 'Project Lost');
                        } catch (error) {
                          toast.error(language === 'fr' ? 'Erreur' : 'Error');
                        }
                      }}
                      size="sm"
                      className={`h-7 text-xs px-2 ${
                        simulation?.business_status === 'lost' 
                          ? 'bg-red-600 hover:bg-red-700 text-white' 
                          : 'bg-red-100 hover:bg-red-200 text-red-700'
                      }`}
                      data-testid="status-lost-btn"
                    >
                      <XCircle className="w-3 h-3 mr-1" />
                      {language === 'fr' ? 'Perdu' : 'Lost'}
                    </Button>
                    
                    {/* Cancel button when in edit mode */}
                    {isEditingFinalStatus && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs px-2 text-slate-500"
                        onClick={() => setIsEditingFinalStatus(false)}
                        data-testid="cancel-edit-status-btn"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Decision visible to creator (non-admin) when a decision has been made */}
        {!canMakeDecision && simulation?.admin_status && (
          <Card className={`border ${
            simulation.admin_status === 'approved' ? 'border-emerald-400 bg-emerald-50/30' :
            simulation.admin_status === 'rejected' ? 'border-red-400 bg-red-50/30' :
            simulation.admin_status === 'counter_proposal' ? 'border-amber-400 bg-amber-50/30' :
            'border-slate-300 bg-slate-50/30'
          }`} data-testid="user-decision-display">
            <CardContent className="py-3 space-y-2">
              {/* Header compact */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-slate-700">
                    {language === 'fr' ? 'Décision Direction' : 'Management Decision'}
                  </span>
                </div>
                <Badge className={`text-xs ${
                  simulation.admin_status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                  simulation.admin_status === 'rejected' ? 'bg-red-100 text-red-800' :
                  simulation.admin_status === 'counter_proposal' ? 'bg-amber-100 text-amber-800' :
                  'bg-slate-100 text-slate-800'
                }`}>
                  {simulation.admin_status === 'approved' && (language === 'fr' ? '✓ GO' : '✓ GO')}
                  {simulation.admin_status === 'rejected' && (language === 'fr' ? '✗ NO GO' : '✗ NO GO')}
                  {simulation.admin_status === 'counter_proposal' && (language === 'fr' ? '📝 Contre-prop.' : '📝 Counter')}
                  {simulation.admin_status === 'pending_review' && (language === 'fr' ? '🔄 À l\'étude' : '🔄 Review')}
                </Badge>
              </div>
              <p className="text-[10px] text-slate-500">
                {language === 'fr'
                  ? `Par ${simulation.admin_status_updated_by_name || 'la Direction'}`
                  : `By ${simulation.admin_status_updated_by_name || 'Management'}`}
                {simulation.admin_status_updated_at && (
                  <span className="ml-1">
                    — {new Date(simulation.admin_status_updated_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </span>
                )}
              </p>
              
              {simulation.admin_counter_proposal && (
                <div className="p-2 bg-amber-50 rounded border border-amber-200">
                  <p className="text-[10px] font-semibold text-amber-700 mb-1">
                    {language === 'fr' ? 'Contre-proposition' : 'Counter-proposal'}
                  </p>
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-[10px] text-amber-600">{language === 'fr' ? 'Commission' : 'Commission'}</p>
                      <p className="text-base font-bold text-amber-700">
                        {((simulation.admin_counter_proposal.commission_rate || 0) * 100).toFixed(0)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-amber-600">{language === 'fr' ? 'CA HT' : 'Revenue'}</p>
                      <p className="text-base font-bold text-amber-700">
                        {formatCurrency(simulation.admin_counter_proposal.ca_previsionnel_ht || 0)}
                      </p>
                    </div>
                  </div>
                  {simulation.admin_counter_proposal.comment && (
                    <p className="mt-1 text-[10px] text-amber-600 italic">
                      "{simulation.admin_counter_proposal.comment}"
                    </p>
                  )}
                </div>
              )}
              {simulation.admin_comment && (
                <div className="p-2 bg-slate-100 rounded">
                  <p className="text-xs text-slate-600">
                    <strong>{language === 'fr' ? 'Commentaire :' : 'Comment:'}</strong> {simulation.admin_comment}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Bouton "Demande de modification" pour les non-admins quand une décision est posée */}
        {!canMakeDecision && simulation?.admin_status && simulation?.id && token && (
          <DecisionModificationRequest
            simulationId={simulation.id}
            simulation={simulation}
            token={token}
            onUnlocked={() => { /* state refresh handled by component-level polling */ }}
          />
        )}

        {/* Photos Section removed - images were not displaying correctly */}

        {/* Tabs for P&L and Details */}
        <Tabs defaultValue="details" className="space-y-4" onValueChange={(val) => { if (val === 'audit' && auditLogs.length === 0) fetchAuditLogs(); if (val === 'notes' && notes.length === 0) fetchNotes(); }}>
          <TabsList>
            <TabsTrigger value="details">{t('summary')}</TabsTrigger>
            <TabsTrigger value="projections">{t('plProjections')}</TabsTrigger>
            <TabsTrigger value="notes" data-testid="notes-tab">
              <MessageSquare className="w-4 h-4 mr-1" />
              {language === 'fr' ? 'Notes' : 'Notes'}
              {notes.length > 0 && <Badge variant="secondary" className="ml-1 text-[10px]">{notes.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="audit-tab">
              <History className="w-4 h-4 mr-1" />
              {language === 'fr' ? 'Historique' : 'History'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projections">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {t('plProjections')}
                  {previewData && (
                    <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                      {language === 'fr' ? 'Prévisualisation' : 'Preview'}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' ? 'Projections sur 4 ans' : '4-year projections'}
                </CardDescription>
                {/* Source selection tabs for P&L */}
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    variant={analysisSource === 'tool' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleSourceChange('tool')}
                    data-testid="pl-source-tool-btn"
                  >
                    {language === 'fr' ? 'Outil' : 'Tool'}
                  </Button>
                  {simulation.validated && (
                    <Button
                      variant={analysisSource === 'commercial' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleSourceChange('commercial')}
                      data-testid="pl-source-commercial-btn"
                    >
                      {language === 'fr' ? 'Commercial' : 'Commercial'}
                    </Button>
                  )}
                  {(simulation.admin_status === 'approved' || simulation.admin_counter_proposal) && (
                    <Button
                      variant={analysisSource === 'direction' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleSourceChange('direction')}
                      data-testid="pl-source-direction-btn"
                    >
                      {language === 'fr' ? 'Direction' : 'Direction'}
                    </Button>
                  )}
                </div>
                {/* Parameters used header */}
                {(() => {
                  // Determine which data to use for the parameters display
                  // IMPORTANT: Always respect analysisSource to show correct data per tab
                  let caHt, commissionRate, foodCostRate, otherCostRate, laborCostRate, clusterName;
                  
                  // First, compute the plData used for the table (so we can derive effective rates)
                  let plDataForRates;
                  if (previewData?.pl_projections) {
                    plDataForRates = previewData.pl_projections;
                  } else if (analysisSource === 'commercial' && simulation.commercial_pl_projections) {
                    plDataForRates = simulation.commercial_pl_projections;
                  } else if (analysisSource === 'direction' && simulation.direction_pl_projections) {
                    plDataForRates = simulation.direction_pl_projections;
                  } else {
                    plDataForRates = simulation.pl_projections;
                  }
                  // Effective Y1 labor cost ratio derived from the data actually shown in the table.
                  // This guarantees consistency between the header and the table cell, regardless of
                  // which view (Outil / Commercial / Direction) is selected.
                  const effectiveLaborRateFromPL = (() => {
                    const y1 = plDataForRates?.[0];
                    if (!y1 || !y1.gross_sales_ht) return null;
                    return y1.direct_labor_costs / y1.gross_sales_ht;
                  })();
                  
                  if (analysisSource === 'tool') {
                    // OUTIL tab: Always use original tool/system values
                    if (previewData) {
                      caHt = previewData.ca_ht_annual;
                      foodCostRate = previewData.food_cost_rate_used;
                      otherCostRate = previewData.other_cost_rate_used;
                      laborCostRate = previewData.labor_cost_rate_used;
                      clusterName = previewData.cluster_used;
                    } else {
                      caHt = simulation.average_hypothesis?.ca_ht_annual;
                      foodCostRate = simulation.food_cost_rate_used;
                      otherCostRate = simulation.other_cost_rate_used;
                      laborCostRate = simulation.labor_cost_rate_used;
                      clusterName = simulation.cluster_used;
                    }
                    // Commission MUST use dynamic calculation to match "Proposition du Système"
                    commissionRate = getDynamicCommissionRate();
                  } else if (analysisSource === 'direction') {
                    caHt = simulation.admin_counter_proposal?.ca_previsionnel_ht || simulation.ca_previsionnel_ht_proposed || simulation.average_hypothesis?.ca_ht_annual;
                    commissionRate = simulation.admin_counter_proposal?.commission_rate || simulation.commission_rate_proposed || simulation.commission_rate_used;
                    foodCostRate = simulation.direction_food_cost_rate || simulation.food_cost_rate_used;
                    otherCostRate = simulation.direction_other_cost_rate || simulation.other_cost_rate_used;
                    // Labor cost: prefer effective rate derived from the P&L (always available),
                    // fallback to stored rate if no plData (extremely unlikely).
                    laborCostRate = effectiveLaborRateFromPL ?? (simulation.direction_labor_cost_rate || simulation.labor_cost_rate_used);
                    clusterName = simulation.direction_cluster_used || simulation.cluster_used;
                  } else if (analysisSource === 'commercial') {
                    caHt = simulation.ca_previsionnel_ht_proposed || simulation.average_hypothesis?.ca_ht_annual;
                    commissionRate = simulation.commission_rate_proposed || simulation.commission_rate_used;
                    foodCostRate = simulation.commercial_food_cost_rate || simulation.food_cost_rate_used;
                    otherCostRate = simulation.commercial_other_cost_rate || simulation.other_cost_rate_used;
                    // Labor cost: prefer effective rate derived from the P&L (always available),
                    // fallback to stored rate if no plData (extremely unlikely).
                    laborCostRate = effectiveLaborRateFromPL ?? (simulation.commercial_labor_cost_rate || simulation.labor_cost_rate_used);
                    clusterName = simulation.commercial_cluster_used || simulation.cluster_used;
                  } else {
                    caHt = simulation.average_hypothesis?.ca_ht_annual;
                    commissionRate = simulation.commission_rate_used;
                    foodCostRate = simulation.food_cost_rate_used;
                    otherCostRate = simulation.other_cost_rate_used;
                    laborCostRate = simulation.labor_cost_rate_used;
                    clusterName = simulation.cluster_used;
                  }
                  
                  return (
                    <div className="mt-4 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                      <div className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                        {language === 'fr' ? 'Données ' : 'Data '}
                        {analysisSource === 'tool' ? (language === 'fr' ? 'Outil' : 'Tool') : 
                         analysisSource === 'commercial' ? 'Commercial' : 'Direction'}
                        {clusterName && <span className="ml-2 text-xs text-slate-500">({clusterName})</span>}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        <div>
                          <span className="text-slate-500">{language === 'fr' ? 'CA prévisionnel HT:' : 'Expected Revenue:'}</span>
                          <span className="ml-1 font-semibold">{formatCurrency(caHt)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">{language === 'fr' ? 'Commission:' : 'Commission:'}</span>
                          <span className="ml-1 font-semibold">{((commissionRate || 0) * 100).toFixed(1)}%</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Food Cost:</span>
                          <span className="ml-1 font-semibold text-red-600">{((foodCostRate || 0) * 100).toFixed(2)}%</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Labor Cost:</span>
                          <span className="ml-1 font-semibold text-red-600">{((laborCostRate || 0) * 100).toFixed(2)}%</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Other Cost:</span>
                          <span className="ml-1 font-semibold text-red-600">{((otherCostRate || 0) * 100).toFixed(2)}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {(() => {
                  // Use preview P&L data if available, otherwise use stored simulation data based on analysisSource
                  let plData;
                  if (previewData?.pl_projections) {
                    plData = previewData.pl_projections;
                  } else if (analysisSource === 'commercial' && simulation.commercial_pl_projections) {
                    plData = simulation.commercial_pl_projections;
                  } else if (analysisSource === 'direction' && simulation.direction_pl_projections) {
                    plData = simulation.direction_pl_projections;
                  } else {
                    // Default: use tool data (original pl_projections)
                    plData = simulation.pl_projections;
                  }
                  
                  return (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead rowSpan={2} className="w-48 align-bottom">{language === 'fr' ? 'Indicateur' : 'Indicator'}</TableHead>
                      <TableHead colSpan={2} className="text-center border-l">Y1</TableHead>
                      <TableHead colSpan={2} className="text-center border-l">Y2</TableHead>
                      <TableHead colSpan={2} className="text-center border-l">Y3</TableHead>
                      <TableHead colSpan={2} className="text-center border-l">Y4</TableHead>
                    </TableRow>
                    <TableRow>
                      <TableHead className="text-right font-mono text-xs border-l">Montant</TableHead>
                      <TableHead className="text-right font-mono text-xs text-slate-500">%</TableHead>
                      <TableHead className="text-right font-mono text-xs border-l">Montant</TableHead>
                      <TableHead className="text-right font-mono text-xs text-slate-500">%</TableHead>
                      <TableHead className="text-right font-mono text-xs border-l">Montant</TableHead>
                      <TableHead className="text-right font-mono text-xs text-slate-500">%</TableHead>
                      <TableHead className="text-right font-mono text-xs border-l">Montant</TableHead>
                      <TableHead className="text-right font-mono text-xs text-slate-500">%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">{t('grossSalesTTC')}</TableCell>
                      {plData.map((pl) => (
                        <>
                          <TableCell key={`ttc-${pl.year}`} className="text-right font-mono border-l">
                            {formatCurrency(pl.gross_sales_ttc)}
                          </TableCell>
                          <TableCell key={`ttc-pct-${pl.year}`} className="text-right font-mono text-slate-400">
                            -
                          </TableCell>
                        </>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">{t('grossSalesHT')}</TableCell>
                      {plData.map((pl) => (
                        <>
                          <TableCell key={`ht-${pl.year}`} className="text-right font-mono border-l">
                            {formatCurrency(pl.gross_sales_ht)}
                          </TableCell>
                          <TableCell key={`ht-pct-${pl.year}`} className="text-right font-mono text-slate-500">
                            100%
                          </TableCell>
                        </>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">{t('salesCommissions')}</TableCell>
                      {plData.map((pl) => {
                        const pct = pl.gross_sales_ht ? Math.abs(pl.sales_commissions / pl.gross_sales_ht * 100) : 0;
                        return (
                          <>
                            <TableCell key={`comm-${pl.year}`} className="text-right font-mono text-emerald-600 border-l">
                              {formatCurrency(pl.sales_commissions)}
                            </TableCell>
                            <TableCell key={`comm-pct-${pl.year}`} className="text-right font-mono text-slate-500">
                              ({pct.toFixed(1)}%)
                            </TableCell>
                          </>
                        );
                      })}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">{t('foodCosts')}</TableCell>
                      {plData.map((pl) => {
                        const pct = pl.gross_sales_ht ? Math.abs(pl.food_costs / pl.gross_sales_ht * 100) : 0;
                        return (
                          <>
                            <TableCell key={`food-${pl.year}`} className="text-right font-mono text-red-600 border-l">
                              {formatCurrency(pl.food_costs)}
                            </TableCell>
                            <TableCell key={`food-pct-${pl.year}`} className="text-right font-mono text-slate-500">
                              ({pct.toFixed(1)}%)
                            </TableCell>
                          </>
                        );
                      })}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">{t('directLaborCosts')}</TableCell>
                      {plData.map((pl) => {
                        const pct = pl.gross_sales_ht ? Math.abs(pl.direct_labor_costs / pl.gross_sales_ht * 100) : 0;
                        return (
                          <>
                            <TableCell key={`labor-${pl.year}`} className="text-right font-mono text-red-600 border-l">
                              {formatCurrency(pl.direct_labor_costs)}
                            </TableCell>
                            <TableCell key={`labor-pct-${pl.year}`} className="text-right font-mono text-slate-500">
                              ({pct.toFixed(1)}%)
                            </TableCell>
                          </>
                        );
                      })}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">{t('otherCosts')}</TableCell>
                      {plData.map((pl) => {
                        const pct = pl.gross_sales_ht ? Math.abs(pl.other_costs / pl.gross_sales_ht * 100) : 0;
                        return (
                          <>
                            <TableCell key={`other-${pl.year}`} className="text-right font-mono text-red-600 border-l">
                              {formatCurrency(pl.other_costs)}
                            </TableCell>
                            <TableCell key={`other-pct-${pl.year}`} className="text-right font-mono text-slate-500">
                              ({pct.toFixed(1)}%)
                            </TableCell>
                          </>
                        );
                      })}
                    </TableRow>
                    <TableRow className="bg-slate-50 dark:bg-slate-800">
                      <TableCell className="font-semibold">{t('contributionMargin')}</TableCell>
                      {plData.map((pl) => {
                        const pct = pl.gross_sales_ht ? (pl.contribution_margin / pl.gross_sales_ht * 100) : 0;
                        return (
                          <>
                            <TableCell key={`margin-${pl.year}`} className="text-right font-mono font-semibold border-l">
                              {formatCurrency(pl.contribution_margin)}
                            </TableCell>
                            <TableCell key={`margin-pct-${pl.year}`} className="text-right font-mono text-slate-500">
                              {pct.toFixed(1)}%
                            </TableCell>
                          </>
                        );
                      })}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">{t('sga')}</TableCell>
                      {plData.map((pl) => {
                        const pct = pl.gross_sales_ht ? Math.abs(pl.sga / pl.gross_sales_ht * 100) : 0;
                        return (
                          <>
                            <TableCell key={`sga-${pl.year}`} className="text-right font-mono text-red-600 border-l">
                              {formatCurrency(pl.sga)}
                            </TableCell>
                            <TableCell key={`sga-pct-${pl.year}`} className="text-right font-mono text-slate-500">
                              ({pct.toFixed(1)}%)
                            </TableCell>
                          </>
                        );
                      })}
                    </TableRow>
                    <TableRow className="bg-blue-50 dark:bg-blue-900/20">
                      <TableCell className="font-bold text-blue-700 dark:text-blue-400">{t('ebitda')}</TableCell>
                      {plData.map((pl) => {
                        const pct = pl.gross_sales_ht ? (pl.ebitda / pl.gross_sales_ht * 100) : 0;
                        return (
                          <>
                            <TableCell 
                              key={`ebitda-${pl.year}`} 
                              className={`text-right font-mono font-bold border-l ${
                                pl.ebitda >= 0 
                                  ? 'text-emerald-600 dark:text-emerald-400' 
                                  : 'text-red-600 dark:text-red-400'
                              }`}
                            >
                              {formatCurrency(pl.ebitda)}
                            </TableCell>
                            <TableCell key={`ebitda-pct-${pl.year}`} className="text-right font-mono text-slate-500 font-normal">
                              {pct.toFixed(1)}%
                            </TableCell>
                          </>
                        );
                      })}
                    </TableRow>
                    {/* CAPEX Row */}
                    <TableRow className="border-t-2 border-slate-300">
                      <TableCell className="font-medium">CAPEX</TableCell>
                      <TableCell className="text-right font-mono text-red-600 border-l">
                        {formatCurrency(-(simulation.ccd_capex || simulation.total_capex || 0))}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-400">-</TableCell>
                      <TableCell className="text-right font-mono text-slate-400 border-l">-</TableCell>
                      <TableCell className="text-right font-mono text-slate-400">-</TableCell>
                      <TableCell className="text-right font-mono text-slate-400 border-l">-</TableCell>
                      <TableCell className="text-right font-mono text-slate-400">-</TableCell>
                      <TableCell className="text-right font-mono text-slate-400 border-l">-</TableCell>
                      <TableCell className="text-right font-mono text-slate-400">-</TableCell>
                    </TableRow>
                    {/* Cash Estimate Row (cumulative EBITDA - CAPEX) */}
                    <TableRow className="bg-amber-50 dark:bg-amber-900/20">
                      <TableCell className="font-semibold text-amber-700 dark:text-amber-400">
                        Cash Estimate
                        <span className="block text-xs font-normal text-slate-500">= EBITDA cumulé - CAPEX</span>
                      </TableCell>
                      {(() => {
                        const capex = simulation.ccd_capex || simulation.total_capex || 0;
                        let cumulative = 0;
                        return plData.map((pl) => {
                          cumulative += pl.ebitda;
                          const cashEstimate = cumulative - capex;
                          return (
                            <>
                              <TableCell 
                                key={`cash-${pl.year}`} 
                                className={`text-right font-mono font-semibold border-l ${
                                  cashEstimate >= 0 
                                    ? 'text-emerald-600 dark:text-emerald-400' 
                                    : 'text-red-600 dark:text-red-400'
                                }`}
                              >
                                {formatCurrency(cashEstimate)}
                              </TableCell>
                              <TableCell key={`cash-pct-${pl.year}`} className="text-right font-mono text-slate-400">-</TableCell>
                            </>
                          );
                        });
                      })()}
                    </TableRow>
                    {/* Payback Period Row */}
                    <TableRow className="bg-purple-50 dark:bg-purple-900/20">
                      <TableCell className="font-semibold text-purple-700 dark:text-purple-400">
                        {language === 'fr' ? 'Période Payback' : 'Payback Period'}
                      </TableCell>
                      <TableCell colSpan={8} className="text-center font-mono">
                        {(() => {
                          // Recompute payback DYNAMICALLY from plData + CAPEX so it stays
                          // consistent with the Cash Estimate row above when switching tabs
                          // (Outil / Commercial / Direction).
                          // Monthly EBITDA is approximated linearly within each year.
                          const capex = simulation.ccd_capex || simulation.total_capex || 0;
                          let paybackValue = 0;
                          if (capex > 0 && Array.isArray(plData) && plData.length > 0) {
                            let cumul = 0;
                            let monthIdx = 0;
                            outer: for (let y = 0; y < plData.length; y++) {
                              const yearEbitda = plData[y]?.ebitda || 0;
                              const monthlyEbitda = yearEbitda / 12;
                              for (let m = 0; m < 12; m++) {
                                const prev = cumul;
                                cumul += monthlyEbitda;
                                monthIdx += 1;
                                if (cumul >= capex && monthlyEbitda > 0) {
                                  const remaining = capex - prev;
                                  const fraction = remaining / monthlyEbitda;
                                  paybackValue = (monthIdx - 1) + fraction;
                                  break outer;
                                }
                              }
                            }
                            paybackValue = Math.round(paybackValue * 10) / 10;
                          }
                          
                          if (paybackValue > 0 && paybackValue < 48) {
                            if (paybackValue < 12) {
                              return (
                                <span className="text-emerald-600 font-bold" data-testid="payback-value">
                                  {paybackValue.toFixed(1)} {language === 'fr' ? 'mois' : 'months'}
                                </span>
                              );
                            } else {
                              const years = Math.floor(paybackValue / 12);
                              const months = Math.round(paybackValue % 12);
                              return (
                                <span className="text-emerald-600 font-bold" data-testid="payback-value">
                                  {years} {language === 'fr' ? 'an(s)' : 'year(s)'} {months > 0 ? `${months} ${language === 'fr' ? 'mois' : 'months'}` : ''}
                                  <span className="text-slate-500 font-normal ml-2">
                                    ({language === 'fr' ? 'soit' : 'i.e.'} {paybackValue.toFixed(1)} {language === 'fr' ? 'mois' : 'months'})
                                  </span>
                                </span>
                              );
                            }
                          } else {
                            return (
                              <span className="text-red-600 font-bold" data-testid="payback-value">
                                {language === 'fr' ? '> 4 ans (non atteint)' : '> 4 years (not reached)'}
                              </span>
                            );
                          }
                        })()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="details">
            <Card>
              <CardHeader>
                <CardTitle>{t('summary')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white mb-3">
                      {t('generalInfo')}
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{t('projectName')}</dt>
                        <dd className="font-medium">{getFormattedProjectName()}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{t('retailerName')}</dt>
                        <dd className="font-medium">{simulation.input_data.general_info.retailer_name}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{t('projectType')}</dt>
                        <dd className="font-medium">{simulation.input_data.general_info.project_type}</dd>
                      </div>
                      <div className="flex justify-between items-center">
                        <dt className="text-slate-500">Format</dt>
                        <dd><FormatPill format={simulation.input_data.general_info.format} testid="recap-format" /></dd>
                      </div>
                      {/* Renewal-specific details */}
                      {simulation.input_data.general_info.project_type === 'Renouvellement contrat' && (
                        <>
                          {simulation.input_data.general_info.renouvellement_change_inox && (
                            <div className="flex justify-between">
                              <dt className="text-slate-500">Changement inox</dt>
                              <dd className="font-medium">{simulation.input_data.general_info.renouvellement_change_inox}</dd>
                            </div>
                          )}
                          {simulation.input_data.general_info.renouvellement_change_vitrines && (
                            <div className="flex justify-between">
                              <dt className="text-slate-500">Changement vitrine(s)</dt>
                              <dd className="font-medium">{simulation.input_data.general_info.renouvellement_change_vitrines}</dd>
                            </div>
                          )}
                          {simulation.input_data.general_info.renouvellement_change_plv && (
                            <div className="flex justify-between">
                              <dt className="text-slate-500">Changement PLV</dt>
                              <dd className="font-medium">{simulation.input_data.general_info.renouvellement_change_plv}</dd>
                            </div>
                          )}
                        </>
                      )}
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{t('concept')}</dt>
                        <dd className="font-medium">{simulation.input_data.capex_investment.concept}</dd>
                      </div>
                      {/* Emplacement du corner */}
                      {simulation.input_data.capex_investment.corner_location && (
                        <div className="flex justify-between">
                          <dt className="text-slate-500">{language === 'fr' ? 'Emplacement' : 'Location'}</dt>
                          <dd className="font-medium">{simulation.input_data.capex_investment.corner_location}</dd>
                        </div>
                      )}
                      {/* Note de l'emplacement */}
                      {simulation.input_data.capex_investment.location_rating && (
                        <div className="flex justify-between">
                          <dt className="text-slate-500">{language === 'fr' ? 'Note emplacement' : 'Location Rating'}</dt>
                          <dd className="font-medium">{simulation.input_data.capex_investment.location_rating}</dd>
                        </div>
                      )}
                      {/* Projet du corner */}
                      {simulation.input_data.capex_investment.corner_project && (
                        <div className="flex justify-between">
                          <dt className="text-slate-500">{language === 'fr' ? 'Projet corner' : 'Corner Project'}</dt>
                          <dd className="font-medium">{simulation.input_data.capex_investment.corner_project}</dd>
                        </div>
                      )}
                      {/* Démontage du corner (toujours affiché) */}
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{language === 'fr' ? 'Démontage corner' : 'Corner dismantling'}</dt>
                        <dd className="font-medium">
                          {simulation.input_data.capex_investment.corner_dismantling
                            ? (() => {
                                const by = simulation.input_data.capex_investment.dismantling_by;
                                const byLabel = by === 'DSAO' ? 'DSAO' : by === 'CLIENT' ? (language === 'fr' ? 'Client' : 'Client') : by === 'PRESTATAIRE_SORTANT' ? (language === 'fr' ? 'Prestataire sortant' : 'Outgoing provider') : null;
                                return (language === 'fr' ? 'Oui' : 'Yes') + (byLabel ? ` — ${byLabel}` : '');
                              })()
                            : (language === 'fr' ? 'Non' : 'No')}
                        </dd>
                      </div>
                      {/* Composition des vitrines */}
                      {(() => {
                        const comp = simulation.input_data.capex_investment.vitrine_composition || {};
                        const types = [
                          ['v250', 'Vitrine 250 cm', 8000],
                          ['v196', 'Vitrine 196 cm', 6000],
                          ['v146', 'Vitrine 146 cm', 4000],
                          ['v116', 'Vitrine 116 cm', 3300],
                          ['bac120', 'Bac réfrigéré 120x120', 4000],
                        ];
                        const rows = types.filter(([k]) => (comp[k] || 0) > 0);
                        const totalVitrines = (comp.v250 || 0) + (comp.v196 || 0) + (comp.v146 || 0) + (comp.v116 || 0);
                        const totalBacs = comp.bac120 || 0;
                        if (rows.length === 0) return null;
                        return (
                          <>
                            <div className="flex justify-between" data-testid="result-vitrines-count">
                              <dt className="text-slate-500">{language === 'fr' ? 'Vitrines installées' : 'Vitrines installed'}</dt>
                              <dd className="font-medium font-mono">{totalVitrines}</dd>
                            </div>
                            {totalBacs > 0 && (
                              <div className="flex justify-between" data-testid="result-bacs-count">
                                <dt className="text-slate-500">{language === 'fr' ? 'Bacs réfrigérés' : 'Refrigerated bins'}</dt>
                                <dd className="font-medium font-mono">{totalBacs}</dd>
                              </div>
                            )}
                            <div className="pt-1" data-testid="result-vitrine-composition">
                              <dt className="text-slate-500 mb-1">{language === 'fr' ? 'Composition vitrines' : 'Vitrine composition'}</dt>
                              {rows.map(([k, lbl, pr]) => (
                                <div key={k} className="flex justify-between pl-3 text-xs">
                                  <span className="text-slate-500">{lbl} ×{comp[k]}</span>
                                  <span className="font-medium font-mono">{formatCurrency(comp[k] * pr)}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        );
                      })()}
                      {/* Labo équipé ? Oui/Non (toujours affiché) */}
                      <div className="flex justify-between" data-testid="result-labo-equipped">
                        <dt className="text-slate-500">{language === 'fr' ? 'Labo équipé' : 'Lab equipped'}</dt>
                        <dd className="font-medium">
                          {simulation.input_data.capex_investment.labo_to_equip
                            ? (language === 'fr' ? 'Oui' : 'Yes')
                            : (language === 'fr' ? 'Non' : 'No')}
                        </dd>
                      </div>
                      {/* Inox Labo */}
                      {simulation.input_data.capex_investment.labo_to_equip && (
                        <div className="flex justify-between" data-testid="result-inox-labo">
                          <dt className="text-slate-500">{language === 'fr' ? 'Inox Labo' : 'Lab Inox'}</dt>
                          <dd className="font-medium font-mono">
                            {simulation.input_data.capex_investment.labo_inox_investor || 'CCD'} — {formatCurrency(simulation.input_data.capex_investment.capex_inox_labo || 0)}
                          </dd>
                        </div>
                      )}
                      {simulation.input_data.capex_investment.labo_to_equip && simulation.input_data.capex_investment.labo_comment && (
                        <div className="flex justify-between">
                          <dt className="text-slate-500">{language === 'fr' ? 'Commentaire labo' : 'Lab comment'}</dt>
                          <dd className="font-medium text-right max-w-[60%]">{simulation.input_data.capex_investment.labo_comment}</dd>
                        </div>
                      )}
                      {/* Machine à tapisser le riz */}
                      {simulation.input_data.capex_investment.rice_machine && (
                        <div className="flex justify-between" data-testid="result-rice-machine">
                          <dt className="text-slate-500">{language === 'fr' ? 'Machine à tapisser riz' : 'Rice machine'}</dt>
                          <dd className="font-medium font-mono">CCD — {formatCurrency(simulation.input_data.capex_investment.capex_rice_machine || 0)}</dd>
                        </div>
                      )}
                      {/* Porteur du CAPEX */}
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{language === 'fr' ? 'Porteur CAPEX' : 'CAPEX Carrier'}</dt>
                        <dd className="font-medium">
                          {(() => {
                            const capex = simulation.input_data.capex_investment;
                            const clientCarriesAll = 
                              capex.corner_investor === 'CLIENT' && 
                              capex.vitrine_investor === 'CLIENT' && 
                              capex.inox_investor === 'CLIENT';
                            return clientCarriesAll 
                              ? (language === 'fr' ? 'CLIENT' : 'CLIENT') 
                              : (language === 'fr' ? 'CCD (Wasabi)' : 'CCD (Wasabi)');
                          })()}
                        </dd>
                      </div>
                      {/* Nombre de collaborateurs */}
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{language === 'fr' ? 'Collaborateurs' : 'Employees'}</dt>
                        <dd className="font-medium font-mono">
                          {(() => {
                            const labor = simulation.input_data.labor_costs || {};
                            // Somme des ETP (hors manager multisite qui est un poste partagé)
                            const totalEtp = (labor.chef_confirme_count || 0) +
                              (labor.chef_junior_count || 0) +
                              (labor.second_count || 0) +
                              (labor.commis_count || 0);
                            return totalEtp;
                          })()}
                        </dd>
                      </div>
                      {/* Masse salariale estimée (Y1) */}
                      {(() => {
                        const labor = simulation.input_data.labor_costs || {};
                        const totalEtp = (labor.chef_confirme_count || 0) +
                          (labor.chef_junior_count || 0) +
                          (labor.second_count || 0) +
                          (labor.commis_count || 0);
                        const y1Labor = Math.abs(simulation.pl_projections?.[0]?.direct_labor_costs || 0);
                        if (!y1Labor) return null;
                        const avgPerEtp = totalEtp > 0 ? y1Labor / totalEtp : 0;
                        return (
                          <>
                            <div className="flex justify-between" data-testid="payroll-annual-row">
                              <dt className="text-slate-500">{language === 'fr' ? 'Masse salariale estimée (An 1)' : 'Estimated payroll (Y1)'}</dt>
                              <dd className="font-medium font-mono">{formatCurrency(y1Labor)}</dd>
                            </div>
                            {avgPerEtp > 0 && (
                              <div className="flex justify-between" data-testid="payroll-per-etp-row">
                                <dt className="text-slate-500">{language === 'fr' ? 'Coût moyen / ETP' : 'Avg. cost / FTE'}</dt>
                                <dd className="font-medium font-mono">{formatCurrency(avgPerEtp)}</dd>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      {/* Date de création de l'analyse */}
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{language === 'fr' ? "Date d'ouverture estimée" : 'Estimated Opening Date'}</dt>
                        <dd className="font-medium font-mono">
                          {simulation.input_data?.general_info?.opening_date ? new Date(simulation.input_data.general_info.opening_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          }) : '-'}
                        </dd>
                      </div>
                      {/* Revenu médian */}
                      {simulation.input_data.general_info.revenu_median && (
                        <div className="flex justify-between">
                          <dt className="text-slate-500">{language === 'fr' ? 'Revenu médian' : 'Median Income'}</dt>
                          <dd className="font-medium font-mono">{formatCurrency(simulation.input_data.general_info.revenu_median)}</dd>
                        </div>
                      )}
                      {/* Age moyen zone */}
                      {simulation.input_data.general_info.zone_avg_age && (
                        <div className="flex justify-between">
                          <dt className="text-slate-500">{language === 'fr' ? 'Âge moyen zone' : 'Average Age'}</dt>
                          <dd className="font-medium font-mono">{simulation.input_data.general_info.zone_avg_age} ans</dd>
                        </div>
                      )}
                      {/* CSP */}
                      {simulation.input_data.general_info.zone_csp && (
                        <div className="flex justify-between">
                          <dt className="text-slate-500">CSP</dt>
                          <dd className="font-medium">{simulation.input_data.general_info.zone_csp}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white mb-3">
                      {t('contractInfo')}
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{t('commissionModel')}</dt>
                        <dd className="font-medium">{simulation.input_data.contract_info.commission_model}</dd>
                      </div>
                      {/* Taux négocié = commission basée sur la décision finale (Direction > Commercial > Outil) */}
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{t('negotiatedRate')}</dt>
                        <dd className="font-medium font-mono">
                          {formatPercent(
                            // Priorité: Direction (counter-proposal ou approved) > Commercial > Outil
                            simulation.admin_counter_proposal?.commission_rate ||
                            (simulation.admin_status === 'approved' && simulation.commission_rate_proposed) ||
                            simulation.commission_rate_proposed || 
                            validationData.commission_rate || 
                            simulation.commission_rate_used ||
                            simulation.input_data.contract_info.negotiated_commission_rate
                          )}
                        </dd>
                      </div>
                      {/* CA Prévisionnel HT - basé sur la décision finale */}
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{language === 'fr' ? 'CA Prévisionnel HT' : 'Projected Revenue'}</dt>
                        <dd className="font-medium font-mono">
                          {formatCurrency(
                            // Priorité: Direction (counter-proposal ou approved) > Commercial > Outil
                            simulation.admin_counter_proposal?.ca_previsionnel_ht ||
                            (simulation.admin_status === 'approved' && simulation.ca_previsionnel_ht_proposed) ||
                            simulation.ca_previsionnel_ht_proposed || 
                            simulation.average_hypothesis?.ca_ht_annual ||
                            0
                          )}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{t('contractDuration')}</dt>
                        <dd className="font-medium font-mono">
                          {simulation.input_data.contract_info.contract_duration_months} {t('months')}
                        </dd>
                      </div>
                      {/* Contribution Margin - mois en négatif - utiliser previewData si disponible */}
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{language === 'fr' ? 'Contribution Margin' : 'Contribution Margin'}</dt>
                        <dd className="font-medium font-mono">
                          {(() => {
                            // Utiliser les données recalculées si disponibles
                            const redMonths = previewData?.red_months_count ?? simulation.red_months_count ?? 0;
                            const cmColor = redMonths < 3 ? 'text-emerald-600' : redMonths <= 6 ? 'text-amber-600' : 'text-red-600';
                            if (redMonths === 0) {
                              return <span className={cmColor}>{language === 'fr' ? '0 mois négatif' : '0 negative months'}</span>;
                            } else {
                              return <span className={cmColor}>{redMonths} {language === 'fr' ? 'mois négatif(s)' : 'negative month(s)'}</span>;
                            }
                          })()}
                        </dd>
                      </div>
                      {/* Décision dossier provisoire - basée sur les mois recalculés */}
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{language === 'fr' ? 'Décision provisoire' : 'Provisional Decision'}</dt>
                        <dd className="font-medium">
                          {(() => {
                            const redMonths = previewData?.red_months_count ?? simulation.red_months_count ?? 0;
                            if (redMonths < 3) {
                              return <span className="text-emerald-600 font-semibold">{language === 'fr' ? 'ACCEPTÉ' : 'ACCEPTED'}</span>;
                            } else if (redMonths <= 6) {
                              return <span className="text-amber-600 font-semibold">{language === 'fr' ? 'EN COMMISSION' : 'UNDER REVIEW'}</span>;
                            } else {
                              return <span className="text-red-600 font-semibold">{language === 'fr' ? 'REFUSÉ' : 'REJECTED'}</span>;
                            }
                          })()}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Données Financières Magasin - Cliquable */}
                <details className="mt-6 pt-6 border-t group">
                  <summary className="font-medium text-slate-900 dark:text-white flex items-center gap-2 cursor-pointer list-none hover:text-blue-600 transition-colors">
                    <BarChart3 className="w-4 h-4 text-blue-500" />
                    {language === 'fr' ? 'Données Financières Magasin' : 'Store Financial Data'}
                    <span className="ml-auto text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
                    {/* CA Hors Carburant */}
                    {simulation.input_data.financial_data.annual_ca_hors_carburant && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <dt className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                          {language === 'fr' ? 'CA Hors Carburant' : 'Revenue excl. Fuel'}
                        </dt>
                        <dd className="font-semibold font-mono text-slate-900 dark:text-white">
                          {formatCurrency(simulation.input_data.financial_data.annual_ca_hors_carburant)}
                        </dd>
                      </div>
                    )}
                    
                    {/* CA Alimentaire */}
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <dt className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                        {language === 'fr' ? 'CA Alimentaire' : 'Food Revenue'}
                      </dt>
                      <dd className="font-semibold font-mono text-slate-900 dark:text-white">
                        {formatCurrency(simulation.input_data.financial_data.annual_ca_alimentaire)}
                      </dd>
                    </div>
                    
                    {/* CA Poissonnerie & Saurisserie */}
                    {simulation.input_data.financial_data.annual_ca_poissonnerie > 0 && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <dt className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                          {language === 'fr' ? 'CA Poissonnerie & Saurisserie' : 'Fish & Delicatessen Revenue'}
                        </dt>
                        <dd className="font-semibold font-mono text-slate-900 dark:text-white">
                          {formatCurrency(simulation.input_data.financial_data.annual_ca_poissonnerie)}
                        </dd>
                      </div>
                    )}
                    
                    {/* Fréquentation magasin */}
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <dt className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                        {language === 'fr' ? 'Fréquentation magasin' : 'Store Traffic'}
                      </dt>
                      <dd className="font-semibold font-mono text-slate-900 dark:text-white">
                        {new Intl.NumberFormat('fr-FR').format(simulation.input_data.financial_data.annual_frequentation)} {language === 'fr' ? 'clients/an' : 'customers/year'}
                      </dd>
                    </div>
                    
                    {/* Panier moyen */}
                    {simulation.input_data.financial_data.global_ticket_moyen && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <dt className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                          {language === 'fr' ? 'Panier moyen' : 'Average Basket'}
                        </dt>
                        <dd className="font-semibold font-mono text-slate-900 dark:text-white">
                          {formatCurrency(simulation.input_data.financial_data.global_ticket_moyen)}
                        </dd>
                      </div>
                    )}
                  </div>
                </details>

                {/* Competition Info Block - Cliquable - Only show if has competitor */}
                {simulation.input_data.competition_info?.has_competitor && (
                  <details className="mt-6 pt-6 border-t group">
                    <summary className="font-medium text-slate-900 dark:text-white flex items-center gap-2 cursor-pointer list-none hover:text-orange-600 transition-colors">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      {language === 'fr' ? 'Concurrence' : 'Competition'}
                      <span className="ml-auto text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
                      {/* Competitor Name */}
                      {simulation.input_data.competition_info.competitor_name && (
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                          <dt className="text-xs text-orange-600 dark:text-orange-400 mb-1">
                            {language === 'fr' ? 'Concurrent actuel' : 'Current Competitor'}
                          </dt>
                          <dd className="font-semibold text-slate-900 dark:text-white">
                            {simulation.input_data.competition_info.competitor_name}{simulation.input_data.competition_info.competitor_name === 'Autre' && simulation.input_data.competition_info.competitor_other_name ? ` (${simulation.input_data.competition_info.competitor_other_name})` : ''}
                          </dd>
                        </div>
                      )}
                      
                      {/* Current Concept */}
                      {simulation.input_data.competition_info.current_concept && (
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                          <dt className="text-xs text-orange-600 dark:text-orange-400 mb-1">
                            {language === 'fr' ? 'Concept actuel' : 'Current Concept'}
                          </dt>
                          <dd className="font-semibold text-slate-900 dark:text-white">
                            {simulation.input_data.competition_info.current_concept}
                          </dd>
                        </div>
                      )}
                      
                      {/* Competitor CA */}
                      {simulation.input_data.competition_info.competitor_annual_ca && (
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                          <dt className="text-xs text-orange-600 dark:text-orange-400 mb-1">
                            {language === 'fr' ? 'CA annuel concurrent' : 'Competitor Annual Revenue'}
                          </dt>
                          <dd className="font-semibold font-mono text-slate-900 dark:text-white">
                            {formatCurrency(simulation.input_data.competition_info.competitor_annual_ca)}
                          </dd>
                        </div>
                      )}
                      
                      {/* Engagement Duration */}
                      {simulation.input_data.competition_info.engagement_duration && (
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                          <dt className="text-xs text-orange-600 dark:text-orange-400 mb-1">
                            {language === 'fr' ? 'Durée engagement' : 'Engagement Duration'}
                          </dt>
                          <dd className="font-semibold font-mono text-slate-900 dark:text-white">
                            {simulation.input_data.competition_info.engagement_duration} {language === 'fr' ? 'mois' : 'months'}
                          </dd>
                        </div>
                      )}
                      
                      {/* Engagement End Date */}
                      {simulation.input_data.competition_info.engagement_end_date && (
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                          <dt className="text-xs text-orange-600 dark:text-orange-400 mb-1">
                            {language === 'fr' ? 'Fin de contrat' : 'Contract End Date'}
                          </dt>
                          <dd className="font-semibold text-slate-900 dark:text-white">
                            {simulation.input_data.competition_info.engagement_end_date === 'unknown'
                              ? (language === 'fr' ? 'Date inconnue' : 'Unknown date')
                              : new Date(simulation.input_data.competition_info.engagement_end_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                          </dd>
                        </div>
                      )}
                      
                      {/* Current Discount */}
                      {simulation.input_data.competition_info.current_discount && (
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                          <dt className="text-xs text-orange-600 dark:text-orange-400 mb-1">
                            {language === 'fr' ? 'Remise actuelle' : 'Current Discount'}
                          </dt>
                          <dd className="font-semibold font-mono text-slate-900 dark:text-white">
                            {(simulation.input_data.competition_info.current_discount * 100).toFixed(0)} %
                          </dd>
                        </div>
                      )}
                      
                      {/* Competitor Commission */}
                      {simulation.input_data.competition_info.competitor_commission && (
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                          <dt className="text-xs text-orange-600 dark:text-orange-400 mb-1">
                            {language === 'fr' ? 'Commission concurrent' : 'Competitor Commission'}
                          </dt>
                          <dd className="font-semibold font-mono text-slate-900 dark:text-white">
                            {(simulation.input_data.competition_info.competitor_commission * 100).toFixed(0)} %
                          </dd>
                        </div>
                      )}
                      
                      {/* Has Other Restaurant */}
                      {simulation.input_data.competition_info.has_other_restaurant !== undefined && (
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                          <dt className="text-xs text-orange-600 dark:text-orange-400 mb-1">
                            {language === 'fr' ? 'Autre restaurant' : 'Other Restaurant'}
                          </dt>
                          <dd className="font-semibold text-slate-900 dark:text-white">
                            {simulation.input_data.competition_info.has_other_restaurant 
                              ? (language === 'fr' ? 'Oui' : 'Yes')
                              : (language === 'fr' ? 'Non' : 'No')}
                          </dd>
                        </div>
                      )}
                      
                      {/* Has Pan Asian */}
                      {simulation.input_data.competition_info.has_pan_asian !== undefined && (
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                          <dt className="text-xs text-orange-600 dark:text-orange-400 mb-1">
                            {language === 'fr' ? 'Asiatique / Pan-Asian' : 'Asian / Pan-Asian'}
                          </dt>
                          <dd className="font-semibold text-slate-900 dark:text-white">
                            {simulation.input_data.competition_info.has_pan_asian 
                              ? (language === 'fr' ? 'Oui' : 'Yes')
                              : (language === 'fr' ? 'Non' : 'No')}
                          </dd>
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-500" />
                  {language === 'fr' ? 'Notes internes' : 'Internal Notes'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Échangez des notes visibles par l\'équipe interne. Non incluses dans les exports.'
                    : 'Exchange notes visible to the internal team. Not included in exports.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* New note input */}
                <div className="flex gap-2">
                  <textarea
                    className="flex-1 min-h-[60px] p-3 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={language === 'fr' ? 'Ajouter une note...' : 'Add a note...'}
                    value={newNote}
                    onChange={e => setNewNote(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
                    data-testid="note-input"
                  />
                  <Button onClick={handleAddNote} disabled={sendingNote || !newNote.trim()} className="self-end" data-testid="add-note-btn">
                    {sendingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* Notes list */}
                {loadingNotes ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
                ) : notes.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">{language === 'fr' ? 'Aucune note pour le moment' : 'No notes yet'}</p>
                ) : (
                  <div className="space-y-3">
                    {notes.map(note => (
                      <div key={note.id} className="flex gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid={`note-${note.id}`}>
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-blue-700">
                          {(note.author_name || '?')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-slate-800">{note.author_name}</span>
                            <Badge variant="outline" className="text-[10px]">{note.author_role}</Badge>
                            <span className="text-[11px] text-slate-400">
                              {note.created_at ? new Date(note.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{note.content}</p>
                        </div>
                        {(note.author_id === user?.id || isAdmin) && (
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            className="text-slate-300 hover:text-red-500 transition-colors self-start"
                            data-testid={`delete-note-${note.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="w-5 h-5 text-slate-500" />
                    {language === 'fr' ? 'Historique des modifications' : 'Modification History'}
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={fetchAuditLogs} disabled={loadingAudit} data-testid="refresh-audit-btn">
                    {loadingAudit ? <Loader2 className="w-4 h-4 animate-spin" /> : <History className="w-4 h-4" />}
                    {language === 'fr' ? 'Actualiser' : 'Refresh'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingAudit ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : auditLogs.length === 0 ? (
                  <p className="text-center text-slate-400 py-8" data-testid="audit-empty">
                    {language === 'fr' ? 'Aucun historique disponible' : 'No history available'}
                  </p>
                ) : (
                  <div className="relative" data-testid="audit-timeline">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                    <div className="space-y-4">
                      {auditLogs.map((log) => {
                        const actionConfig = {
                          created: { icon: <Check className="w-3.5 h-3.5" />, color: 'bg-emerald-500', label: language === 'fr' ? 'Création' : 'Created' },
                          modified: { icon: <Pencil className="w-3.5 h-3.5" />, color: 'bg-blue-500', label: language === 'fr' ? 'Modification' : 'Modified' },
                          validated: { icon: <Send className="w-3.5 h-3.5" />, color: 'bg-indigo-500', label: language === 'fr' ? 'Proposition soumise' : 'Proposal Submitted' },
                          admin_go: { icon: <ThumbsUp className="w-3.5 h-3.5" />, color: 'bg-emerald-600', label: 'GO' },
                          admin_nogo: { icon: <ThumbsDown className="w-3.5 h-3.5" />, color: 'bg-red-500', label: 'NO GO' },
                          admin_review: { icon: <AlertTriangle className="w-3.5 h-3.5" />, color: 'bg-amber-500', label: language === 'fr' ? 'À l\'étude' : 'Under Review' },
                          counter_proposal: { icon: <MessageSquare className="w-3.5 h-3.5" />, color: 'bg-purple-500', label: language === 'fr' ? 'Contre-proposition' : 'Counter-proposal' },
                          recalculated: { icon: <BarChart3 className="w-3.5 h-3.5" />, color: 'bg-cyan-500', label: language === 'fr' ? 'Recalcul' : 'Recalculated' },
                          porteur_capex_modified: { icon: <Building2 className="w-3.5 h-3.5" />, color: 'bg-violet-500', label: language === 'fr' ? 'Porteur CAPEX modifié' : 'CAPEX Bearer Changed' },
                          deleted: { icon: <Trash2 className="w-3.5 h-3.5" />, color: 'bg-red-600', label: language === 'fr' ? 'Suppression' : 'Deleted' },
                        };
                        const config = actionConfig[log.action] || { icon: <Clock className="w-3.5 h-3.5" />, color: 'bg-slate-400', label: log.action };
                        
                        return (
                          <div key={log.id} className="relative pl-10" data-testid={`audit-entry-${log.action}`}>
                            <div className={`absolute left-2 w-5 h-5 rounded-full ${config.color} flex items-center justify-center text-white ring-4 ring-white`}>
                              {config.icon}
                            </div>
                            <div className="p-3 rounded-lg border border-slate-200 bg-white hover:shadow-sm transition-shadow">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-slate-800">{config.label}</span>
                                <span className="text-xs text-slate-400">
                                  {new Date(log.timestamp).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                  {' '}
                                  {new Date(log.timestamp).toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {log.user_name || log.user_email}
                              </p>
                              {log.details && (
                                <p className="text-sm text-slate-600 mt-1">{log.details}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Tchat par étude — échanges avec le commercial sur le dossier */}
        <SimulationChat simulationId={id} />


        {/* Annonce ALL Modal (admin shortcut from result page) */}
        <AnnouncementEditorModal
          open={showAnnouncementModal}
          onClose={() => setShowAnnouncementModal(false)}
          simulationId={id}
          token={token}
          onSuccess={() => {
            toast.success(language === 'fr' ? 'Annonce ALL soumise pour validation' : 'ALL announcement submitted');
            api().get(`/announcements/by-simulation/${id}`)
              .then(r => setAnnouncementStatus(r.data?.exists ? r.data.status : null))
              .catch(() => {});
          }}
        />

        {/* Send Email Modal */}
        {showSendEmailModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSendEmailModal(false)}>
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-lg w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  {language === 'fr' ? 'Envoyer les documents par email' : 'Send documents by email'}
                </h3>
                <Button variant="ghost" size="icon" onClick={() => setShowSendEmailModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {language === 'fr' ? 'Destinataires (séparés par virgule)' : 'Recipients (comma separated)'}
                  </Label>
                  <Input
                    placeholder="email1@example.com, email2@example.com"
                    value={sendEmailData.recipients}
                    onChange={(e) => setSendEmailData({...sendEmailData, recipients: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendEmailData.includePdf}
                      onChange={(e) => setSendEmailData({...sendEmailData, includePdf: e.target.checked})}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">PDF</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendEmailData.includeExcel}
                      onChange={(e) => setSendEmailData({...sendEmailData, includeExcel: e.target.checked})}
                      className="w-4 h-4 text-emerald-600 rounded"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Excel</span>
                  </label>
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {language === 'fr' ? 'Message (optionnel)' : 'Message (optional)'}
                  </Label>
                  <Textarea
                    placeholder={language === 'fr' ? 'Ajouter un message personnalisé...' : 'Add a custom message...'}
                    value={sendEmailData.message}
                    onChange={(e) => setSendEmailData({...sendEmailData, message: e.target.value})}
                    className="mt-1 h-20"
                  />
                </div>
                
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowSendEmailModal(false)}>
                    {language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button 
                    onClick={handleSendExportsByEmail}
                    disabled={sendingEmail}
                    className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                  >
                    {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {language === 'fr' ? 'Envoyer' : 'Send'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contract Request Modal */}
        {/* Dialogue : date de signature (obligatoire pour Gagné / Signé) */}
        <Dialog open={sigDialog.open} onOpenChange={(o) => setSigDialog((s) => ({ ...s, open: o }))}>
          <DialogContent className="max-w-md" data-testid="signature-date-dialog">
            <DialogHeader>
              <DialogTitle>{language === 'fr' ? 'Date de signature du contrat' : 'Contract signature date'}</DialogTitle>
              <DialogDescription>
                {language === 'fr'
                  ? 'Cette date est obligatoire pour valider un dossier Gagné / Signé.'
                  : 'This date is required to validate a Won / Signed project.'}
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <Label htmlFor="sig-date">{language === 'fr' ? 'Date de signature' : 'Signature date'}</Label>
              <Input
                id="sig-date"
                type="date"
                value={sigDate}
                min={simulation?.created_at ? new Date(simulation.created_at).toISOString().slice(0, 10) : undefined}
                max={(() => { const m = new Date(); m.setFullYear(m.getFullYear() + 1); return m.toISOString().slice(0, 10); })()}
                onChange={(e) => setSigDate(e.target.value)}
                className="mt-1"
                data-testid="signature-date-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSigDialog({ open: false, status: null })} data-testid="signature-date-cancel">
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={confirmSignature} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="signature-date-confirm">
                {language === 'fr' ? 'Valider' : 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        <Dialog open={showContractModal} onOpenChange={setShowContractModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <FileSignature className="w-5 h-5" />
                Demande de contrat
              </DialogTitle>
              <DialogDescription>
                Remplissez les informations pour générer la trame de préparation de contrat
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Section 1: Établissement */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-amber-800 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Informations établissement
                  </h4>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    onClick={searchSocietes}
                    disabled={societeSearch.loading || (!contractData.code_postal && !contractData.ville)}
                    data-testid="pappers-search-btn"
                  >
                    {societeSearch.loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <SearchIcon className="w-4 h-4 mr-1" />}
                    Remplir via Pappers
                  </Button>
                </div>
                {societeMemoire && !societeSearch.results && (
                  <p className="mb-3 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1" data-testid="societe-memoire-badge">
                    Société mémorisée pour ce magasin : {societeMemoire.raison_sociale}
                    {societeMemoire.updated_at ? ` — enregistrée le ${new Date(societeMemoire.updated_at).toLocaleDateString('fr-FR')}` : ''}
                    {societeMemoire.updated_by ? ` par ${societeMemoire.updated_by}` : ''} (aucun crédit Pappers utilisé)
                  </p>
                )}
                {societeSearch.results && (
                  <div className="mb-4 rounded-lg border border-blue-200 bg-white p-2 space-y-1" data-testid="societe-results">
                    <p className="px-2 pt-1 text-[11px] text-slate-500">
                      {societeSearch.results.length ? 'Sélectionnez la société du magasin (supermarchés / hypermarchés) :' : 'Aucune société trouvée — complétez à la main.'}
                      {societeSearch.fallback && <span className="block text-amber-700">{societeSearch.fallback}</span>}
                      {societeSearch.excluded > 0 && <span className="block text-slate-400">{societeSearch.excluded} société(s) de moins de 20 salariés écartée(s) (holdings probables)</span>}
                    </p>
                    {societeSearch.results.map((r, i) => (
                      <button key={r.siren} type="button" onClick={() => pickSociete(r)} data-testid={`societe-result-${i}`}
                        className="w-full text-left px-2 py-1.5 rounded-md hover:bg-blue-50 transition-colors">
                        <span className="text-sm font-semibold text-slate-800">{r.raison_sociale}</span>
                        <span className="ml-2 text-[11px] text-slate-500">{r.libelle_naf} · {r.code_postal} {r.ville} · SIRET {r.siret}{r.capital ? ` · capital ${Number(r.capital).toLocaleString('fr-FR')} €` : ''}</span>
                        <span className={`ml-2 text-[11px] font-medium ${r.effectif_min == null ? 'text-amber-600' : 'text-emerald-600'}`}>{r.effectif}</span>
                        {r.dirigeant && <span className="block text-[11px] text-slate-500">Dirigeant : {[r.dirigeant.prenom, r.dirigeant.nom].filter(Boolean).join(' ')}{r.dirigeant.qualite ? ` (${r.dirigeant.qualite})` : ''}</span>}
                      </button>
                    ))}
                    <button type="button" onClick={() => setSocieteSearch({ loading: false, results: null })} className="px-2 text-[11px] text-slate-500 underline">Fermer</button>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="enseigne">Enseigne *</Label>
                    <Input
                      id="enseigne"
                      value={contractData.enseigne}
                      onChange={(e) => {
                        const newEnseigne = e.target.value;
                        setContractData({
                          ...contractData, 
                          enseigne: newEnseigne,
                          capex_carrier_label: getCapexCarrierLabel(newEnseigne, contractData.capex_carrier)
                        });
                      }}
                      placeholder="Leclerc"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="raison_sociale">Raison sociale</Label>
                    <Input
                      id="raison_sociale"
                      value={contractData.raison_sociale}
                      onChange={(e) => setContractData({...contractData, raison_sociale: e.target.value})}
                      placeholder="PLAGECO DISTRIBUTION"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="capital">Capital (EUR)</Label>
                    <Input
                      id="capital"
                      type="number"
                      value={contractData.capital}
                      onChange={(e) => setContractData({...contractData, capital: e.target.value})}
                      placeholder="100000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="siret">SIRET</Label>
                    <Input
                      id="siret"
                      value={contractData.siret}
                      onChange={(e) => setContractData({...contractData, siret: e.target.value})}
                      placeholder="123 456 789 00012"
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <a href={`https://www.google.com/search?q=${encodeURIComponent(`${contractData.enseigne || ''} ${contractData.ville || ''} pappers`.trim())}`}
                    target="_blank" rel="noopener noreferrer" data-testid="pappers-google-search-btn"
                    className="inline-flex items-center gap-1.5 rounded-md border border-orange-400 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-100 transition-colors">
                    <SearchIcon className="w-3.5 h-3.5" />Recherche Pappers (Google)
                  </a>
                </div>
              </div>
              
              {/* Section 2: Adresse */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="adresse">Adresse *</Label>
                  <Input
                    id="adresse"
                    value={contractData.adresse}
                    onChange={(e) => setContractData({...contractData, adresse: e.target.value})}
                    placeholder="123 Rue du Commerce"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code_postal">Code postal *</Label>
                  <Input
                    id="code_postal"
                    value={contractData.code_postal}
                    onChange={(e) => setContractData({...contractData, code_postal: e.target.value})}
                    placeholder="75001"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ville">Ville *</Label>
                  <Input
                    id="ville"
                    value={contractData.ville}
                    onChange={(e) => setContractData({...contractData, ville: e.target.value})}
                    placeholder="Paris"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surface_m2">Surface M² Corner</Label>
                  <Input
                    id="surface_m2"
                    type="number"
                    value={contractData.surface_m2}
                    onChange={(e) => setContractData({...contractData, surface_m2: e.target.value})}
                    placeholder="50"
                  />
                </div>
              </div>
              
              {/* Section 3: Dirigeant */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-medium text-slate-700 mb-4">Informations dirigeant</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="genre_dirigeant">Genre</Label>
                    <Select 
                      value={contractData.genre_dirigeant} 
                      onValueChange={(v) => setContractData({...contractData, genre_dirigeant: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Monsieur">Monsieur</SelectItem>
                        <SelectItem value="Madame">Madame</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="prenom_dirigeant">Prénom</Label>
                    <Input
                      id="prenom_dirigeant"
                      value={contractData.prenom_dirigeant}
                      onChange={(e) => setContractData({...contractData, prenom_dirigeant: e.target.value})}
                      placeholder="Jean"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nom_dirigeant">Nom</Label>
                    <Input
                      id="nom_dirigeant"
                      value={contractData.nom_dirigeant}
                      onChange={(e) => setContractData({...contractData, nom_dirigeant: e.target.value})}
                      placeholder="Dupont"
                    />
                  </div>
                </div>
              </div>
              
              {/* Section 4: Contrat */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Informations contrat
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date_signature">Date de signature du contrat</Label>
                    <Input
                      id="date_signature"
                      type="date"
                      value={contractData.date_signature}
                      onChange={(e) => setContractData({...contractData, date_signature: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_mise_en_place">Date de mise en place (MEP) *</Label>
                    <Input
                      id="date_mise_en_place"
                      type="date"
                      value={contractData.date_mise_en_place}
                      onChange={(e) => setContractData({...contractData, date_mise_en_place: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="duree_mois">Durée (mois)</Label>
                    <Select 
                      value={contractData.duree_mois} 
                      onValueChange={(v) => setContractData({...contractData, duree_mois: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="24">24 mois</SelectItem>
                        <SelectItem value="36">36 mois</SelectItem>
                        <SelectItem value="48">48 mois</SelectItem>
                        <SelectItem value="60">60 mois</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="taux_commission">Taux de commission contrat (%) *</Label>
                    <Input
                      id="taux_commission"
                      type="number"
                      step="0.1"
                      value={contractData.taux_commission}
                      onChange={(e) => setContractData({...contractData, taux_commission: e.target.value})}
                      placeholder="21"
                    />
                  </div>
                </div>
              </div>
              
              {/* Section 5: Financier */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <h4 className="font-medium text-emerald-800 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Informations financières
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ca_minimum_mensuel">CA minimum mensuel (€ HT)</Label>
                    <Input
                      id="ca_minimum_mensuel"
                      type="number"
                      value={contractData.ca_minimum_mensuel}
                      onChange={(e) => setContractData({...contractData, ca_minimum_mensuel: e.target.value})}
                      placeholder="15000"
                    />
                    <p className="text-xs text-slate-500">Par défaut : 15 000 €</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ca_sortie_anticipee">CA sortie anticipée (€ HT/mois)</Label>
                    <Input
                      id="ca_sortie_anticipee"
                      type="number"
                      value={contractData.ca_sortie_anticipee}
                      onChange={(e) => setContractData({...contractData, ca_sortie_anticipee: e.target.value})}
                      placeholder="15000"
                    />
                    <p className="text-xs text-slate-500">Par défaut : 15 000 €</p>
                  </div>
                </div>
              </div>
              
              {/* Section 6: CAPEX & Zone de chalandise */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-medium text-purple-800 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  CAPEX & Zone de chalandise
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="capex_carrier">Corner installé et pris en charge par *</Label>
                    <Select 
                      value={contractData.capex_carrier} 
                      onValueChange={(v) => {
                        setContractData({
                          ...contractData, 
                          capex_carrier: v,
                          capex_carrier_label: getCapexCarrierLabel(contractData.enseigne, v)
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CCD">CCD</SelectItem>
                        <SelectItem value="Client">Client</SelectItem>
                      </SelectContent>
                    </Select>
                    {contractData.capex_carrier && contractData.enseigne && (
                      <p className="text-xs text-purple-600 font-medium">
                        → {getCapexCarrierLabel(contractData.enseigne, contractData.capex_carrier)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zone_chalandise_voiture">Zone chalandise voiture (min)</Label>
                    <Input
                      id="zone_chalandise_voiture"
                      type="number"
                      value={contractData.zone_chalandise_voiture}
                      onChange={(e) => setContractData({...contractData, zone_chalandise_voiture: e.target.value})}
                      placeholder="10"
                    />
                    <p className="text-xs text-slate-500">Par défaut : 10 min</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zone_chalandise_pied">Zone chalandise à pied (min)</Label>
                    <Input
                      id="zone_chalandise_pied"
                      type="number"
                      value={contractData.zone_chalandise_pied}
                      onChange={(e) => setContractData({...contractData, zone_chalandise_pied: e.target.value})}
                      placeholder="30"
                    />
                    <p className="text-xs text-slate-500">Par défaut : 30 min</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Section 7: Investissement CAPEX */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-4 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Investissement CAPEX
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Corner</Label>
                  <Select 
                    value={contractData.corner_investor} 
                    onValueChange={(v) => setContractData({...contractData, corner_investor: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CCD">CCD</SelectItem>
                      <SelectItem value="Client">Client</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Vitrine</Label>
                  <Select 
                    value={contractData.vitrine_investor} 
                    onValueChange={(v) => setContractData({...contractData, vitrine_investor: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CCD">CCD</SelectItem>
                      <SelectItem value="Client">Client</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Inox</Label>
                  <Select 
                    value={contractData.inox_investor} 
                    onValueChange={(v) => setContractData({...contractData, inox_investor: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CCD">CCD</SelectItem>
                      <SelectItem value="Client">Client</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Concept section */}
              <div className="border-t border-blue-200 pt-4 mt-4">
                <Label className="text-blue-800 font-medium mb-3 block">Concept</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="concept_wasabi"
                      checked={contractData.concept_wasabi}
                      onChange={(e) => setContractData({...contractData, concept_wasabi: e.target.checked})}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <Label htmlFor="concept_wasabi" className="cursor-pointer">WASABI</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="concept_mme_wang"
                      checked={contractData.concept_mme_wang}
                      onChange={(e) => setContractData({...contractData, concept_mme_wang: e.target.checked})}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <Label htmlFor="concept_mme_wang" className="cursor-pointer">MME WANG</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="concept_sorae"
                      checked={contractData.concept_sorae}
                      onChange={(e) => setContractData({...contractData, concept_sorae: e.target.checked})}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <Label htmlFor="concept_sorae" className="cursor-pointer">SORAE</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="concept_pokawa"
                      checked={contractData.concept_pokawa}
                      onChange={(e) => setContractData({...contractData, concept_pokawa: e.target.checked})}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <Label htmlFor="concept_pokawa" className="cursor-pointer">POKAWA</Label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contract History Section */}
            <div className="border-t pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowContractHistory(!showContractHistory);
                  if (!showContractHistory && contractHistory.length === 0) {
                    loadContractHistory();
                  }
                }}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
              >
                <History className="w-4 h-4" />
                {showContractHistory ? 'Masquer l\'historique' : 'Voir l\'historique des demandes'}
                {contractHistory.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{contractHistory.length}</Badge>
                )}
              </button>
              
              {showContractHistory && (
                <div className="mt-4 space-y-2">
                  {loadingContractHistory ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : contractHistory.length === 0 ? (
                    <p className="text-sm text-slate-500 italic py-2">Aucune demande précédente</p>
                  ) : (
                    contractHistory.map((req) => (
                      <div key={req.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-700">
                            {req.data?.enseigne} - {req.data?.ville}
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(req.created_at).toLocaleDateString('fr-FR', { 
                              day: '2-digit', month: '2-digit', year: 'numeric', 
                              hour: '2-digit', minute: '2-digit' 
                            })} par {req.created_by_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleReexportContract(req.id)}
                            className="h-8 w-8 p-0"
                            title="Ré-exporter Excel"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setContractEmailModal({ open: true, requestId: req.id });
                              setShowContractModal(false);
                            }}
                            className="h-8 w-8 p-0"
                            title="Envoyer par email"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
              <Button variant="outline" onClick={() => setShowContractModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveContractDraft}
                disabled={savingContractDraft}
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50 gap-2"
              >
                {savingContractDraft ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Enregistrer
              </Button>
              <Button 
                onClick={() => {
                  setContractSendModal(true);
                }}
                disabled={sendingContract}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 gap-2"
              >
                {sendingContract ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Envoyer contrat
              </Button>
              <Button 
                onClick={handleExportContractExcel}
                disabled={exportingContract}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 gap-2"
              >
                {exportingContract ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4" />
                )}
                Exporter Excel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Contract Send Modal */}
        <Dialog open={contractSendModal} onOpenChange={setContractSendModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Envoyer le contrat par email
              </DialogTitle>
              <DialogDescription>
                Le fichier Excel sera envoyé en pièce jointe au destinataire
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Multi-select from configured recipients */}
              {contractRecipients.length > 0 && (
                <div className="space-y-2">
                  <Label>Destinataires configurés</Label>
                  <div className="flex flex-col gap-2">
                    {contractRecipients.filter(r => r.active !== false).map((recipient) => {
                      const isSelected = contractSendData.recipient_emails.includes(recipient.email);
                      return (
                        <label
                          key={recipient.id}
                          className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                            isSelected ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              if (isSelected) {
                                const idx = contractSendData.recipient_emails.indexOf(recipient.email);
                                setContractSendData(prev => ({
                                  ...prev,
                                  recipient_emails: prev.recipient_emails.filter((_, i) => i !== idx),
                                  recipient_names: prev.recipient_names.filter((_, i) => i !== idx),
                                }));
                              } else {
                                setContractSendData(prev => ({
                                  ...prev,
                                  recipient_emails: [...prev.recipient_emails, recipient.email],
                                  recipient_names: [...prev.recipient_names, recipient.name || ''],
                                }));
                              }
                            }}
                            className="w-4 h-4 text-blue-600 rounded"
                            data-testid={`recipient-check-${recipient.id}`}
                          />
                          <div>
                            <span className="text-sm font-medium">{recipient.name || recipient.email}</span>
                            <span className="text-xs text-slate-500 ml-2">{recipient.email}</span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Ou saisir un email manuellement</Label>
                <div className="flex gap-2">
                  <Input
                    id="send_contract_email_manual"
                    type="email"
                    placeholder="autre@example.com"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const val = e.target.value.trim();
                        if (val && !contractSendData.recipient_emails.includes(val)) {
                          setContractSendData(prev => ({
                            ...prev,
                            recipient_emails: [...prev.recipient_emails, val],
                            recipient_names: [...prev.recipient_names, ''],
                          }));
                          e.target.value = '';
                        }
                      }
                    }}
                    data-testid="manual-email-input"
                  />
                  <Button variant="outline" size="sm" onClick={() => {
                    const inp = document.getElementById('send_contract_email_manual');
                    const val = inp?.value?.trim();
                    if (val && !contractSendData.recipient_emails.includes(val)) {
                      setContractSendData(prev => ({
                        ...prev,
                        recipient_emails: [...prev.recipient_emails, val],
                        recipient_names: [...prev.recipient_names, ''],
                      }));
                      inp.value = '';
                    }
                  }}>Ajouter</Button>
                </div>
              </div>

              {/* Selected recipients summary */}
              {contractSendData.recipient_emails.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Envoi à {contractSendData.recipient_emails.length} destinataire(s) :</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {contractSendData.recipient_emails.map((email, i) => (
                      <span key={email} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                        {contractSendData.recipient_names[i] || email}
                        <button onClick={() => setContractSendData(prev => ({
                          ...prev,
                          recipient_emails: prev.recipient_emails.filter((_, idx) => idx !== i),
                          recipient_names: prev.recipient_names.filter((_, idx) => idx !== i),
                        }))} className="text-blue-500 hover:text-red-500 ml-0.5">&times;</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="send_contract_message">Message personnalisé</Label>
                <Textarea
                  id="send_contract_message"
                  value={contractSendData.message}
                  onChange={(e) => setContractSendData({ ...contractSendData, message: e.target.value })}
                  placeholder="Veuillez trouver ci-joint la demande de contrat..."
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setContractSendModal(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleSendContractEmail}
                disabled={sendingContract}
                className="bg-blue-600 hover:bg-blue-700 gap-2"
              >
                {sendingContract ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Envoyer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Contract Email Send Modal (from history) */}
        <Dialog open={contractEmailModal.open} onOpenChange={(open) => setContractEmailModal({ ...contractEmailModal, open })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-600" />
                Envoyer par email
              </DialogTitle>
              <DialogDescription>
                Le fichier Excel sera envoyé en pièce jointe
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {contractRecipients.length > 0 && (
                <div className="space-y-2">
                  <Label>Destinataires configurés</Label>
                  <div className="flex flex-col gap-2">
                    {contractRecipients.filter(r => r.active !== false).map((recipient) => {
                      const isSelected = contractEmailData.recipient_emails.includes(recipient.email);
                      return (
                        <label key={recipient.id}
                          className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                          <input type="checkbox" checked={isSelected}
                            onChange={() => {
                              if (isSelected) {
                                const idx = contractEmailData.recipient_emails.indexOf(recipient.email);
                                setContractEmailData(prev => ({ ...prev, recipient_emails: prev.recipient_emails.filter((_, i) => i !== idx), recipient_names: prev.recipient_names.filter((_, i) => i !== idx) }));
                              } else {
                                setContractEmailData(prev => ({ ...prev, recipient_emails: [...prev.recipient_emails, recipient.email], recipient_names: [...prev.recipient_names, recipient.name || ''] }));
                              }
                            }} className="w-4 h-4 text-emerald-600 rounded" />
                          <div>
                            <span className="text-sm font-medium">{recipient.name || recipient.email}</span>
                            <span className="text-xs text-slate-500 ml-2">{recipient.email}</span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Ou saisir un email manuellement</Label>
                <div className="flex gap-2">
                  <Input id="history_email_manual" type="email" placeholder="autre@example.com"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const val = e.target.value.trim(); if (val && !contractEmailData.recipient_emails.includes(val)) { setContractEmailData(prev => ({ ...prev, recipient_emails: [...prev.recipient_emails, val], recipient_names: [...prev.recipient_names, ''] })); e.target.value = ''; } } }} />
                  <Button variant="outline" size="sm" onClick={() => { const inp = document.getElementById('history_email_manual'); const val = inp?.value?.trim(); if (val && !contractEmailData.recipient_emails.includes(val)) { setContractEmailData(prev => ({ ...prev, recipient_emails: [...prev.recipient_emails, val], recipient_names: [...prev.recipient_names, ''] })); inp.value = ''; } }}>Ajouter</Button>
                </div>
              </div>
              {contractEmailData.recipient_emails.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Envoi à {contractEmailData.recipient_emails.length} destinataire(s) :</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {contractEmailData.recipient_emails.map((email, i) => (
                      <span key={email} className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-xs px-2 py-1 rounded-full">
                        {contractEmailData.recipient_names[i] || email}
                        <button onClick={() => setContractEmailData(prev => ({ ...prev, recipient_emails: prev.recipient_emails.filter((_, idx) => idx !== i), recipient_names: prev.recipient_names.filter((_, idx) => idx !== i) }))} className="text-emerald-500 hover:text-red-500 ml-0.5">&times;</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="contract_message">Message personnalisé</Label>
                <Textarea id="contract_message" value={contractEmailData.message}
                  onChange={(e) => setContractEmailData({ ...contractEmailData, message: e.target.value })}
                  placeholder="Veuillez trouver ci-joint la demande de contrat..." rows={3} />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setContractEmailModal({ open: false, requestId: null })}>
                Annuler
              </Button>
              <Button
                onClick={handleSendContractFromHistory}
                disabled={sendingContractEmail}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              >
                {sendingContractEmail ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Envoyer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* HG Send Confirmation Modal */}
        <Dialog open={hgModalOpen} onOpenChange={setHgModalOpen}>
          <DialogContent className="max-w-lg" data-testid="hg-send-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <Send className="w-5 h-5" />
                {language === 'fr' ? 'Envoyer le dossier à HG' : 'Send dossier to HG'}
              </DialogTitle>
              <DialogDescription>
                {language === 'fr'
                  ? 'Vérifiez les pièces jointes avant l\'envoi.'
                  : 'Review attachments before sending.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Previous HG sends history (collapsible) */}
              {hgHistory.length > 0 && (
                <div className="bg-slate-50 rounded-md border border-slate-200">
                  <button
                    type="button"
                    onClick={() => setHgHistoryOpen(v => !v)}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-md transition"
                    data-testid="hg-history-toggle"
                  >
                    <span className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-slate-500" />
                      {language === 'fr'
                        ? `Historique des envois HG (${hgHistory.length})`
                        : `HG send history (${hgHistory.length})`}
                    </span>
                    <span className="text-slate-400">{hgHistoryOpen ? '▾' : '▸'}</span>
                  </button>
                  {hgHistoryOpen && (
                    <div className="px-3 pb-3 pt-1 space-y-2 max-h-48 overflow-y-auto" data-testid="hg-history-list">
                      {hgHistory.map(h => (
                        <div key={h.id} className="bg-white rounded border border-slate-200 p-2.5 text-xs">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <span className="font-semibold text-slate-700">{h.sent_by_name}</span>
                            <span className="text-slate-400 text-[10px] whitespace-nowrap">
                              {new Date(h.sent_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                            </span>
                          </div>
                          <div className="text-slate-500 text-[11px] mb-1">
                            → {(h.recipients || []).join(', ')}
                            {h.contract_attachments_count > 0 && (
                              <span className="ml-2 text-amber-600">+ {h.contract_attachments_count} contrat(s)</span>
                            )}
                          </div>
                          {h.note && (
                            <div className="mt-1 pl-2 border-l-2 border-amber-300 text-slate-600 italic whitespace-pre-wrap">
                              "{h.note}"
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Recipients */}
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {language === 'fr' ? 'Destinataires' : 'Recipients'}
                </div>
                <div className="bg-slate-50 rounded-md p-3 space-y-1 text-sm">
                  {(hgPreview?.recipients || []).map(r => (
                    <div key={r.email} className="flex items-center gap-2">
                      <span className="text-slate-700">📧 {r.email}</span>
                      {r.name && <span className="text-xs text-slate-400">— {r.name}</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Always-included attachments */}
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {language === 'fr' ? 'Pièces jointes obligatoires' : 'Required attachments'}
                </div>
                <div className="bg-emerald-50 rounded-md p-3 text-sm space-y-1 border border-emerald-200">
                  <div className="flex items-center gap-2 text-emerald-800">
                    <CheckCircle2 className="w-4 h-4" />
                    {language === 'fr' ? 'Export PDF complet (toutes les pages + Décision Direction)' : 'Full PDF export'}
                  </div>
                  <div className="flex items-center gap-2 text-emerald-800">
                    <CheckCircle2 className="w-4 h-4" />
                    {language === 'fr' ? 'Export Excel complet (tous les onglets)' : 'Full Excel export'}
                  </div>
                </div>
              </div>

              {/* Contract attachments (optional) */}
              {hgPreview?.has_contract_attachments && (
                <div>
                  <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors">
                    <Checkbox
                      checked={hgIncludeContracts}
                      onCheckedChange={setHgIncludeContracts}
                      className="mt-0.5"
                      data-testid="hg-include-contracts-checkbox"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-amber-900 text-sm">
                        {language === 'fr'
                          ? `Inclure également le(s) contrat(s) signé(s) (${hgPreview.contract_attachments.length})`
                          : `Also include signed contract(s) (${hgPreview.contract_attachments.length})`}
                      </div>
                      <div className="mt-1 space-y-0.5">
                        {hgPreview.contract_attachments.map((a, i) => (
                          <div key={i} className="text-xs text-amber-700 flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {a.original_name}
                            <span className="text-amber-500 ml-1">({(a.size_bytes / 1024).toFixed(1)} KB)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </label>
                </div>
              )}

              {/* Optional NB / Note from sender */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  📝 {language === 'fr' ? 'NB / Note pour HG' : 'NB / Note for HG'}
                  <span className="ml-2 text-[10px] font-normal text-slate-400 normal-case tracking-normal">
                    ({language === 'fr' ? 'optionnel — apparaîtra en haut de l\'email' : 'optional — will appear at the top of the email'})
                  </span>
                </label>
                <Textarea
                  value={hgNote}
                  onChange={e => setHgNote(e.target.value)}
                  placeholder={language === 'fr'
                    ? 'Ex: Contre-proposition acceptée à 18% au lieu de 15%. Dossier prioritaire pour ouverture en juin.'
                    : 'E.g. Counter-proposal accepted at 18% instead of 15%. Priority dossier for June opening.'}
                  rows={3}
                  className="text-sm border-amber-200 focus:border-amber-400 focus:ring-amber-300"
                  maxLength={1000}
                  data-testid="hg-note-textarea"
                />
                {hgNote && (
                  <div className="text-[10px] text-slate-400 text-right mt-1">{hgNote.length} / 1000</div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setHgModalOpen(false)}
                disabled={sendingToHG}
                data-testid="hg-cancel-btn"
              >
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button
                onClick={handleSendToHG}
                disabled={sendingToHG}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white gap-2"
                data-testid="hg-confirm-send-btn"
              >
                {sendingToHG ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {language === 'fr' ? 'Envoyer à HG' : 'Send to HG'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
